import React, { useEffect, useState, useMemo, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '/api';

// ─── HELPERS ──────────────────────────────────────────────
function getToken() { return localStorage.getItem('atiflow_token') || ''; }
function getUser()  { try { return JSON.parse(localStorage.getItem('atiflow_user')); } catch { return null; } }
function saveSession(token, user) {
  localStorage.setItem('atiflow_token', token);
  localStorage.setItem('atiflow_user', JSON.stringify(user));
}
function clearSession() {
  localStorage.removeItem('atiflow_token');
  localStorage.removeItem('atiflow_user');
}
async function api(path, opts = {}, tokenOverride) {
  try {
    const token = tokenOverride || getToken();
    const res = await fetch(`${API}${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts.headers || {}) }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { sucesso: false, mensagem: data.mensagem || `Erro ${res.status}` };
    return data;
  } catch (e) {
    return { sucesso: false, mensagem: 'Erro de conexão com o servidor.' };
  }
}

// ─── VALIDAÇÃO DE FORÇA DE SENHA ──────────────────────────
function validarForcaSenha(senha) {
  const erros = [];
  if (!senha || senha.length < 8) erros.push('Mínimo 8 caracteres');
  if (!/[A-Z]/.test(senha)) erros.push('Uma letra maiúscula');
  if (!/[0-9]/.test(senha)) erros.push('Um número');
  if (!/[!@#$%^&*(),.?":{}|<>_-]/.test(senha)) erros.push('Um caractere especial');
  return erros;
}

function ForcaSenha({ senha }) {
  const erros = validarForcaSenha(senha);
  const forte = erros.length === 0;
  if (!senha) return null;
  return (
    <div style={{ marginTop:6 }}>
      <div style={{ display:'flex', gap:4, marginBottom:4 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ flex:1, height:3, borderRadius:999, background: erros.length <= i ? (forte ? '#22C55E' : erros.length <= 1 ? '#F59E0B' : '#EF4444') : 'rgba(255,255,255,.1)' }}/>
        ))}
      </div>
      {!forte && erros.map(e => (
        <div key={e} style={{ fontSize:10, color:'#FCA5A5', display:'flex', alignItems:'center', gap:4 }}>
          <span>✗</span> {e}
        </div>
      ))}
      {forte && <div style={{ fontSize:10, color:'#86EFAC' }}>✓ Senha forte</div>}
    </div>
  );
}

function arr(data, key) {
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data)) return data;
  return [];
}
function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 60000);
  if (diff < 1) return 'Agora há pouco';
  if (diff < 60) return `${diff} min atrás`;
  if (diff < 1440) return `${Math.floor(diff/60)}h atrás`;
  return `${Math.floor(diff/1440)} dias atrás`;
}
function proto(id) { return `ATI-${new Date().getFullYear()}-${String(id).padStart(4,'0')}`; }

// ─── DESIGN TOKENS ────────────────────────────────────────
const C = {
  bg: '#0B111D', panel: '#111827', panel2: '#1F2937',
  border: 'rgba(255,255,255,.09)', red: '#E31B23', redDark: '#A80F15',
  text: '#F8FAFC', muted: '#9CA3AF', green: '#22C55E',
  orange: '#F59E0B', blue: '#3B82F6', purple: '#8B5CF6',
};
const PRIO = {
  critica: { label:'Crítica', desc:'Minha equipe está parada ou muito impactada',      color:'#fff',    bg:'#7F1D1D' },
  alta:    { label:'Alta',    desc:'Estou completamente parado, não consigo trabalhar', color:'#FCA5A5', bg:'rgba(185,28,28,.25)' },
  media:   { label:'Média',   desc:'Estou com dificuldade, mas consigo trabalhar',      color:'#FCD34D', bg:'rgba(180,83,9,.25)' },
  baixa:   { label:'Baixa',   desc:'Dúvida ou melhoria, sem urgência',                 color:'#86EFAC', bg:'rgba(21,128,61,.25)' },
};
const STATUS_MAP = {
  aberto:       { label:'Aberto',       color:'#93C5FD', bg:'rgba(37,99,235,.2)' },
  em_andamento: { label:'Em andamento', color:'#FCD34D', bg:'rgba(180,83,9,.2)' },
  concluido:    { label:'Concluído',    color:'#86EFAC', bg:'rgba(22,163,74,.2)' },
  cancelado:    { label:'Cancelado',    color:'#9CA3AF', bg:'rgba(75,85,99,.2)' },
};

// ─── ATOMS ────────────────────────────────────────────────
function Badge({ value, type }) {
  const map = type === 'status' ? STATUS_MAP : PRIO;
  const m = map[String(value).toLowerCase()] || { label: value, color: C.muted, bg: 'rgba(255,255,255,.08)' };
  return <span style={{ display:'inline-flex', alignItems:'center', padding:'3px 10px', borderRadius:999, fontSize:11, fontWeight:700, background:m.bg, color:m.color }}>{m.label}</span>;
}
function Icon({ name, size=18, color='currentColor' }) {
  const icons = {
    dashboard: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    kanban:   'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7',
    clients:  'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
    assets:   'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    category: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z',
    sla:      'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    reports:  'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    users:    'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
    settings: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
    bell:     'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
    search:   'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    moon:     'M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z',
    menu:     'M4 6h16M4 12h16M4 18h16',
    plus:     'M12 4v16m8-8H4',
    refresh:  'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
    chevron:  'M19 9l-7 7-7-7',
    dots:     'M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z',
    check:    'M5 13l4 4L19 7',
    x:        'M6 18L18 6M6 6l12 12',
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={icons[name] || icons.dashboard} />
    </svg>
  );
}

// ─── DONUT CHART ──────────────────────────────────────────
function DonutChart({ data, size=120 }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  let offset = 0;
  const r = 45, cx = 60, cy = 60, circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="14"/>
      {data.map((d, i) => {
        const dash = (d.value / total) * circ;
        const gap = circ - dash;
        const seg = (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={d.color} strokeWidth="14"
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset}
            style={{ transform:'rotate(-90deg)', transformOrigin:'center' }}
          />
        );
        offset += dash;
        return seg;
      })}
    </svg>
  );
}

// ─── MINI LINE CHART ──────────────────────────────────────
function LineChart({ data=[], color=C.red, height=80 }) {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const w = 400, h = height;
  const pts = data.map((v, i) => `${(i/(data.length-1))*w},${h - (v/max)*h*0.85}`).join(' ');
  const fill = `${pts} ${w},${h} 0,${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width:'100%', height }} preserveAspectRatio="none">
      <defs>
        <linearGradient id="lg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".35"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polygon points={fill} fill="url(#lg)"/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ─── MODAL ────────────────────────────────────────────────
function Modal({ title, children, onClose, width=560 }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', backdropFilter:'blur(8px)', display:'grid', placeItems:'center', zIndex:1000, padding:20 }}>
      <div style={{ width:`min(${width}px,100%)`, maxHeight:'90vh', overflowY:'auto', background:'#111827', border:`1px solid ${C.border}`, borderRadius:20, padding:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ margin:0, fontSize:18 }}>{title}</h3>
          <button onClick={onClose} style={{ background:'transparent', border:`1px solid ${C.border}`, color:C.muted, borderRadius:10, padding:'6px 10px', cursor:'pointer' }}>
            <Icon name="x" size={16}/>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return <label style={{ display:'block', marginBottom:12 }}><span style={{ display:'block', fontSize:12, color:C.muted, marginBottom:6, fontWeight:600 }}>{label}</span>{children}</label>;
}
const inp = { width:'100%', background:'rgba(255,255,255,.05)', border:`1px solid ${C.border}`, borderRadius:10, padding:'10px 14px', color:C.text, fontSize:13, outline:'none', boxSizing:'border-box' };

// ─── LOGIN ────────────────────────────────────────────────
function Login({ onLogin }) {
  const [tela, setTela] = useState('login'); // 'login' | 'recuperar' | 'codigo' | 'redefinir'
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  async function entrar(e) {
    e.preventDefault(); setErro(''); setLoading(true);
    try {
      const d = await api('/login', { method:'POST', body:JSON.stringify({ email, senha }) });
      saveSession(d.token, d.usuario);
      onLogin(d.usuario);
    } catch(err) { setErro(err.message || 'E-mail ou senha inválidos.'); }
    finally { setLoading(false); }
  }

  async function solicitarCodigo(e) {
    e.preventDefault(); setErro(''); setLoading(true);
    try {
      await api('/recuperar-senha', { method:'POST', body:JSON.stringify({ email }) });
      setMsg('Se o e-mail existir, um código foi enviado via WhatsApp.');
      setTela('codigo');
    } catch(err) { setErro('Erro ao solicitar. Tente novamente.'); }
    finally { setLoading(false); }
  }

  async function redefinir(e) {
    e.preventDefault(); setErro(''); setLoading(true);
    try {
      const d = await api('/redefinir-senha', { method:'POST', body:JSON.stringify({ email, token:codigo, novaSenha }) });
      setMsg('Senha redefinida! Faça login com a nova senha.');
      setTela('login');
      setSenha(''); setCodigo(''); setNovaSenha('');
    } catch(err) { setErro(err.message || 'Código inválido ou expirado.'); }
    finally { setLoading(false); }
  }

  const card = (
    <div style={{ width:'min(420px,100%)', background:'linear-gradient(180deg,rgba(255,255,255,.07),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:24, padding:36, boxShadow:'0 30px 80px rgba(0,0,0,.6)' }}>
      <div style={{ textAlign:'center', marginBottom:28 }}>
        <img src="/logo-ati.png" alt="ATI Services" style={{ width:160, height:'auto', marginBottom:16, filter:'drop-shadow(0 0 20px rgba(227,27,35,.5))' }}/>
        <p style={{ margin:0, color:C.muted, fontSize:13 }}>Sistema de Gestão de Chamados</p>
      </div>
      {msg && <div style={{ background:'rgba(34,197,94,.1)', border:'1px solid rgba(34,197,94,.3)', color:'#86EFAC', padding:'10px 14px', borderRadius:10, marginBottom:16, fontSize:13 }}>{msg}</div>}
      {erro && <div style={{ background:'rgba(239,68,68,.12)', border:'1px solid rgba(239,68,68,.3)', color:'#FCA5A5', padding:'10px 14px', borderRadius:10, marginBottom:16, fontSize:13 }}>{erro}</div>}

      {tela === 'login' && (
        <form onSubmit={entrar}>
          <Field label="Email"><input style={inp} type="email" value={email} onChange={e=>setEmail(e.target.value)} required/></Field>
          <SenhaInput label="Senha" value={senha} onChange={e=>setSenha(e.target.value)} required/>
          <button type="submit" disabled={loading} style={{ width:'100%', padding:'12px', background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:12, color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', marginTop:8, boxShadow:`0 10px 25px rgba(227,27,35,.3)` }}>
            {loading ? 'Entrando...' : 'Entrar no painel'}
          </button>
          <div style={{ textAlign:'center', marginTop:14 }}>
            <span onClick={()=>{ setErro(''); setMsg(''); setTela('recuperar'); }} style={{ fontSize:12, color:C.muted, cursor:'pointer', textDecoration:'underline' }}>Esqueci minha senha</span>
          </div>
        </form>
      )}

      {tela === 'recuperar' && (
        <form onSubmit={solicitarCodigo}>
          <p style={{ fontSize:13, color:C.muted, marginTop:0, marginBottom:16 }}>Informe seu e-mail. Enviaremos um código via WhatsApp.</p>
          <Field label="Email"><input style={inp} type="email" value={email} onChange={e=>setEmail(e.target.value)} required/></Field>
          <button type="submit" disabled={loading} style={{ width:'100%', padding:'12px', background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:12, color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', marginTop:8 }}>
            {loading ? 'Enviando...' : 'Enviar código'}
          </button>
          <div style={{ textAlign:'center', marginTop:14 }}>
            <span onClick={()=>{ setErro(''); setMsg(''); setTela('login'); }} style={{ fontSize:12, color:C.muted, cursor:'pointer', textDecoration:'underline' }}>Voltar ao login</span>
          </div>
        </form>
      )}

      {tela === 'codigo' && (
        <form onSubmit={redefinir}>
          <p style={{ fontSize:13, color:C.muted, marginTop:0, marginBottom:16 }}>Digite o código recebido via WhatsApp e escolha uma nova senha.</p>
          <Field label="Código recebido"><input style={inp} value={codigo} onChange={e=>setCodigo(e.target.value.toUpperCase())} placeholder="Ex: A1B2C3D4" required maxLength={8}/></Field>
          <SenhaInput label="Nova senha" value={novaSenha} onChange={e=>setNovaSenha(e.target.value)} required/>
          <button type="submit" disabled={loading} style={{ width:'100%', padding:'12px', background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:12, color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', marginTop:8 }}>
            {loading ? 'Salvando...' : 'Redefinir senha'}
          </button>
          <div style={{ textAlign:'center', marginTop:14, display:'flex', gap:16, justifyContent:'center' }}>
            <span onClick={()=>{ setErro(''); setMsg(''); setTela('recuperar'); }} style={{ fontSize:12, color:C.muted, cursor:'pointer', textDecoration:'underline' }}>Reenviar código</span>
            <span onClick={()=>{ setErro(''); setMsg(''); setTela('login'); }} style={{ fontSize:12, color:C.muted, cursor:'pointer', textDecoration:'underline' }}>Voltar ao login</span>
          </div>
        </form>
      )}
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', display:'grid', placeItems:'center', background:`radial-gradient(circle at 20% 20%, rgba(227,27,35,.2), transparent 40%), ${C.bg}`, padding:20 }}>
      {card}
    </div>
  );
}

// ─── METRIC CARD ──────────────────────────────────────────
function MetricCard({ icon, label, value, sub, trend, color, bg }) {
  return (
    <div style={{ background:'linear-gradient(180deg,rgba(255,255,255,.07),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:16, padding:'18px 20px', display:'flex', alignItems:'center', gap:16 }}>
      <div style={{ width:56, height:56, borderRadius:14, background:bg||'rgba(255,255,255,.08)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, flexShrink:0 }}>{icon}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:12, color:C.muted, marginBottom:4 }}>{label}</div>
        <div style={{ fontSize:32, fontWeight:800, color:color||C.text, lineHeight:1 }}>{value ?? '—'}</div>
        {sub && <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>{sub} {trend && <span style={{ color: trend>0?C.green:C.red }}>↑ {Math.abs(trend)}%</span>}</div>}
      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────
function Dashboard({ setAba }) {
  const isMobile = React.useContext(MobileContext);
  const [data, setData] = useState(null);
  useEffect(() => { api('/dashboard').then(setData).catch(()=>{}); }, []);

  const porStatus = data?.porStatus || [];
  const abertos   = porStatus.find(x=>x.status==='aberto')?.total||0;
  const concluido = porStatus.find(x=>x.status==='concluido')?.total||0;
  const slaVenc   = data?.slaVencidos?.total||0;
  const total     = data?.totais?.total||0;

  const chartData = [45,62,38,74,55,88,72,91,65,78,84,95];
  const prioData = [
    { label:'Crítica', value:24, color:C.red },
    { label:'Alta',    value:98, color:C.orange },
    { label:'Média',   value:142, color:C.blue },
    { label:'Baixa',   value:78, color:C.green },
  ];
  const slaData = [
    { label:'OK',     value:75, color:C.green },
    { label:'Atenção',value:16, color:C.orange },
    { label:'Crítico',value:9,  color:C.red },
  ];

  return (
    <div>
      {/* HEADER */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:28, fontWeight:800 }}>Dashboard</h1>
          <p style={{ margin:0, color:C.muted, fontSize:14 }}>Bem-vindo(a) de volta, Administrador! 👋</p>
        </div>
        <div style={{ opacity:.18 }}>
          <img src="/logo-ati.png" alt="ATI Services" style={{ width:110, height:'auto', filter:'grayscale(30%)' }}/>
        </div>
      </div>

      {/* METRICS */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(5,1fr)', gap:14, marginBottom:24 }}>
        <MetricCard icon="📋" label="Total de Chamados" value={total||342} sub="Todos os chamados" trend={12} color={C.red} bg="rgba(227,27,35,.15)"/>
        <MetricCard icon="🔓" label="Abertos"    value={abertos||128} sub="Em andamento" trend={8}  color={C.orange} bg="rgba(245,158,11,.15)"/>
        <MetricCard icon="✅" label="Resolvidos" value={concluido||198} sub="Finalizados"  trend={15} color={C.green}  bg="rgba(34,197,94,.15)"/>
        <MetricCard icon="⏰" label="SLA Vencidos" value={slaVenc||16} sub="Atenção necessária" trend={100} color={C.purple} bg="rgba(139,92,246,.15)"/>
        <MetricCard icon="🏢" label="Clientes Ativos" value={56} sub="Empresas" trend={5} color={C.blue} bg="rgba(59,130,246,.15)"/>
      </div>

      {/* KANBAN PREVIEW + SIDEBAR */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr minmax(280px, 320px)', gap:16, marginBottom:16 }}>
        {/* KANBAN PREVIEW */}
        <div style={{ background:'linear-gradient(180deg,rgba(255,255,255,.06),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:16, padding:'14px 18px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <h3 style={{ margin:0, fontSize:15 }}>Kanban — Fluxo de Atendimento</h3>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <select style={{ ...inp, width:'auto', fontSize:12, padding:'6px 12px' }}><option>Help Desk ATI</option></select>
              <button style={{ background:'rgba(255,255,255,.08)', border:`1px solid ${C.border}`, borderRadius:8, padding:'6px 10px', cursor:'pointer', color:C.muted }}><Icon name="refresh" size={14}/></button>
              <button onClick={()=>setAba('kanban')} style={{ background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:8, padding:'8px 14px', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                <Icon name="plus" size={13}/> Novo Chamado
              </button>
            </div>
          </div>
          <div style={{ display:'flex', gap:10, overflowX:'auto', paddingBottom:4 }}>
            {[
              { name:'Novo', count:24, color:'#64748B', cards:[{id:342,title:'Internet instável',client:'Empresa ABC Ltda',prio:'alta',time:'1h atrás'},{id:341,title:'Troca de equipamento',client:'Consultoria XYZ',prio:'media',time:'2h atrás'},{id:340,title:'Erro de sistema ERP',client:'Indústria 123',prio:'alta',time:'3h atrás'}] },
              { name:'Em Análise', count:18, color:C.blue, cards:[{id:339,title:'Backup não realizado',client:'Comércio Aliança',prio:'media',time:'2h atrás'},{id:338,title:'Acesso VPN falhando',client:'Tech Solutions',prio:'alta',time:'3h atrás'},{id:337,title:'Impressora offline',client:'Logística Prime',prio:'baixa',time:'4h atrás'}] },
              { name:'Em Execução', count:32, color:C.orange, cards:[{id:336,title:'Servidor lento',client:'Hospital Saúde+',prio:'alta',time:'30min atrás'},{id:335,title:'Migração de e-mail',client:'Escritório Contábil',prio:'media',time:'1h atrás'},{id:334,title:'Instalação de software',client:'Agropecuária Boa Vista',prio:'baixa',time:'4h atrás'}] },
              { name:'Aguardando Cliente', count:14, color:C.purple, cards:[{id:333,title:'Liberação de acesso',client:'Construtora Ideal',prio:'media',time:'5h atrás'},{id:332,title:'Validação de teste',client:'Auto Peças Brasil',prio:'baixa',time:'1 dia atrás'}] },
              { name:'Resolvido', count:254, color:C.green, cards:[{id:331,title:'Chamado finalizado',client:'Distribuidora Elite',prio:'alta',time:'Hoje'},{id:330,title:'Problema resolvido',client:'Transportes União',prio:'media',time:'Hoje'}] },
            ].map(col => (
              <div key={col.name} style={{ minWidth:185, flex:'0 0 185px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 10px', background:'rgba(255,255,255,.05)', borderRadius:'10px 10px 0 0', borderTop:`3px solid ${col.color}` }}>
                  <span style={{ fontSize:13, fontWeight:600 }}>{col.name}</span>
                  <span style={{ background:col.color, color:'#fff', borderRadius:999, padding:'2px 8px', fontSize:11, fontWeight:700 }}>{col.count}</span>
                </div>
                <div style={{ background:'rgba(0,0,0,.2)', borderRadius:'0 0 10px 10px', padding:6 }}>
                  {col.cards.map(c => (
                    <div key={c.id} style={{ background:'rgba(255,255,255,.05)', border:`1px solid ${C.border}`, borderRadius:10, padding:'8px 10px', marginBottom:6 }}>
                      <div style={{ fontSize:10, color:C.red, fontWeight:700, marginBottom:3 }}>{proto(c.id)}</div>
                      <div style={{ fontSize:12, fontWeight:600, marginBottom:3, lineHeight:1.3 }}>{c.title}</div>
                      <div style={{ fontSize:11, color:C.muted, marginBottom:5 }}>{c.client}</div>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <Badge value={c.prio}/>
                        <span style={{ fontSize:10, color:C.muted }}>{c.time}</span>
                      </div>
                    </div>
                  ))}
                  <button onClick={()=>setAba('kanban')} style={{ width:'100%', background:'transparent', border:`1px dashed ${C.border}`, borderRadius:8, padding:'8px', color:C.muted, fontSize:11, cursor:'pointer' }}>+ Ver todos</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ATIVIDADES RECENTES */}
        <div style={{ background:'linear-gradient(180deg,rgba(255,255,255,.06),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:16, padding:18 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <h3 style={{ margin:0, fontSize:14 }}>Atividades Recentes</h3>
            <span style={{ fontSize:11, color:C.red, cursor:'pointer' }}>Ver todas atividades</span>
          </div>
          {[
            { icon:'🔴', color:'rgba(227,27,35,.2)', label:'Novo chamado criado', sub:`${proto(342)} - Internet instável`, time:'Agora há pouco' },
            { icon:'✅', color:'rgba(34,197,94,.15)', label:'Chamado resolvido', sub:`${proto(331)} - Chamado finalizado`, time:'10 min atrás' },
            { icon:'🔀', color:'rgba(139,92,246,.15)', label:'Chamado movido', sub:`${proto(338)} - Acesso VPN falhando`, time:'15 min atrás' },
            { icon:'🏢', color:'rgba(59,130,246,.15)', label:'Novo cliente cadastrado', sub:'Empresa Nova Ltda', time:'1 hora atrás' },
            { icon:'⏰', color:'rgba(245,158,11,.15)', label:'SLA vencido', sub:`${proto(320)} - Backup não realizado`, time:'2 horas atrás' },
          ].map((a,i) => (
            <div key={i} style={{ display:'flex', gap:10, marginBottom:12, padding:'10px', background:'rgba(0,0,0,.2)', borderRadius:10 }}>
              <div style={{ width:34, height:34, borderRadius:10, background:a.color, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:15 }}>{a.icon}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:600, marginBottom:2 }}>{a.label}</div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.sub}</div>
                <div style={{ fontSize:10, color:'#4B5563' }}>{a.time}</div>
              </div>
            </div>
          ))}

          {/* SLA */}
          <div style={{ marginTop:16, paddingTop:16, borderTop:`1px solid ${C.border}` }}>
            <h4 style={{ margin:'0 0 12px', fontSize:13 }}>SLA — Situação Atual</h4>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <DonutChart data={slaData} size={90}/>
              <div style={{ flex:1 }}>
                {slaData.map(s=>(
                  <div key={s.label} style={{ display:'flex', justifyContent:'space-between', marginBottom:5, fontSize:12 }}>
                    <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ width:8, height:8, borderRadius:'50%', background:s.color, display:'inline-block' }}/>
                      {s.label}
                    </span>
                    <span style={{ color:C.muted }}>{s.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* TOP CATEGORIAS */}
          <div style={{ marginTop:16, paddingTop:16, borderTop:`1px solid ${C.border}` }}>
            <h4 style={{ margin:'0 0 12px', fontSize:13 }}>TOP Categorias</h4>
            {[['Rede / Conectividade',128,'#E31B23'],['Hardware',86,'#F59E0B'],['Software / Sistemas',64,'#3B82F6'],['Segurança',42,'#8B5CF6'],['Servidor / Backup',22,'#22C55E']].map(([name,val,color])=>(
              <div key={name} style={{ marginBottom:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
                  <span style={{ color:C.muted }}>{name}</span>
                  <span style={{ fontWeight:700 }}>{val}</span>
                </div>
                <div style={{ height:4, background:'rgba(255,255,255,.06)', borderRadius:4 }}>
                  <div style={{ height:'100%', width:`${(val/128)*100}%`, background:color, borderRadius:4 }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CHARTS ROW */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap:14 }}>
        {/* Prioridade donut */}
        <div style={{ background:'linear-gradient(180deg,rgba(255,255,255,.06),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:16, padding:18 }}>
          <h4 style={{ margin:'0 0 14px', fontSize:13 }}>Chamados por Prioridade</h4>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <DonutChart data={prioData} size={110}/>
            <div style={{ flex:1 }}>
              {prioData.map(p=>(
                <div key={p.label} style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:12 }}>
                  <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ width:9, height:9, borderRadius:'50%', background:p.color, display:'inline-block' }}/>
                    {p.label}
                  </span>
                  <span style={{ color:C.muted }}>{p.value} ({Math.round(p.value/342*100)}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Line chart */}
        <div style={{ background:'linear-gradient(180deg,rgba(255,255,255,.06),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:16, padding:18 }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:14 }}>
            <h4 style={{ margin:0, fontSize:13 }}>Chamados por Status</h4>
            <select style={{ ...inp, width:'auto', fontSize:11, padding:'4px 8px' }}><option>Últimos 7 dias</option></select>
          </div>
          <LineChart data={chartData} color={C.red} height={90}/>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
            {['01/05','02/05','03/05','04/05','05/05','06/05','07/05'].map(d=>(
              <span key={d} style={{ fontSize:9, color:C.muted }}>{d}</span>
            ))}
          </div>
        </div>

        {/* Segmento donut */}
        <div style={{ background:'linear-gradient(180deg,rgba(255,255,255,.06),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:16, padding:18 }}>
          <h4 style={{ margin:'0 0 14px', fontSize:13 }}>Clientes por Segmento</h4>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <DonutChart data={[{label:'Indústria',value:22,color:C.red},{label:'Comércio',value:18,color:C.orange},{label:'Serviços',value:10,color:C.blue},{label:'Outros',value:6,color:C.green}]} size={110}/>
            <div style={{ flex:1 }}>
              {[['Indústria',22,39,C.red],['Comércio',18,32,C.orange],['Serviços',10,18,C.blue],['Outros',6,11,C.green]].map(([l,v,p,c])=>(
                <div key={l} style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:12 }}>
                  <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ width:9, height:9, borderRadius:'50%', background:c, display:'inline-block' }}/>
                    {l}
                  </span>
                  <span style={{ color:C.muted }}>{v} ({p}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── KANBAN ───────────────────────────────────────────────
function Kanban() {
  const [pipeAtivo, setPipeAtivo] = useState(1);
  const [colunas, setColunas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNovo, setModalNovo] = useState(false);
  const [cardDetalhe, setCardDetalhe] = useState(null);
  const [detalheData, setDetalheData] = useState(null);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [form, setForm] = useState({ titulo:'', descricao:'', prioridade:'media', categoria_id:'', grupo_id:'', empresa_id:'' });
  const [comentText, setComentText] = useState('');
  const [comentInterno, setComentInterno] = useState(false);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatText, setChatText] = useState('');
  const [chatSocket, setChatSocket] = useState(null);
  const [chatBadge, setChatBadge] = useState(0);
  const [chatAba, setChatAba] = useState('comunicacao');
  const [empresasFiltro, setEmpresasFiltro] = useState([]);

  const carregar = () => {
    api(`/kanban/${pipeAtivo}`, {}, getToken())
      .then(d => setColunas(arr(d,'kanban')))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };

  useEffect(() => {
    Promise.all([
      api('/grupos', {}, getToken()),
      api('/portal/categorias'),
      api('/usuarios', {}, getToken()),
    ]).then(([g, c, u]) => {
      setGrupos(arr(g,'grupos'));
      setCategorias(arr(c,'categorias'));
      setUsuarios(arr(u,'usuarios').filter(u=>u.ativo));
    });
    carregar();
  }, [pipeAtivo]);

  // Abrir card pendente (vindo de Chamados ou Relatorios via window.__pendingCard)
  useEffect(() => {
    if (window.__pendingCard) {
      const pc = window.__pendingCard;
      window.__pendingCard = null;
      // Pequeno delay para garantir que o Kanban carregou
      setTimeout(() => abrirDetalhe(pc), 300);
    }
  }, []); // eslint-disable-line

  async function abrirDetalhe(card) {
    setCardDetalhe(card);
    setLoadingDetalhe(true);
    setChatAba('comunicacao');
    const r = await api(`/cards/${card.id}`, {}, getToken());
    if (r.sucesso) setDetalheData(r);
    setLoadingDetalhe(false);
    // Carregar histórico de chat
    const ch = await api(`/chat/${card.id}`, {}, getToken());
    if (ch.sucesso) setChatMsgs(ch.mensagens || []);
    // Conectar socket.io
    if (typeof window !== 'undefined' && window.io) {
      const sk = window.io({ auth:{ token: getToken() }, transports:['polling','websocket'] });
      sk.on('connect', () => sk.emit('entrar_chamado', card.id));
      sk.on('nova_mensagem', msg => setChatMsgs(prev => [...prev, msg]));
      sk.on('erro', err => console.warn('[chat-err]', err));
      setChatSocket(sk);
    }
  }

  async function salvarCampo(campo, valor) {
    const r = await api(`/cards/${cardDetalhe.id}`, { method:'PUT', body:JSON.stringify({[campo]:valor}) }, getToken());
    if (r.sucesso) {
      abrirDetalhe(cardDetalhe);
      carregar();
    }
  }

  async function comentar(e) {
    e.preventDefault();
    if (!comentText.trim()) return;
    await api(`/cards/${cardDetalhe.id}/comentar`, { method:'POST', body:JSON.stringify({ texto:comentText, interno:comentInterno }) }, getToken());
    setComentText('');
    abrirDetalhe(cardDetalhe);
  }

  async function criarCard(e) {
    e.preventDefault();
    const r = await api('/cards', { method:'POST', body:JSON.stringify({ ...form, pipe_id:pipeAtivo, etapa_id:1 }) }, getToken());
    if (r.sucesso || r.id) {
      setModalNovo(false);
      setForm({ titulo:'', descricao:'', prioridade:'media', categoria_id:'', grupo_id:'', empresa_id:'' });
      carregar();
    }
  }

  const SLA_COR = { violado:'#EF4444', critico:'#F59E0B', ok:'#22C55E' };
  const STATUS_OPTS = [
    { value:'aberto',               label:'Novo' },
    { value:'em_analise',           label:'Em análise' },
    { value:'aguardando_aprovacao', label:'Aguard. aprovação' },
    { value:'aguardando_feedback',  label:'Aguard. feedback' },
    { value:'em_execucao',          label:'Em execução' },
    { value:'enc_prestador_externo',label:'Prestador externo' },
    { value:'aguardando_cliente',   label:'Aguard. cliente' },
    { value:'resolvido',            label:'✅ Resolver' },
    { value:'cancelado',            label:'❌ Cancelar' },
  ];

  if (loading) return <div style={{ textAlign:'center', padding:60, color:C.muted }}>Carregando...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div><h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800 }}>Kanban</h1><p style={{ margin:0, color:C.muted, fontSize:13 }}>Clique em um chamado para atender</p></div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={carregar} style={{ background:'rgba(255,255,255,.08)', border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 12px', color:C.muted, cursor:'pointer', fontSize:12 }}>↻</button>
          <button onClick={()=>setModalNovo(true)} style={{ background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:10, padding:'10px 18px', color:'#fff', fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}>
            <Icon name="plus" size={15}/> Novo Chamado
          </button>
        </div>
      </div>

      {/* Colunas Kanban */}
      <div style={{ display:'flex', gap:14, overflowX:'auto', paddingBottom:16 }}>
        {colunas.map(col => (
          <div key={col.id} style={{ minWidth:270, flex:'0 0 270px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'rgba(255,255,255,.06)', borderRadius:'12px 12px 0 0', borderTop:`3px solid ${col.cor}` }}>
              <span style={{ fontWeight:600, fontSize:12 }}>{col.nome}</span>
              <span style={{ background:col.cor, color:'#fff', borderRadius:999, padding:'2px 9px', fontSize:11, fontWeight:700 }}>{col.cards?.length||0}</span>
            </div>
            <div style={{ background:'rgba(0,0,0,.2)', borderRadius:'0 0 12px 12px', padding:8, minHeight:80 }}>
              {(col.cards||[]).map(card => (
                <div key={card.id} data-card-id={card.id} onClick={()=>abrirDetalhe(card)}
                  style={{ background:'linear-gradient(180deg,rgba(255,255,255,.08),rgba(255,255,255,.04))', border:`1px solid ${C.border}`, borderRadius:10, padding:11, marginBottom:8, cursor:'pointer', transition:'all .15s' }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(227,27,35,.4)'}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:5 }}>
                    <span style={{ fontSize:10, color:C.red, fontWeight:700 }}>{proto(card.id)}</span>
                    {card.sla_alerta && <span style={{ fontSize:9, padding:'1px 6px', borderRadius:999, background:`${SLA_COR[card.sla_alerta]}20`, color:SLA_COR[card.sla_alerta], fontWeight:700 }}>
                      {card.sla_alerta === 'violado' ? '⚠ SLA' : '⏰ SLA'}
                    </span>}
                  </div>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:5, lineHeight:1.4 }}>{card.titulo}</div>
                  {card.grupo_nome && <div style={{ fontSize:10, color:C.muted, marginBottom:4 }}>🏢 {card.grupo_nome}</div>}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:6 }}>
                    <Badge value={card.prioridade}/>
                    <div style={{ fontSize:10, color:C.muted }}>
                      {card.responsavel_nome
                        ? <span style={{ background:'rgba(255,255,255,.08)', padding:'2px 6px', borderRadius:999 }}>👤 {card.responsavel_nome.split(' ')[0]}</span>
                        : <span style={{ color:'#EF4444', fontSize:10 }}>Sem técnico</span>}
                    </div>
                  </div>
                </div>
              ))}
              {!(col.cards?.length) && <div style={{ textAlign:'center', padding:'16px 8px', color:C.muted, fontSize:11 }}>Sem chamados</div>}
            </div>
          </div>
        ))}
      </div>

      {/* ─── MODAL NOVO CHAMADO ─────────────────────────────── */}
      {modalNovo && (
        <Modal title="Novo Chamado" onClose={()=>setModalNovo(false)} width={560}>
          <form onSubmit={criarCard}>
            <Field label="Título *"><input style={inp} required value={form.titulo} onChange={e=>setForm({...form,titulo:e.target.value})}/></Field>
            <Field label="Descrição"><textarea style={{...inp,height:80,resize:'vertical'}} value={form.descricao} onChange={e=>setForm({...form,descricao:e.target.value})}/></Field>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <Field label="Prioridade">
                <select style={inp} value={form.prioridade} onChange={e=>setForm({...form,prioridade:e.target.value})}>
                  {Object.entries(PRIO).map(([k,v])=><option key={k} value={k}>{v.desc}</option>)}
                </select>
              </Field>
              <Field label="Categoria">
                <select style={inp} value={form.categoria_id} onChange={e=>setForm({...form,categoria_id:e.target.value})}>
                  <option value="">— Selecione —</option>
                  {categorias.map(c=><option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Grupo / Cliente">
              <select style={inp} value={form.grupo_id} onChange={e=>{
                setForm({...form,grupo_id:e.target.value,empresa_id:''});
                const g = grupos.find(g=>g.id==e.target.value);
                if(g) api(`/grupos/${g.id}/empresas`,{},getToken()).then(d=>setEmpresasFiltro(arr(d,'empresas')));
              }}>
                <option value="">— Sem grupo —</option>
                {grupos.map(g=><option key={g.id} value={g.id}>{g.nome}</option>)}
              </select>
            </Field>
            {empresasFiltro.length>0 && (
              <Field label="Empresa">
                <select style={inp} value={form.empresa_id} onChange={e=>setForm({...form,empresa_id:e.target.value})}>
                  <option value="">— Selecione —</option>
                  {empresasFiltro.map(e=><option key={e.id} value={e.id}>{e.nome}</option>)}
                </select>
              </Field>
            )}
            <button type="submit" style={{ width:'100%', padding:12, background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:10, color:'#fff', fontWeight:700, cursor:'pointer', marginTop:12 }}>Criar Chamado</button>
          </form>
        </Modal>
      )}

      {/* ─── MODAL DETALHE DO CHAMADO ───────────────────────── */}
      {cardDetalhe && (
        <Modal title={`Chamado ${proto(cardDetalhe.id)}`} onClose={()=>{ setCardDetalhe(null); setDetalheData(null); setChatMsgs([]); setChatText(''); if(chatSocket){chatSocket.disconnect();setChatSocket(null);} }} width={720}>
          {loadingDetalhe && <div style={{ textAlign:'center', padding:40, color:C.muted }}>Carregando...</div>}
          {!loadingDetalhe && detalheData && (() => {
            const c = detalheData.card;
            const slaCorMap = { cumprido:'#22C55E', violado:'#EF4444', critico:'#F59E0B', no_prazo:'#22C55E', sem_sla:C.muted };
            return (
              <div>
                {/* Título e status rápido */}
                <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:18, fontWeight:800, marginBottom:6 }}>{c.titulo}</div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      <Badge value={c.prioridade}/>
                      <Badge value={c.status} type="status"/>
                      {c.categoria_nome && <span style={{ fontSize:11, color:C.muted, padding:'2px 8px', background:'rgba(255,255,255,.06)', borderRadius:999 }}>{c.categoria_nome}</span>}
                    </div>
                  </div>
                </div>

                {/* Info grid */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:14 }}>
                  {[
                    { label:'Grupo', val:c.grupo_nome||'—' },
                    { label:'Empresa', val:c.empresa_nome||'—' },
                    { label:'Solicitante', val:c.solicitante_nome||'—' },
                  ].map(i=>(
                    <div key={i.label} style={{ background:'rgba(0,0,0,.25)', borderRadius:8, padding:'8px 10px' }}>
                      <div style={{ fontSize:10, color:C.muted, marginBottom:2 }}>{i.label}</div>
                      <div style={{ fontSize:12, fontWeight:500 }}>{i.val}</div>
                    </div>
                  ))}
                </div>

                {/* Ações — Status + Técnico + SLA */}
                <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:10, padding:14, marginBottom:14 }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                    <div>
                      <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Status</div>
                      <select style={inp} value={c.status} onChange={e=>salvarCampo('status', e.target.value)}>
                        {STATUS_OPTS.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Técnico responsável</div>
                      <select style={inp} value={c.responsavel_id||''} onChange={e=>salvarCampo('responsavel_id', e.target.value||null)}>
                        <option value="">— Sem responsável —</option>
                        {usuarios.map(u=><option key={u.id} value={u.id}>{u.nome} ({PERFIS[u.perfil]?.label||u.perfil})</option>)}
                      </select>
                    </div>
                  </div>
                  {/* SLA */}
                  {c.sla_prazo && (
                    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', background:'rgba(0,0,0,.2)', borderRadius:8 }}>
                      <span style={{ fontSize:11, color:C.muted }}>SLA:</span>
                      <span style={{ fontSize:11, fontWeight:600, color:slaCorMap[c.sla_status] }}>
                        {c.sla_status?.replace(/_/g,' ')} —  {new Date(c.sla_prazo).toLocaleString('pt-BR')}
                      </span>
                      {parseFloat(c.horas_pausadas)>0 && <span style={{ fontSize:10, color:C.muted }}>(pausado {c.horas_pausadas}h)</span>}
                    </div>
                  )}
                </div>

                {/* Descrição */}
                {c.descricao && (
                  <div style={{ background:'rgba(0,0,0,.2)', borderRadius:8, padding:12, marginBottom:14, fontSize:13, color:'#CBD5E1', lineHeight:1.6 }}>
                    {c.descricao}
                  </div>
                )}

                {/* Comentários — visível só na aba 'comunicacao' */}
                {chatAba === 'comunicacao' && <div style={{ marginBottom:10 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                    <span style={{ fontSize:13, fontWeight:600 }}>Comunicação ({detalheData.comentarios?.length||0})</span>
                  </div>
                  <div style={{ maxHeight:220, overflowY:'auto', display:'flex', flexDirection:'column', gap:7, marginBottom:10 }}>
                    {(detalheData.comentarios||[]).map((cm,i)=>(
                      <div key={i} style={{ background:cm.interno?'rgba(139,92,246,.1)':'rgba(0,0,0,.25)', border:`1px solid ${cm.interno?'rgba(139,92,246,.2)':C.border}`, borderRadius:8, padding:'9px 12px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                          <span style={{ fontSize:11, fontWeight:600, color:cm.interno?'#C4B5FD':C.red }}>
                            {cm.interno?'🔒 ':''}{cm.autor||'Equipe ATI'}
                          </span>
                          <span style={{ fontSize:10, color:C.muted }}>{cm.criado_em?new Date(cm.criado_em).toLocaleString('pt-BR'):''}</span>
                        </div>
                        <div style={{ fontSize:12, color:'#CBD5E1', lineHeight:1.5 }}>{cm.texto}</div>
                      </div>
                    ))}
                    {!(detalheData.comentarios?.length) && <div style={{ textAlign:'center', padding:16, color:C.muted, fontSize:12 }}>Nenhum comentário ainda.</div>}
                  </div>
                  <form onSubmit={comentar}>
                    <textarea style={{...inp,height:70,resize:'vertical',marginBottom:8}} placeholder="Adicionar comentário..." value={comentText} onChange={e=>setComentText(e.target.value)}/>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:C.muted, cursor:'pointer' }}>
                        <input type="checkbox" checked={comentInterno} onChange={e=>setComentInterno(e.target.checked)} style={{ accentColor:'#8B5CF6' }}/>
                        🔒 Nota interna (só equipe ATI)
                      </label>
                      <button type="submit" style={{ marginLeft:'auto', padding:'7px 16px', background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:8, color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 }}>
                        Enviar
                      </button>
                    </div>
                  </form>
                </div>}

                {/* Histórico */}
                {detalheData.historico?.length > 0 && (
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, marginBottom:8, marginTop:14, color:C.muted }}>Histórico de ações</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      {detalheData.historico.map((h,i)=>(
                        <div key={i} style={{ display:'flex', gap:8, alignItems:'center', fontSize:11, color:C.muted, padding:'4px 0', borderBottom:`1px solid ${C.border}` }}>
                          <span style={{ color:'#64748B' }}>●</span>
                          <span style={{ fontWeight:500, color:'#94A3B8' }}>{h.usuario_nome||'Sistema'}</span>
                          <span>{h.acao}</span>
                          {h.valor_anterior && <span style={{ color:'#EF4444', textDecoration:'line-through' }}>{h.valor_anterior}</span>}
                          {h.valor_novo && <span style={{ color:'#22C55E' }}>→ {h.valor_novo}</span>}
                          <span style={{ marginLeft:'auto' }}>{h.criado_em?new Date(h.criado_em).toLocaleString('pt-BR'):''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Chat com Solicitante ── */}
                <div style={{ marginTop:20, borderTop:`1px solid ${C.border}`, paddingTop:16 }}>
                  <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                    <button onClick={()=>setChatAba('comunicacao')} style={{ flex:1, padding:'6px 10px', borderRadius:8, border:`1px solid ${chatAba==='comunicacao'?C.red:C.border}`, background:chatAba==='comunicacao'?`rgba(227,27,35,.12)`:'transparent', color:chatAba==='comunicacao'?C.red:'#94A3B8', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                      💬 Comentários internos
                    </button>
                    <button onClick={()=>{ setChatAba('chat'); }} style={{ flex:1, padding:'6px 10px', borderRadius:8, border:`1px solid ${chatAba==='chat'?'#3B82F6':C.border}`, background:chatAba==='chat'?'rgba(59,130,246,.12)':'transparent', color:chatAba==='chat'?'#60A5FA':'#94A3B8', fontSize:12, fontWeight:700, cursor:'pointer', position:'relative' }}>
                      🗨️ Chat com Solicitante {chatMsgs.filter(m=>m.de==='portal'&&!m.lida).length>0?`(${chatMsgs.filter(m=>m.de==='portal'&&!m.lida).length})`:''}
                    </button>
                  </div>

                  {chatAba === 'chat' && (
                    <div>
                      <div style={{ maxHeight:260, overflowY:'auto', display:'flex', flexDirection:'column', gap:6, marginBottom:10, padding:8, background:'rgba(0,0,0,.15)', borderRadius:8 }}>
                        {chatMsgs.length === 0 && <div style={{ textAlign:'center', color:C.muted, fontSize:12, padding:20 }}>Nenhuma mensagem ainda.</div>}
                        {chatMsgs.map((m,i) => (
                          <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:m.de==='tecnico'?'flex-end':'flex-start' }}>
                            <div style={{ maxWidth:'80%', background:m.de==='tecnico'?`rgba(227,27,35,.18)`:'rgba(255,255,255,.07)', border:`1px solid ${m.de==='tecnico'?'rgba(227,27,35,.3)':C.border}`, borderRadius:10, padding:'8px 12px' }}>
                              <div style={{ fontSize:10, fontWeight:600, color:m.de==='tecnico'?C.red:'#60A5FA', marginBottom:3 }}>
                                {m.de==='tecnico'?'🔧 ':'👤 '}{m.nome}{m.empresa?` · ${m.empresa}`:''}
                              </div>
                              <div style={{ fontSize:12, color:'#E2E8F0', lineHeight:1.5 }}>{m.mensagem}</div>
                              <div style={{ fontSize:10, color:C.muted, marginTop:3, textAlign:'right' }}>
                                {m.criado_em?new Date(m.criado_em).toLocaleString('pt-BR'):''}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display:'flex', gap:8 }}>
                        <input
                          style={{ ...inp, flex:1, marginBottom:0 }}
                          placeholder="Mensagem para o solicitante..."
                          value={chatText}
                          onChange={e=>setChatText(e.target.value)}
                          onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey&&chatText.trim()){ e.preventDefault(); if(chatSocket)chatSocket.emit('mensagem',{cardId:cardDetalhe.id,texto:chatText}); setChatText(''); }}}
                        />
                        <button
                          onClick={()=>{ if(chatSocket&&chatText.trim()){ chatSocket.emit('mensagem',{cardId:cardDetalhe.id,texto:chatText}); setChatText(''); }}}
                          style={{ padding:'8px 16px', background:`linear-gradient(135deg,#2563EB,#1D4ED8)`, border:'none', borderRadius:8, color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 }}
                        >Enviar</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </Modal>
      )}
    </div>
  );
}


// ─── CHAMADOS ────────────────────────────────────────────
function Chamados({ setAba }) {
  const [cards, setCards] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(()=>{ api('/cards').then(d=>setCards(arr(d,'cards'))).catch(()=>{}).finally(()=>setLoading(false)); },[]);

  const filtrados = useMemo(()=>cards.filter(c=>`${c.titulo||''} ${c.cliente_nome||''} ${c.responsavel_nome||''}`.toLowerCase().includes(filtro.toLowerCase())),[cards,filtro]);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div><h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800 }}>Chamados</h1><p style={{ margin:0, color:C.muted, fontSize:13 }}>Lista completa de solicitações</p></div>
      </div>
      <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:16, padding:18 }}>
        <input style={{ ...inp, maxWidth:380, marginBottom:16 }} placeholder="Buscar por título, cliente ou responsável..." value={filtro} onChange={e=>setFiltro(e.target.value)}/>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:700 }}>
            <thead>
              <tr>{['Protocolo','Título','Cliente','Responsável','Categoria','Prioridade','Status','Criado'].map(h=>(
                <th key={h} style={{ textAlign:'left', padding:'10px 14px', fontSize:11, color:C.muted, background:'rgba(0,0,0,.3)', fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} style={{ textAlign:'center', padding:40, color:C.muted }}>Carregando...</td></tr>}
              {!loading && filtrados.map(c=>(
                <tr key={c.id} onClick={()=>{ window.__pendingCard={id:c.id,titulo:c.titulo}; setAba('kanban'); }} style={{ borderTop:`1px solid ${C.border}`, cursor:'pointer' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,.05)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={{ padding:'12px 14px', fontSize:12, color:C.red, fontWeight:700, whiteSpace:'nowrap' }}>{proto(c.id)}</td>
                  <td style={{ padding:'12px 14px', fontSize:13, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.titulo}</td>
                  <td style={{ padding:'12px 14px', fontSize:12, color:C.muted }}>{c.cliente_nome||'—'}</td>
                  <td style={{ padding:'12px 14px', fontSize:12, color:C.muted }}>{c.responsavel_nome||'—'}</td>
                  <td style={{ padding:'12px 14px', fontSize:12, color:C.muted }}>{c.categoria_nome||'—'}</td>
                  <td style={{ padding:'12px 14px' }}><Badge value={c.prioridade}/></td>
                  <td style={{ padding:'12px 14px' }}><Badge value={c.status} type="status"/></td>
                  <td style={{ padding:'12px 14px', fontSize:11, color:C.muted, whiteSpace:'nowrap' }}>{timeAgo(c.created_at)}</td>
                </tr>
              ))}
              {!loading && !filtrados.length && <tr><td colSpan={8} style={{ textAlign:'center', padding:40, color:C.muted }}>Nenhum chamado encontrado.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── CLIENTES ────────────────────────────────────────────
function Clientes() {
  const isMobile = React.useContext(MobileContext);
  const [grupos, setGrupos] = useState([]);
  const [grupoAtivo, setGrupoAtivo] = useState(null);
  const [modalGrupo, setModalGrupo] = useState(false);
  const [modalEmpresa, setModalEmpresa] = useState(false);
  const [editandoGrupo, setEditandoGrupo] = useState(null);
  const [editandoEmpresa, setEditandoEmpresa] = useState(null);
  const [linkModal, setLinkModal] = useState(null);
  const [busca, setBusca] = useState('');
  const [msg, setMsg] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [formGrupo, setFormGrupo] = useState({ nome:'', descricao:'' });
  const [formEmpresa, setFormEmpresa] = useState({ nome:'', cnpj:'', tipo:'unica', email:'', telefone:'' });

  const TIPOS = {
    unica:  { label:'Empresa única', icon:'🏢', desc:'Um único CNPJ e localização' },
    matriz: { label:'Matriz',        icon:'🏛️', desc:'Sede principal do grupo' },
    filial: { label:'Filial',        icon:'🏪', desc:'Unidade vinculada à matriz' },
  };

  const carregar = () => {
    api('/grupos', {}, getToken()).then(d => {
      const lista = arr(d,'grupos');
      setGrupos(lista);
      if (grupoAtivo) {
        const atualizado = lista.find(g => g.id === grupoAtivo.id);
        if (atualizado) setGrupoAtivo(atualizado);
      } else if (lista.length) setGrupoAtivo(lista[0]);
    }).catch(()=>{});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{ carregar(); },[]);

  async function salvarGrupo(e) {
    e.preventDefault(); setMsg('');
    if (editandoGrupo) {
      const r = await api(`/grupos/${editandoGrupo.id}`, { method:'PUT', body:JSON.stringify(formGrupo) }, getToken());
      if (!r.sucesso) { setMsg(r.mensagem||'Erro ao salvar'); return; }
      setEditandoGrupo(null);
    } else {
      const r = await api('/grupos', { method:'POST', body:JSON.stringify(formGrupo) }, getToken());
      if (!r.sucesso) { setMsg(r.mensagem||'Erro ao criar'); return; }
      if (r.link_convite) setLinkModal({ link:r.link_convite, nome:formGrupo.nome });
    }
    setModalGrupo(false);
    setFormGrupo({ nome:'', descricao:'' });
    carregar();
  }

  async function salvarEmpresa(e) {
    e.preventDefault(); setMsg('');
    if (!grupoAtivo) return;
    if (editandoEmpresa) {
      const r = await api(`/empresas/${editandoEmpresa.id}`, { method:'PUT', body:JSON.stringify(formEmpresa) }, getToken());
      if (!r.sucesso) { setMsg(r.mensagem||'Erro ao salvar'); return; }
      setEditandoEmpresa(null);
    } else {
      const r = await api(`/grupos/${grupoAtivo.id}/empresas`, { method:'POST', body:JSON.stringify(formEmpresa) }, getToken());
      if (!r.sucesso) { setMsg(r.mensagem||'Erro ao criar'); return; }
    }
    setModalEmpresa(false);
    setFormEmpresa({ nome:'', cnpj:'', tipo:'unica', email:'', telefone:'' });
    carregar();
  }

  function abrirEditarGrupo(g) {
    setEditandoGrupo(g);
    setFormGrupo({ nome:g.nome, descricao:g.descricao||'' });
    setModalGrupo(true);
    setMsg('');
  }

  function abrirEditarEmpresa(emp) {
    setEditandoEmpresa(emp);
    setFormEmpresa({ nome:emp.nome, cnpj:emp.cnpj||'', tipo:emp.tipo||'unica', email:emp.email||'', telefone:emp.telefone||'' });
    setModalEmpresa(true);
    setMsg('');
  }

  async function excluirGrupo(g) {
    if (!window.confirm(`Excluir o grupo "${g.nome}"? Esta ação removerá todas as empresas vinculadas.`)) return;
    await api(`/grupos/${g.id}`, { method:'DELETE' }, getToken());
    setGrupoAtivo(null);
    carregar();
  }

  async function excluirEmpresa(emp) {
    if (!window.confirm(`Excluir a empresa "${emp.nome}"?`)) return;
    await api(`/empresas/${emp.id}`, { method:'DELETE' }, getToken());
    carregar();
  }

  async function copiarConvite(grupo) {
    const r = await api(`/grupos/${grupo.id}`, {}, getToken());
    if (r.sucesso) setLinkModal({ link:r.link_convite, nome:grupo.nome });
  }

  const gruposFiltrados = grupos.filter(g =>
    g.nome.toLowerCase().includes(busca.toLowerCase())
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800 }}>Clientes</h1>
          <p style={{ margin:0, color:C.muted, fontSize:13 }}>Grupos, empresas e contratos ATI Services</p>
        </div>
        <button onClick={()=>{ setEditandoGrupo(null); setFormGrupo({nome:'',descricao:''}); setModalGrupo(true); setMsg(''); }}
          style={{ background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:10, padding:'10px 18px', color:'#fff', fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}>
          <Icon name="plus" size={15}/> Novo Grupo / Cliente
        </button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '280px 1fr', gap:16 }}>
        {/* COLUNA ESQUERDA */}
        <div>
          <input style={{ ...inp, marginBottom:10 }} placeholder="Buscar cliente..." value={busca} onChange={e=>setBusca(e.target.value)}/>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {gruposFiltrados.map(g => (
              <div key={g.id} onClick={()=>setGrupoAtivo(g)}
                style={{ padding:'12px 14px', borderRadius:10, cursor:'pointer', border:`1px solid ${grupoAtivo?.id===g.id ? C.red : C.border}`, background: grupoAtivo?.id===g.id ? 'rgba(227,27,35,.1)' : 'rgba(255,255,255,.04)', transition:'all .15s', position:'relative' }}>
                <div style={{ fontWeight:600, fontSize:13, color: grupoAtivo?.id===g.id ? '#F1F5F9' : C.muted }}>{g.nome}</div>
                <div style={{ fontSize:11, color:C.muted, marginTop:3 }}>
                  {g.total_empresas||0} empresa{g.total_empresas!==1?'s':''} · {g.chamados_abertos||0} chamados abertos
                </div>
              </div>
            ))}
            {!gruposFiltrados.length && <div style={{ color:C.muted, fontSize:12, textAlign:'center', padding:20 }}>Nenhum cliente encontrado</div>}
          </div>
        </div>

        {/* COLUNA DIREITA */}
        {grupoAtivo ? (
          <div>
            <div style={{ background:'linear-gradient(180deg,rgba(255,255,255,.07),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:14, padding:20, marginBottom:14 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
                <div style={{ display:'flex', gap:14, alignItems:'center' }}>
                  <div style={{ width:52, height:52, borderRadius:14, background:'rgba(227,27,35,.15)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:18, color:C.red }}>
                    {(grupoAtivo.nome||'AT').slice(0,2).toUpperCase()}
                  </div>
                  <div>
                    <h2 style={{ margin:'0 0 4px', fontSize:18 }}>{grupoAtivo.nome}</h2>
                    {grupoAtivo.descricao && <p style={{ margin:0, color:C.muted, fontSize:12 }}>{grupoAtivo.descricao}</p>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button onClick={()=>abrirEditarGrupo(grupoAtivo)}
                    style={{ padding:'7px 12px', background:'rgba(59,130,246,.1)', border:'1px solid rgba(59,130,246,.25)', borderRadius:8, color:'#93C5FD', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    ✏️ Editar grupo
                  </button>
                  <button onClick={()=>copiarConvite(grupoAtivo)}
                    style={{ padding:'7px 12px', background:'rgba(34,197,94,.1)', border:'1px solid rgba(34,197,94,.25)', borderRadius:8, color:'#86EFAC', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    🔗 Link de convite
                  </button>
                  <button onClick={()=>{ setEditandoEmpresa(null); setFormEmpresa({nome:'',cnpj:'',tipo:'unica',email:'',telefone:''}); setModalEmpresa(true); setMsg(''); }}
                    style={{ padding:'7px 12px', background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:8, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                    + Empresa / CNPJ
                  </button>
                  <button onClick={()=>excluirGrupo(grupoAtivo)}
                    style={{ padding:'7px 12px', background:'rgba(239,68,68,.1)', border:'1px solid rgba(239,68,68,.25)', borderRadius:8, color:'#FCA5A5', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    🗑️
                  </button>
                </div>
              </div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {[
                  `${grupoAtivo.total_empresas||0} empresa${grupoAtivo.total_empresas!==1?'s':''}`,
                  `${grupoAtivo.total_usuarios||0} usuário${grupoAtivo.total_usuarios!==1?'s':''} no portal`,
                  `${grupoAtivo.chamados_abertos||0} chamados abertos`,
                ].map(t=>(
                  <span key={t} style={{ background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`, borderRadius:999, padding:'4px 12px', fontSize:11 }}>{t}</span>
                ))}
              </div>
            </div>

            <h3 style={{ fontSize:13, color:C.muted, fontWeight:500, marginBottom:10, textTransform:'uppercase', letterSpacing:'.5px' }}>Empresas / CNPJs</h3>
            <EmpresasGrupo grupoId={grupoAtivo.id} token={getToken()} onEditar={abrirEditarEmpresa} onExcluir={excluirEmpresa}/>
          </div>
        ) : (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', color:C.muted, fontSize:13 }}>
            Selecione um cliente à esquerda
          </div>
        )}
      </div>

      {/* MODAL GRUPO (criar/editar) */}
      {modalGrupo && (
        <Modal title={editandoGrupo ? `Editar — ${editandoGrupo.nome}` : 'Novo Grupo / Cliente'} onClose={()=>{ setModalGrupo(false); setEditandoGrupo(null); setMsg(''); }}>
          {!editandoGrupo && <p style={{ color:C.muted, fontSize:13, marginBottom:16, lineHeight:1.6 }}>Um <b style={{ color:'#F1F5F9' }}>grupo</b> representa o contrato ATI. Pode ter uma ou várias empresas/CNPJs vinculados.</p>}
          <form onSubmit={salvarGrupo}>
            <Field label="Nome do grupo / cliente *">
              <input style={inp} required placeholder="Ex: Grupo Delta, Farmácia Vida..." value={formGrupo.nome} onChange={e=>setFormGrupo({...formGrupo,nome:e.target.value})}/>
            </Field>
            <Field label="Descrição / observação">
              <input style={inp} placeholder="Ex: Contrato mensal, 3 filiais, segmento saúde..." value={formGrupo.descricao} onChange={e=>setFormGrupo({...formGrupo,descricao:e.target.value})}/>
            </Field>
            {msg && <p style={{ color:'#FCA5A5', fontSize:12, marginTop:6 }}>{msg}</p>}
            {!editandoGrupo && (
              <div style={{ background:'rgba(59,130,246,.08)', border:'1px solid rgba(59,130,246,.2)', borderRadius:8, padding:'10px 12px', marginTop:8 }}>
                <p style={{ color:'#93C5FD', fontSize:11, margin:0, lineHeight:1.6 }}>
                  Após criar, você poderá adicionar empresas/CNPJs e um link de convite será gerado automaticamente.
                </p>
              </div>
            )}
            <button type="submit" style={{ width:'100%', padding:12, background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:10, color:'#fff', fontWeight:700, cursor:'pointer', marginTop:14 }}>
              {editandoGrupo ? '💾 Salvar alterações' : 'Criar Grupo'}
            </button>
          </form>
        </Modal>
      )}

      {/* MODAL EMPRESA (criar/editar) */}
      {modalEmpresa && grupoAtivo && (
        <Modal title={editandoEmpresa ? `Editar — ${editandoEmpresa.nome}` : `Nova Empresa — ${grupoAtivo.nome}`} onClose={()=>{ setModalEmpresa(false); setEditandoEmpresa(null); setMsg(''); }} width={580}>
          <form onSubmit={salvarEmpresa}>
            <Field label="Tipo de empresa *">
              <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap:8, marginBottom:4 }}>
                {Object.entries(TIPOS).map(([k,v]) => (
                  <div key={k} onClick={()=>setFormEmpresa({...formEmpresa,tipo:k})}
                    style={{ padding:'10px 8px', borderRadius:8, cursor:'pointer', textAlign:'center', border:`1px solid ${formEmpresa.tipo===k ? C.red : C.border}`, background: formEmpresa.tipo===k ? 'rgba(227,27,35,.1)' : 'rgba(255,255,255,.04)' }}>
                    <div style={{ fontSize:20, marginBottom:4 }}>{v.icon}</div>
                    <div style={{ fontSize:11, fontWeight:600, color: formEmpresa.tipo===k ? '#F1F5F9' : C.muted }}>{v.label}</div>
                    <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{v.desc}</div>
                  </div>
                ))}
              </div>
            </Field>
            <Field label="Nome da empresa *">
              <input style={inp} required placeholder="Razão social ou nome fantasia" value={formEmpresa.nome} onChange={e=>setFormEmpresa({...formEmpresa,nome:e.target.value})}/>
            </Field>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <Field label="CNPJ">
                <input style={inp} placeholder="00.000.000/0001-00" value={formEmpresa.cnpj} onChange={e=>setFormEmpresa({...formEmpresa,cnpj:e.target.value})}/>
              </Field>
              <Field label="Telefone">
                <input style={inp} placeholder="(92) 99999-0000" value={formEmpresa.telefone} onChange={e=>setFormEmpresa({...formEmpresa,telefone:e.target.value})}/>
              </Field>
            </div>
            <Field label="E-mail de contato de TI">
              <input style={inp} type="email" placeholder="ti@empresa.com.br" value={formEmpresa.email} onChange={e=>setFormEmpresa({...formEmpresa,email:e.target.value})}/>
            </Field>
            {msg && <p style={{ color:'#FCA5A5', fontSize:12, marginTop:6 }}>{msg}</p>}
            <button type="submit" style={{ width:'100%', padding:12, background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:10, color:'#fff', fontWeight:700, cursor:'pointer', marginTop:12 }}>
              {editandoEmpresa ? '💾 Salvar alterações' : 'Adicionar Empresa'}
            </button>
          </form>
        </Modal>
      )}

      {/* MODAL LINK CONVITE */}
      {linkModal && (
        <Modal title="Link de Convite do Portal" onClose={()=>setLinkModal(null)}>
          <p style={{ color:C.muted, fontSize:13, marginBottom:6 }}>Grupo: <b style={{ color:'#F1F5F9' }}>{linkModal.nome}</b></p>
          <p style={{ color:C.muted, fontSize:13, marginBottom:12, lineHeight:1.6 }}>Compartilhe este link com os usuários do cliente para se cadastrarem no portal de suporte.</p>
          <div style={{ background:'rgba(0,0,0,.3)', border:`1px solid ${C.border}`, borderRadius:10, padding:12, fontSize:12, wordBreak:'break-all', color:'#86EFAC', lineHeight:1.6 }}>
            {linkModal.link}
          </div>
          <button onClick={()=>navigator.clipboard.writeText(linkModal.link)}
            style={{ marginTop:12, width:'100%', padding:10, background:'rgba(34,197,94,.15)', border:`1px solid rgba(34,197,94,.3)`, borderRadius:10, color:'#86EFAC', fontWeight:700, cursor:'pointer' }}>
            📋 Copiar Link
          </button>
        </Modal>
      )}
    </div>
  );
}


// ─── EMPRESAS DO GRUPO (sub-componente) ──────────────────
function EmpresasGrupo({ grupoId, token, onEditar, onExcluir }) {
  const [empresas, setEmpresas] = useState([]);

  useEffect(() => {
    api(`/grupos/${grupoId}/empresas`, {}, token)
      .then(d => setEmpresas(arr(d,'empresas')))
      .catch(()=>{});
  }, [grupoId, token]);

  const TIPO_ICON = { unica:'🏢', matriz:'🏛️', filial:'🏪' };
  const TIPO_COR  = { unica:'#3B82F6', matriz:'#8B5CF6', filial:'#22C55E' };

  if (!empresas.length) return (
    <div style={{ background:'rgba(255,255,255,.04)', border:`1px dashed rgba(255,255,255,.12)`, borderRadius:10, padding:28, textAlign:'center', color:C.muted, fontSize:13 }}>
      Nenhuma empresa cadastrada. Clique em <b style={{ color:C.red }}>+ Empresa / CNPJ</b> para adicionar.
    </div>
  );

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:12 }}>
      {empresas.map(e => (
        <div key={e.id} style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:16, borderLeft:`3px solid ${TIPO_COR[e.tipo]||C.blue}` }}>
          <div style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom:10 }}>
            <span style={{ fontSize:22 }}>{TIPO_ICON[e.tipo]||'🏢'}</span>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:13, color:'#F1F5F9', marginBottom:3 }}>{e.nome}</div>
              <span style={{ fontSize:10, padding:'2px 8px', borderRadius:999, fontWeight:600, background:`${TIPO_COR[e.tipo]||C.blue}22`, color:TIPO_COR[e.tipo]||C.blue }}>
                {{ unica:'Empresa única', matriz:'Matriz', filial:'Filial' }[e.tipo] || e.tipo}
              </span>
            </div>
            <div style={{ display:'flex', gap:4, flexShrink:0 }}>
              {onEditar && (
                <button onClick={()=>onEditar(e)}
                  style={{ background:'rgba(59,130,246,.1)', border:'1px solid rgba(59,130,246,.2)', borderRadius:6, padding:'3px 7px', color:'#93C5FD', fontSize:11, cursor:'pointer' }}
                  title="Editar empresa">✏️</button>
              )}
              {onExcluir && (
                <button onClick={()=>onExcluir(e)}
                  style={{ background:'rgba(239,68,68,.1)', border:'1px solid rgba(239,68,68,.2)', borderRadius:6, padding:'3px 7px', color:'#FCA5A5', fontSize:11, cursor:'pointer' }}
                  title="Excluir empresa">🗑️</button>
              )}
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
            {e.cnpj     && <div style={{ fontSize:11, color:C.muted }}>📋 {e.cnpj}</div>}
            {e.email    && <div style={{ fontSize:11, color:C.muted }}>📧 {e.email}</div>}
            {e.telefone && <div style={{ fontSize:11, color:C.muted }}>📞 {e.telefone}</div>}
            <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>
              {e.total_unidades||0} unidade{e.total_unidades!==1?'s':''}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── ATIVOS ──────────────────────────────────────────────
function Ativos() {
  const [ativos, setAtivos] = useState([]);
  const [syncando, setSyncando] = useState(false);
  const [modalCmd, setModalCmd] = useState(null); // ativo selecionado para comando
  const [cmdEnviando, setCmdEnviando] = useState(false);
  const [cmdResultado, setCmdResultado] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [empresasFiltroAtivo, setEmpresasFiltroAtivo] = useState([]);
  const [modal, setModal] = useState(false);
  const [editandoAtivo, setEditandoAtivo] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [form, setForm] = useState({ tipo:'computador', nome:'', marca:'', modelo:'', numero_serie:'', ip:'', mac:'', so:'', localizacao:'', cliente_id:'', grupo_id:'', empresa_id:'', status:'ativo', rustdesk_id:'' });

  const [avisoAtivo, setAvisoAtivo] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState('');
  const enviarComando = async (ativo, comando, parametros = {}) => {
    setCmdEnviando(true);
    setCmdResultado(null);
    try {
      const r = await api('/agente/comandos', {
        method: 'POST',
        body: JSON.stringify({ hostname: ativo.nome, ativo_id: ativo.id, comando, parametros })
      });
      setCmdResultado({ sucesso: true, msg: 'Comando enviado! O agente executará em até 5 minutos.' });
    } catch(e) {
      setCmdResultado({ erro: e.message });
    } finally {
      setCmdEnviando(false);
    }
  };
  const sincronizarOCS = async () => {
    setSyncando(true);
    setSyncResult(null);
    try {
      const r = await api('/ocs/importar', { method: 'POST', body: JSON.stringify({}) });
      setSyncResult(r);
      if (r.sucesso) carregar();
    } catch(e) { setSyncResult({ erro: e.message }); }
    finally { setSyncando(false); }
  };

  const carregar = useCallback(()=>{
    Promise.all([api('/ativos'),api('/clientes'),api('/grupos')]).then(([a,cl,g])=>{ setAtivos(arr(a,'ativos')); setClientes(arr(cl,'clientes')); setGrupos(arr(g,'grupos')); }).catch(()=>{});
  },[]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{ carregar(); },[]);

  async function salvar(e) {
    e.preventDefault();
    setErroForm('');

    if (!avisoAtivo) {
      const dup = await api('/ativos/verificar-duplicata', {
        method: 'POST',
        body: JSON.stringify({
          mac: form.mac || '',
          numero_serie: form.numero_serie || '',
          id_excluir: editandoAtivo ? editandoAtivo.id : null
        })
      });
      if (dup && dup.duplicata) {
        setAvisoAtivo(dup.conflitos);
        return;
      }
    }

    setSalvando(true);
    try {
      const r = editandoAtivo
        ? await api(`/ativos/${editandoAtivo.id}`, { method:'PUT', body: JSON.stringify(form) })
        : await api('/ativos', { method:'POST', body: JSON.stringify(form) });

      if (r && r.erro) { setErroForm(r.erro); setSalvando(false); return; }
      setModal(false); setAvisoAtivo(null); setErroForm('');
      setEditandoAtivo(null); setForm({ tipo:'computador', nome:'', marca:'', modelo:'',
        numero_serie:'', ip:'', mac:'', so:'', localizacao:'', cliente_id:'', status:'ativo', rustdesk_id:'' });
      carregar();
    } catch(err) {
      setErroForm('Erro ao salvar. Tente novamente.');
    }
    setSalvando(false);
  }

  const filtrados = ativos.filter(a=>`${a.nome||''} ${a.tipo||''} ${a.cliente_nome||''} ${a.ip||''}`.toLowerCase().includes(filtro.toLowerCase()));
  const tipoIcon = { computador:'💻', servidor:'🖥️', impressora:'🖨️', switch:'🔀', roteador:'📡', outro:'📦' };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div><h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800 }}>Ativos</h1><p style={{ margin:0, color:C.muted, fontSize:13 }}>Inventário de equipamentos e infraestrutura</p></div>
        <button onClick={()=>{ setEditandoAtivo(null); setEmpresasFiltroAtivo([]); setForm({ tipo:'computador', nome:'', marca:'', modelo:'', numero_serie:'', ip:'', mac:'', so:'', localizacao:'', cliente_id:'', grupo_id:'', empresa_id:'', status:'ativo', rustdesk_id:'' }); setModal(true); }} style={{ background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:10, padding:'10px 18px', color:'#fff', fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}>
          <Icon name="plus" size={15}/> Novo Ativo
        </button>
        {syncResult && (
          <span style={{fontSize:12,color:syncResult.sucesso?"#22c55e":"#ef4444",alignSelf:"center"}}>
            {syncResult.sucesso ? `✓ ${syncResult.importados} importados, ${syncResult.atualizados} atualizados` : `✗ ${syncResult.erro||"Erro"}`}
          </span>
        )}
        <button onClick={sincronizarOCS} disabled={syncando} style={{background:"#22c55e",color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontWeight:700,cursor:syncando?"wait":"pointer",display:"flex",alignItems:"center",gap:6,opacity:syncando?0.7:1}}>
          {syncando ? "⏳ Sincronizando..." : "🔄 Sync OCS"}
        </button>
      </div>

      <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:16, padding:18 }}>
        <input style={{ ...inp, maxWidth:360, marginBottom:16 }} placeholder="Buscar por nome, tipo, IP ou cliente..." value={filtro} onChange={e=>setFiltro(e.target.value)}/>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:700 }}>
            <thead>
              <tr>{['Tipo','Nome','Fabricante/Modelo','IP','MAC','Cliente','Localização','Status','Remoto','Ações'].map(h=>(
                <th key={h} style={{ textAlign:'left', padding:'10px 14px', fontSize:11, color:C.muted, background:'rgba(0,0,0,.3)', fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {filtrados.map(a=>(
                <tr key={a.id} style={{ borderTop:`1px solid ${C.border}` }}>
                  <td style={{ padding:'10px 14px', fontSize:18 }}>{tipoIcon[a.tipo]||'📦'}</td>
                  <td style={{ padding:'10px 14px', fontSize:13, fontWeight:600 }}>{a.nome}</td>
                  <td style={{ padding:'10px 14px', fontSize:12, color:C.muted }}>{[a.marca,a.modelo].filter(Boolean).join(' / ')||'—'}</td>
                  <td style={{ padding:'10px 14px', fontSize:12, color:C.blue, fontFamily:'monospace' }}>{a.ip||'—'}</td>
                  <td style={{ padding:'10px 14px', fontSize:11, color:C.muted, fontFamily:'monospace' }}>{a.mac||'—'}</td>
                  <td style={{ padding:'10px 14px', fontSize:12, color:C.muted }}>{a.cliente_nome||'—'}</td>
                  <td style={{ padding:'10px 14px', fontSize:12, color:C.muted }}>{a.localizacao||'—'}</td>
                  <td style={{ padding:'10px 14px' }}>
                    <select value={a.status} onChange={async e=>{ await api(`/ativos/${a.id}`, { method:'PUT', body:JSON.stringify({...a, status:e.target.value}) }); carregar(); }}
                      style={{ background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`, borderRadius:6, padding:'3px 6px', color:C.muted, fontSize:11, cursor:'pointer' }}>
                      {['ativo','inativo','manutencao','descartado'].map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td style={{ padding:'10px 14px', textAlign:'center' }}>
                    {a.rustdesk_id
                      ? <span style={{ fontSize:10, padding:'2px 8px', background:'rgba(6,182,212,.15)', color:'#A5F3FC', borderRadius:999, fontWeight:600, cursor:'pointer' }}
                          title={`ID: ${a.rustdesk_id}`}
                          onClick={()=>window.open(`rustdesk://${a.rustdesk_id}`,'_blank')}>
                          🖥️ {a.rustdesk_id}
                        </span>
                      : <span style={{ fontSize:10, color:C.muted }}>—</span>
                    }
                  </td>
                  <td style={{ padding:'10px 14px' }}>
                    <div style={{ display:'flex', gap:5 }}>
                      {a.rustdesk_id && (
                        <button
                          title={`Acesso remoto — RustDesk ID: ${a.rustdesk_id}`}
                          onClick={()=>{
                            const url = `rustdesk://${a.rustdesk_id}`;
                            window.open(url, '_blank');
                          }}
                          style={{ background:'rgba(6,182,212,.15)', border:'1px solid rgba(6,182,212,.3)', borderRadius:6, padding:'4px 8px', color:'#A5F3FC', fontSize:11, cursor:'pointer', fontWeight:600, display:'flex', alignItems:'center', gap:3 }}>
                          🖥️ Acessar
                        </button>
                      )}
                      <button title="Enviar comando ao agente" onClick={()=>{ setModalCmd(a); setCmdResultado(null); }} style={{background:'#f59e0b',color:'#fff',border:'none',borderRadius:6,padding:'4px 10px',cursor:'pointer',fontWeight:700,fontSize:13}}>⚡</button>
                      <button onClick={()=>{ setForm({...a, cliente_id:a.cliente_id||'', grupo_id:a.grupo_id||'', empresa_id:a.empresa_id||'', rustdesk_id:a.rustdesk_id||''}); if(a.grupo_id) api('/grupos/'+a.grupo_id+'/empresas',{},getToken()).then(d=>setEmpresasFiltroAtivo(arr(d,'empresas'))); else setEmpresasFiltroAtivo([]); setEditandoAtivo(a); setModal(true); }} style={{ background:'rgba(59,130,246,.1)', border:'1px solid rgba(59,130,246,.25)', borderRadius:6, padding:'4px 8px', color:'#93C5FD', fontSize:11, cursor:'pointer' }}>✏️</button>
                      <button onClick={async()=>{ if(window.confirm(`Excluir "${a.nome}"?`)){ await api(`/ativos/${a.id}`, { method:'DELETE' }); carregar(); }}} style={{ background:'rgba(239,68,68,.1)', border:'1px solid rgba(239,68,68,.25)', borderRadius:6, padding:'4px 8px', color:'#FCA5A5', fontSize:11, cursor:'pointer' }}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtrados.length && <tr><td colSpan={9} style={{ textAlign:'center', padding:40, color:C.muted }}>Nenhum ativo cadastrado.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modalCmd && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.65)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={e=>{if(e.target===e.currentTarget){setModalCmd(null);setCmdResultado(null);}}}>
          <div style={{background:'#1a1a2e',border:'1px solid #333',borderRadius:14,padding:28,minWidth:440,maxWidth:540,boxShadow:'0 12px 40px rgba(0,0,0,0.6)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
              <div>
                <h3 style={{color:'#fff',margin:0,fontSize:17}}>⚡ Comandos Remotos</h3>
                <div style={{color:'#888',fontSize:12,marginTop:3}}>{modalCmd.nome} {modalCmd.ip ? '· '+modalCmd.ip : ''}</div>
              </div>
              <button onClick={()=>{setModalCmd(null);setCmdResultado(null);}} style={{background:'rgba(255,255,255,.08)',border:'1px solid #444',borderRadius:6,color:'#aaa',fontSize:16,cursor:'pointer',padding:'4px 10px'}}>✕</button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:18}}>
              {[
                {cmd:'ping',           label:'🏓 Ping',              desc:'Verificar se online'},
                {cmd:'info',           label:'ℹ️ Info',              desc:'SO, IP, MAC, RustDesk ID'},
                {cmd:'executar_ocs',   label:'📦 Sync OCS',          desc:'Forçar inventário agora'},
                {cmd:'reboot',         label:'🔄 Reiniciar',         desc:'Reiniciar em 60s'},
                {cmd:'instalar_rustdesk', label:'🖥️ Inst. RustDesk', desc:'Download + install silencioso'},
                {cmd:'instalar_ocs',   label:'📋 Inst. OCS Agent',   desc:'Download + install OCS'},
              ].map(({cmd,label,desc})=>(
                <button key={cmd} onClick={()=>enviarComando(modalCmd,cmd)} disabled={cmdEnviando}
                  style={{background:'rgba(255,255,255,.05)',border:'1px solid #3a3a4e',borderRadius:9,padding:'13px 12px',cursor:cmdEnviando?'wait':'pointer',color:'#fff',textAlign:'left',transition:'background .15s',opacity:cmdEnviando?.6:1}}>
                  <div style={{fontWeight:700,fontSize:13}}>{label}</div>
                  <div style={{fontSize:11,color:'#666',marginTop:3}}>{desc}</div>
                </button>
              ))}
            </div>
            <div style={{marginBottom:14,padding:14,background:'rgba(255,255,255,.03)',border:'1px solid #2a2a3e',borderRadius:9}}>
              <div style={{color:'#999',fontSize:12,marginBottom:8,fontWeight:600}}>🔑 Alterar senha RustDesk</div>
              <div style={{display:'flex',gap:8}}>
                <input id="nova-senha-rd" type="text" placeholder="Nova senha..." style={{flex:1,background:'rgba(0,0,0,.4)',border:'1px solid #444',borderRadius:6,padding:'8px 10px',color:'#fff',fontSize:13}} />
                <button onClick={()=>{const s=document.getElementById('nova-senha-rd').value;if(s)enviarComando(modalCmd,'script_ps',{script:'$p="'+'"'+'" ;New-Item -Path HKLM:\\SOFTWARE\\RustDesk -Force|Out-Null;Set-ItemProperty -Path HKLM:\\SOFTWARE\\RustDesk\\RustDesk -Name password -Value $p'});}} disabled={cmdEnviando}
                  style={{background:'#e11d48',color:'#fff',border:'none',borderRadius:6,padding:'8px 14px',cursor:'pointer',fontWeight:700,fontSize:12,whiteSpace:'nowrap'}}>
                  Aplicar
                </button>
              </div>
            </div>
            {cmdResultado && (
              <div style={{padding:'10px 14px',borderRadius:8,fontSize:13,background:cmdResultado.sucesso?'rgba(34,197,94,.12)':'rgba(239,68,68,.12)',color:cmdResultado.sucesso?'#22c55e':'#ef4444',border:'1px solid '+(cmdResultado.sucesso?'rgba(34,197,94,.3)':'rgba(239,68,68,.3)')}}>
                {cmdResultado.sucesso ? '✓ '+cmdResultado.msg : '✗ '+cmdResultado.erro}
              </div>
            )}
          </div>
        </div>
      )}
      {modal && (
      <Modal title={editandoAtivo ? "Editar Ativo" : "Novo Ativo"} onClose={()=>{ setModal(false); setEditandoAtivo(null); }} width={640}>
          <form onSubmit={salvar}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <Field label="Tipo *">
                <select style={inp} value={form.tipo} onChange={e=>setForm({...form,tipo:e.target.value})}>
                  {['computador','servidor','impressora','switch','roteador','outro'].map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select style={inp} value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                  {['ativo','inativo','manutencao','descartado'].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Nome *"><input style={inp} required value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})}/></Field>
              <Field label="Cliente"><select style={inp} value={form.cliente_id} onChange={e=>setForm({...form,cliente_id:e.target.value})}><option value="">— Sem cliente —</option>{clientes.map(c=><option key={c.id} value={c.id}>{c.nome}</option>)}</select></Field>

              <Field label="Fabricante"><input style={inp} value={form.marca} onChange={e=>setForm({...form,marca:e.target.value})}/></Field>
              <Field label="Modelo"><input style={inp} value={form.modelo} onChange={e=>setForm({...form,modelo:e.target.value})}/></Field>
              <Field label="Nº Série"><input style={inp} value={form.numero_serie} onChange={e=>setForm({...form,numero_serie:e.target.value})}/></Field>
              <Field label="IP"><input style={inp} value={form.ip} onChange={e=>setForm({...form,ip:e.target.value})}/></Field>
              <Field label="MAC"><input style={inp} value={form.mac} onChange={e=>setForm({...form,mac:e.target.value})}/></Field>
              <Field label="Sistema Operacional"><input style={inp} value={form.so} onChange={e=>setForm({...form,so:e.target.value})}/></Field>
            </div>
            <Field label="Localização"><select style={inp} value={form.localizacao} onChange={e=>setForm({...form,localizacao:e.target.value})}><option value="">— Selecione —</option><option>Acesso Manoa</option><option>Acesso Djalma</option><option>Acesso Compensa</option><option>Doctor Centro</option><option>Doctor ZL</option><option>Doctor VieiraAlves</option><option>Lapac</option><option>IUAM</option><option>Coworkim</option><option>RH FENIX</option><option>CMS MATRIZ</option><option>CMS ANEXO</option></select></Field>
            <div style={{ background:'rgba(6,182,212,.08)', border:'1px solid rgba(6,182,212,.2)', borderRadius:10, padding:12, marginTop:4 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                <span style={{ fontSize:18 }}>🖥️</span>
                <div>
                  <div style={{ fontSize:12, fontWeight:600, color:'#A5F3FC' }}>Acesso Remoto — RustDesk</div>
                  <div style={{ fontSize:11, color:C.muted }}>ID da máquina no RustDesk para acesso remoto direto do painel</div>
                </div>
              </div>
              <Field label="RustDesk ID">
                <input style={inp} placeholder="Ex: 123456789" value={form.rustdesk_id||''} onChange={e=>setForm({...form,rustdesk_id:e.target.value})}/>
              </Field>
              {form.rustdesk_id && (
                <button type="button" onClick={()=>window.open(`rustdesk://${form.rustdesk_id}`,'_blank')}
                  style={{ marginTop:6, padding:'6px 14px', background:'rgba(6,182,212,.2)', border:'1px solid rgba(6,182,212,.4)', borderRadius:7, color:'#A5F3FC', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                  🖥️ Testar acesso remoto
                </button>
              )}
            </div>
            {avisoAtivo && (
            <div style={{background:'rgba(239,68,68,.08)',border:'1px solid rgba(239,68,68,.3)',borderRadius:8,padding:'10px 14px',marginBottom:10}}>
              <p style={{color:'#FCA5A5',fontWeight:500,fontSize:13,marginBottom:6}}>⚠️ Possível duplicata detectada:</p>
              {avisoAtivo.map((c,i) => (
                <p key={i} style={{color:'#FDA4AF',fontSize:12,margin:'2px 0'}}>
                  {c.campo === 'mac' ? '🔌 Mesmo MAC' : '🏷️ Mesmo nº série'}: <strong>{c.ativo.nome}</strong> (status: {c.ativo.status})
                </p>
              ))}
              <div style={{display:'flex',gap:8,marginTop:8}}>
                <button type="button" onClick={()=>setAvisoAtivo(null)}
                  style={{fontSize:12,padding:'4px 10px',borderRadius:6,border:'1px solid rgba(239,68,68,.4)',background:'transparent',color:'#FCA5A5',cursor:'pointer'}}>
                  Cancelar
                </button>
                <button type="button" onClick={async()=>{ setAvisoAtivo(null); await salvar({preventDefault:()=>{}}); }}
                  style={{fontSize:12,padding:'4px 10px',borderRadius:6,border:'1px solid rgba(34,197,94,.4)',background:'rgba(34,197,94,.1)',color:'#86EFAC',cursor:'pointer'}}>
                  Salvar mesmo assim
                </button>
              </div>
            </div>
          )}
          {erroForm && (
            <div style={{background:'rgba(239,68,68,.08)',border:'1px solid rgba(239,68,68,.3)',borderRadius:8,padding:'8px 12px',marginBottom:10,color:'#FCA5A5',fontSize:13}}>
              ❌ {erroForm}
            </div>
          )}
          <button type="submit" style={{ width:'100%', padding:12, background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:10, color:'#fff', fontWeight:700, cursor:'pointer', marginTop:8 }}>Salvar Ativo</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ─── CATEGORIAS ──────────────────────────────────────────
function Categorias() {
  const [cats, setCats] = useState([]);
  useEffect(()=>{ api('/categorias').then(d=>setCats(arr(d,'categorias'))).catch(()=>{}); },[]);
  return (
    <div>
      <h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800 }}>Categorias</h1>
      <p style={{ margin:'0 0 20px', color:C.muted, fontSize:13 }}>Tipos de serviço e atendimento</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:14 }}>
        {cats.map(c=>(
          <div key={c.id} style={{ background:'linear-gradient(180deg,rgba(255,255,255,.07),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:14, padding:18, borderLeft:`4px solid ${c.cor||C.red}` }}>
            <div style={{ fontSize:24, marginBottom:10 }}>🏷️</div>
            <h3 style={{ margin:'0 0 6px', fontSize:14 }}>{c.nome}</h3>
            <p style={{ margin:0, color:C.muted, fontSize:12 }}>{c.descricao||'Sem descrição'}</p>
          </div>
        ))}
        {!cats.length && <div style={{ gridColumn:'1/-1', textAlign:'center', padding:40, color:C.muted }}>Nenhuma categoria encontrada.</div>}
      </div>
    </div>
  );
}

// ─── SLA ─────────────────────────────────────────────────
function SLA() {
  const [config, setConfig] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [feriados, setFeriados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [novoFeriado, setNovoFeriado] = useState({ data:'', nome:'' });
  const [recalculando, setRecalculando] = useState(false);

  const PRIO_COR = { critica:'#EF4444', alta:'#F97316', media:'#3B82F6', baixa:'#22C55E' };
  const PRIO_ICON = { critica:'🔴', alta:'🟠', media:'🔵', baixa:'🟢' };
  const DIAS = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    const r = await api('/sla/config', {}, getToken());
    if (r.sucesso) {
      setConfig(r.config);
      setHorarios(r.horarios);
      setFeriados(r.feriados);
    }
    setLoading(false);
  }

  async function salvarHoras(prioridade, horas) {
    const r = await api(`/sla/config/${prioridade}`, {
      method:'PUT', body:JSON.stringify({ horas_uteis: parseInt(horas) })
    }, getToken());
    if (r.sucesso) { setMsg(`✅ SLA da prioridade ${prioridade} atualizado.`); setTimeout(()=>setMsg(''),3000); }
  }

  async function adicionarFeriado(e) {
    e.preventDefault();
    const r = await api('/sla/feriados', {
      method:'POST', body:JSON.stringify(novoFeriado)
    }, getToken());
    if (r.sucesso) {
      setNovoFeriado({ data:'', nome:'' });
      carregar();
    } else setMsg(r.mensagem);
  }

  async function removerFeriado(id) {
    if (!window.confirm('Remover este feriado?')) return;
    await api(`/sla/feriados/${id}`, { method:'DELETE' }, getToken());
    carregar();
  }

  async function recalcularTodos() {
    if (!window.confirm('Recalcular o prazo SLA de todos os chamados em aberto com base nas configurações atuais?')) return;
    setRecalculando(true);
    const r = await api('/sla/recalcular', { method:'POST' }, getToken());
    if (r.sucesso) setMsg(`✅ ${r.atualizados} chamados atualizados.`);
    else setMsg(`❌ ${r.mensagem}`);
    setRecalculando(false);
    setTimeout(()=>setMsg(''),5000);
  }

  if (loading) return <div style={{ textAlign:'center', padding:60, color:C.muted }}>Carregando...</div>;

  const feriadosFuturos = feriados.filter(f => new Date(f.data) >= new Date());
  const feriadosPassados = feriados.filter(f => new Date(f.data) < new Date());

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800 }}>SLA</h1>
          <p style={{ margin:0, color:C.muted, fontSize:13 }}>Acordos de nível de serviço em horas úteis</p>
        </div>
        <button onClick={recalcularTodos} disabled={recalculando}
          style={{ padding:'9px 16px', background:'rgba(245,158,11,.15)', border:'1px solid rgba(245,158,11,.3)', borderRadius:8, color:'#FCD34D', fontSize:12, fontWeight:600, cursor:'pointer' }}>
          {recalculando ? '⏳ Recalculando...' : '🔄 Recalcular chamados abertos'}
        </button>
      </div>

      {msg && <div style={{ padding:'10px 14px', background:'rgba(34,197,94,.1)', border:'1px solid rgba(34,197,94,.25)', borderRadius:10, color:'#86EFAC', fontSize:13, marginBottom:16 }}>{msg}</div>}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>

        {/* Prazos por prioridade */}
        <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:14, padding:18 }}>
          <h3 style={{ fontSize:14, fontWeight:600, marginBottom:14 }}>⏱️ Prazos em horas úteis</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {config.map(c => (
              <div key={c.prioridade} style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, width:100 }}>
                  <span style={{ fontSize:16 }}>{PRIO_ICON[c.prioridade]||'⚪'}</span>
                  <span style={{ fontSize:13, fontWeight:600, color:PRIO_COR[c.prioridade]||C.muted, textTransform:'capitalize' }}>{c.prioridade}</span>
                </div>
                <input type="number" min="1" max="999" defaultValue={c.horas_uteis}
                  onBlur={e => { if (parseInt(e.target.value) !== c.horas_uteis) salvarHoras(c.prioridade, e.target.value); }}
                  style={{ ...inp, width:80, textAlign:'center', fontSize:15, fontWeight:700, color:PRIO_COR[c.prioridade] }}/>
                <span style={{ fontSize:12, color:C.muted }}>horas úteis</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop:14, padding:'10px 12px', background:'rgba(59,130,246,.08)', border:'1px solid rgba(59,130,246,.15)', borderRadius:8, fontSize:11, color:'#93C5FD', lineHeight:1.6 }}>
            💡 Altere o valor e clique fora do campo para salvar automaticamente.
          </div>
        </div>

        {/* Horário comercial */}
        <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:14, padding:18 }}>
          <h3 style={{ fontSize:14, fontWeight:600, marginBottom:14 }}>🕐 Horário comercial ATI</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {[1,2,3,4,5,6].map(dia => {
              const h = horarios.find(x => x.dia_semana === dia);
              return (
                <div key={dia} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 10px', background: h ? 'rgba(34,197,94,.06)' : 'rgba(239,68,68,.06)', border:`1px solid ${h ? 'rgba(34,197,94,.15)' : 'rgba(239,68,68,.15)'}`, borderRadius:8 }}>
                  <span style={{ fontSize:12, fontWeight:600, width:30, color: h ? '#86EFAC' : '#FCA5A5' }}>{DIAS[dia]}</span>
                  {h ? (
                    <span style={{ fontSize:12, color:C.muted }}>
                      {h.hora_inicio.slice(0,5)} às {h.hora_fim.slice(0,5)}
                    </span>
                  ) : (
                    <span style={{ fontSize:12, color:'#FCA5A5' }}>Fechado</span>
                  )}
                  {dia === 6 && h && <span style={{ fontSize:10, padding:'1px 6px', background:'rgba(245,158,11,.15)', color:'#FCD34D', borderRadius:999, marginLeft:'auto' }}>meio período</span>}
                </div>
              );
            })}
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 10px', background:'rgba(239,68,68,.06)', border:'1px solid rgba(239,68,68,.15)', borderRadius:8 }}>
              <span style={{ fontSize:12, fontWeight:600, width:30, color:'#FCA5A5' }}>Dom</span>
              <span style={{ fontSize:12, color:'#FCA5A5' }}>Fechado</span>
            </div>
          </div>
          <div style={{ marginTop:12, fontSize:11, color:C.muted, lineHeight:1.6 }}>
            Para alterar o horário comercial, entre em contato com o administrador do sistema.
          </div>
        </div>
      </div>

      {/* Feriados */}
      <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:14, padding:18 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <h3 style={{ fontSize:14, fontWeight:600 }}>🗓️ Feriados nacionais ({feriadosFuturos.length} próximos)</h3>
        </div>

        {/* Adicionar feriado */}
        <form onSubmit={adicionarFeriado} style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
          <input type="date" style={{ ...inp, width:160 }} required value={novoFeriado.data} onChange={e=>setNovoFeriado({...novoFeriado,data:e.target.value})}/>
          <input style={{ ...inp, flex:1, minWidth:200 }} placeholder="Nome do feriado" required value={novoFeriado.nome} onChange={e=>setNovoFeriado({...novoFeriado,nome:e.target.value})}/>
          <button type="submit" style={{ padding:'9px 16px', background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:8, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
            + Adicionar
          </button>
        </form>

        {/* Lista de feriados futuros */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:8 }}>
          {feriadosFuturos.slice(0,20).map(f => (
            <div key={f.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 12px', background:'rgba(0,0,0,.2)', borderRadius:8, border:`1px solid ${C.border}` }}>
              <div>
                <div style={{ fontSize:12, fontWeight:500 }}>{f.nome}</div>
                <div style={{ fontSize:11, color:C.muted }}>
                  {new Date(f.data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday:'short', day:'2-digit', month:'long', year:'numeric' })}
                </div>
              </div>
              <button onClick={()=>removerFeriado(f.id)}
                style={{ background:'rgba(239,68,68,.1)', border:'1px solid rgba(239,68,68,.2)', borderRadius:6, padding:'3px 7px', color:'#FCA5A5', fontSize:11, cursor:'pointer' }}>
                🗑️
              </button>
            </div>
          ))}
        </div>

        {feriadosPassados.length > 0 && (
          <div style={{ marginTop:12, fontSize:11, color:C.muted }}>
            + {feriadosPassados.length} feriado(s) passado(s) cadastrados
          </div>
        )}
      </div>
    </div>
  );
}

// ─── RELATORIOS ──────────────────────────────────────────

// ─── LOADER XLSX ──────────────────────────────────────────
function useXLSX() {
  useEffect(() => {
    if (!window.XLSX) {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      document.head.appendChild(s);
    }
  }, []);
}

// ─── HELPERS RELATÓRIOS ──────────────────────────────────
function MiniBar({ valor, max, cor }) {
  const pct = max > 0 ? Math.round((valor/max)*100) : 0;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, height:6, background:'rgba(255,255,255,.08)', borderRadius:999 }}>
        <div style={{ width:`${pct}%`, height:'100%', background:cor||C.red, borderRadius:999, transition:'width .3s' }}/>
      </div>
      <span style={{ fontSize:11, color:C.muted, minWidth:24, textAlign:'right' }}>{valor}</span>
    </div>
  );
}

function MetricCardRel({ label, valor, sub, cor, icon }) {
  return (
    <div style={{ background:'linear-gradient(180deg,rgba(255,255,255,.07),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:14, padding:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div style={{ fontSize:12, color:C.muted, marginBottom:8 }}>{label}</div>
        {icon && <span style={{ fontSize:20 }}>{icon}</span>}
      </div>
      <div style={{ fontSize:28, fontWeight:800, color:cor||'#F1F5F9', marginBottom:4 }}>{valor??'—'}</div>
      {sub && <div style={{ fontSize:11, color:C.muted }}>{sub}</div>}
    </div>
  );
}

function Relatorios({ setAba: setAbaPrincipal }) {
  const hoje = new Date();
  const mesPassado = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().slice(0,10);
  const hojeStr = hoje.toISOString().slice(0,10);

  const [aba, setAba] = useState('dashboard');
  const [de, setDe] = useState(mesPassado);
  const [ate, setAte] = useState(hojeStr);
  const [grupoFiltro, setGrupoFiltro] = useState('');
  const [tecnicoFiltro, setTecnicoFiltro] = useState('');
  const [catFiltro, setCatFiltro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [prioFiltro, setPrioFiltro] = useState('');

  useXLSX();
  const [metricas, setMetricas] = useState(null);
  const [chamados, setChamados] = useState([]);
  const [grupoExec, setGrupoExec] = useState('');
  const [execData, setExecData] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api('/grupos', {}, getToken()),
      api('/usuarios', {}, getToken()),
      api('/portal/categorias'),
    ]).then(([g, u, c]) => {
      setGrupos(arr(g, 'grupos'));
      setTecnicos(arr(u, 'usuarios'));
      setCategorias(arr(c, 'categorias'));
    });
    carregarDashboard();
  }, []);

  function buildQuery() {
    const p = new URLSearchParams({ de, ate });
    if (grupoFiltro)  p.set('grupo_id', grupoFiltro);
    if (tecnicoFiltro)p.set('tecnico_id', tecnicoFiltro);
    if (catFiltro)    p.set('categoria_id', catFiltro);
    if (statusFiltro) p.set('status', statusFiltro);
    if (prioFiltro)   p.set('prioridade', prioFiltro);
    return p.toString();
  }

  async function carregarDashboard() {
    setLoading(true);
    const r = await api(`/relatorios/metricas?de=${de}&ate=${ate}${grupoFiltro?`&grupo_id=${grupoFiltro}`:''}`);
    setMetricas(r.sucesso ? r : null);
    setLoading(false);
  }

  async function carregarChamados() {
    setLoading(true);
    const r = await api(`/relatorios/atendimentos?${buildQuery()}`);
    setChamados(arr(r, 'registros'));
    setLoading(false);
  }

  const [ativos, setAtivos] = useState([]);
  const [ativosPorCliente, setAtivosPorCliente] = useState([]);
  const [filtroAtivoGrupo, setFiltroAtivoGrupo] = useState('');
  const [filtroAtivoStatus, setFiltroAtivoStatus] = useState('');

  async function carregarAtivos() {
    setLoading(true);
    const p = new URLSearchParams();
    if (filtroAtivoGrupo)  p.set('grupo_id', filtroAtivoGrupo);
    if (filtroAtivoStatus) p.set('status',   filtroAtivoStatus);
    const r = await api('/relatorios/ativos?' + p.toString());
    if (r.sucesso) { setAtivos(r.todos||[]); setAtivosPorCliente(r.por_cliente||[]); }
    setLoading(false);
  }

  async function carregarExecutivo() {
    if (!grupoExec) return;
    setLoading(true);
    const r = await api(`/relatorios/executivo/${grupoExec}?de=${de}&ate=${ate}`);
    setExecData(r.sucesso ? r : null);
    setLoading(false);
  }

  // ─── EXPORTAR EXCEL ───────────────────────────────────
  function exportarExcel() {
    const rows = chamados.map(c => ({
      'Protocolo': `ATI-${new Date().getFullYear()}-${String(c.id).padStart(6,'0')}`,
      'Título': c.titulo,
      'Status': c.status,
      'Prioridade': c.prioridade,
      'Grupo': c.grupo_nome||'',
      'Empresa': c.empresa_nome||'',
      'Categoria': c.categoria_nome||'',
      'Responsável': c.responsavel_nome||'',
      'Solicitante': c.solicitante_nome||'',
      'Criado em': c.criado_em ? new Date(c.criado_em).toLocaleString('pt-BR') : '',
      'SLA Status': c.sla_status||'',
      'TMR (horas)': c.tempo_resolucao_h||'',
      'Horas pausadas': c.horas_pausadas||0,
    }));

    const ws = {};
    const header = Object.keys(rows[0]||{});
    header.forEach((h,i) => { ws[String.fromCharCode(65+i)+'1'] = {v:h,t:'s'}; });
    rows.forEach((row,ri) => {
      header.forEach((h,ci) => {
        ws[String.fromCharCode(65+ci)+(ri+2)] = {v:row[h]??'', t:'s'};
      });
    });
    ws['!ref'] = `A1:${String.fromCharCode(65+header.length-1)}${rows.length+1}`;

    const wb = { Sheets:{ 'Chamados': ws }, SheetNames:['Chamados'] };
    const wbout = window.XLSX ? window.XLSX.write(wb, {type:'binary',bookType:'xlsx'}) : null;
    if (!wbout) { alert('Biblioteca Excel não carregada. Recarregue a página.'); return; }

    const buf = new ArrayBuffer(wbout.length);
    const view = new Uint8Array(buf);
    for (let i=0; i<wbout.length; i++) view[i] = wbout.charCodeAt(i) & 0xff;
    const blob = new Blob([buf], {type:'application/octet-stream'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ATIFlow_Relatorio_${de}_${ate}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  }

  // ─── EXPORTAR PDF ─────────────────────────────────────
  const exportarPDF = () => {
    const win = window.open('', '_blank');
    const periodo = de && ate ? `${de} a ${ate}` : 'Todos os períodos';
    const grupoNome = grupos.find(g => g.id == grupoFiltro)?.nome || 'Todos os clientes';
    const dataGeracao = new Date().toLocaleString('pt-BR');
    const m = metricas?.totais || {};
    const slaPct = m.total > 0 ? Math.round(((m.total - (m.sla_vencido||0)) / m.total) * 100) : 0;
    const slaCor = slaPct >= 90 ? '#16a34a' : slaPct >= 70 ? '#d97706' : '#dc2626';
    const tmr = m.tme_horas ? parseFloat(m.tme_horas).toFixed(1) + 'h' : 'N/D';

    const SLA_BADGE = {
      cumprido: '<span style="background:#dcfce7;color:#16a34a;padding:2px 8px;border-radius:4px;font-size:11px">Cumprido</span>',
      violado:  '<span style="background:#fee2e2;color:#dc2626;padding:2px 8px;border-radius:4px;font-size:11px">Violado</span>',
      no_prazo: '<span style="background:#dbeafe;color:#2563eb;padding:2px 8px;border-radius:4px;font-size:11px">No prazo</span>',
      sem_sla:  '<span style="background:#f1f5f9;color:#64748b;padding:2px 8px;border-radius:4px;font-size:11px">Sem SLA</span>',
    };
    const linhasChamados = (chamados || []).slice(0, 100).map(c => {
      const slaBadge = SLA_BADGE[c.sla_status] || SLA_BADGE.sem_sla;
      const statusCor = c.status === 'resolvido' ? '#16a34a' : c.status === 'em_atendimento' ? '#2563eb' : '#d97706';
      const tmrCell = c.tempo_resolucao_h != null ? c.tempo_resolucao_h + 'h' : '-';
      return `<tr>
        <td style="padding:8px 10px;border-bottom:1px solid #f1f5f9;font-size:12px;color:#64748b">${c.id}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #f1f5f9;font-size:12px;max-width:200px">${c.titulo || ''}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #f1f5f9;font-size:12px">${c.empresa_nome || '-'}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #f1f5f9;font-size:12px">
          <span style="color:${statusCor};font-weight:600">${(c.status||'').replace(/_/g,' ')}</span>
        </td>
        <td style="padding:8px 10px;border-bottom:1px solid #f1f5f9;font-size:12px">${c.prioridade || '-'}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #f1f5f9;font-size:12px">${c.responsavel_nome || '-'}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #f1f5f9;font-size:12px">${slaBadge}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #f1f5f9;font-size:12px;color:#64748b">${tmrCell}</td>
      </tr>`;
    }).join('');

    const linhasTecnicos = (metricas?.por_tecnico || []).map(t => `
      <tr>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9">${t.tecnico || t.nome || '-'}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center;font-weight:600">${t.total || 0}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center;color:#16a34a;font-weight:600">${t.resolvidos || 0}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center">${t.tme || t.tme || 'N/D'}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center">
          ${t.total > 0 ? Math.round(((t.resolvidos||0)/t.total)*100) : 0}%
        </td>
      </tr>`).join('');

    const linhasGrupos = (metricas?.por_grupo || []).map(g => `
      <tr>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;font-weight:500">${g.grupo || g.nome || '-'}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center;font-weight:600">${g.total || 0}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center;color:#16a34a">${g.resolvidos || g.abertos || 0}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center;color:#dc2626">${g.sla_vencido || 0}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center">
          ${g.total > 0 ? Math.round(((g.total-(g.sla_vencido||0))/g.total)*100) : 100}%
        </td>
      </tr>`).join('');

    const linhasCats = (metricas?.top_categorias || metricas?.por_categoria || []).slice(0,8).map((c,i) => `
      <tr>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9">${i+1}. ${c.categoria || c.nome || '-'}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center;font-weight:600">${c.total || 0}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9">
          <div style="background:#e2e8f0;border-radius:4px;height:8px;width:100%">
            <div style="background:#c8302a;border-radius:4px;height:8px;width:${Math.min(100,Math.round((c.total/(metricas?.totais?.total||1))*100))}%"></div>
          </div>
        </td>
      </tr>`).join('');

    win.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Relatorio ATI Services - ${grupoNome}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color:#1e293b; background:#fff; }
    @media print {
      .no-print { display:none !important; }
      .page-break { page-break-before: always; }
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    }
    .header { background:linear-gradient(135deg,#c8302a 0%,#8b1a1a 100%); color:#fff; padding:32px 40px; display:flex; justify-content:space-between; align-items:center; }
    .header-left h1 { font-size:26px; font-weight:700; letter-spacing:-0.5px; }
    .header-left p { font-size:13px; opacity:.85; margin-top:4px; }
    .header-right { text-align:right; font-size:12px; opacity:.85; line-height:1.8; }
    .badge { display:inline-block; background:rgba(255,255,255,.2); border-radius:20px; padding:4px 14px; font-size:12px; margin-top:8px; }
    .section { padding:32px 40px; border-bottom:1px solid #f1f5f9; }
    .section:last-child { border-bottom:none; }
    .section-title { font-size:15px; font-weight:700; color:#c8302a; text-transform:uppercase; letter-spacing:1px; margin-bottom:20px; display:flex; align-items:center; gap:8px; }
    .section-title::after { content:''; flex:1; height:1px; background:#fee2e2; }
    .metrics-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
    .metric-card { background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; padding:20px; text-align:center; }
    .metric-card.destaque { background:linear-gradient(135deg,#c8302a,#8b1a1a); color:#fff; border:none; }
    .metric-card.destaque .metric-label { color:rgba(255,255,255,.8); }
    .metric-value { font-size:36px; font-weight:800; line-height:1; }
    .metric-label { font-size:12px; color:#64748b; margin-top:6px; font-weight:500; }
    .metric-sub { font-size:11px; color:#94a3b8; margin-top:4px; }
    .sla-bar-bg { background:#e2e8f0; border-radius:8px; height:12px; overflow:hidden; margin-top:8px; }
    .sla-bar-fill { height:12px; border-radius:8px; }
    table { width:100%; border-collapse:collapse; font-size:13px; }
    th { background:#f8fafc; padding:10px 12px; text-align:left; font-size:11px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.5px; border-bottom:2px solid #e2e8f0; }
    th.center { text-align:center; }
    tr:nth-child(even) td { background:#fafafa; }
    .footer { background:#f8fafc; padding:20px 40px; display:flex; justify-content:space-between; align-items:center; font-size:11px; color:#94a3b8; border-top:2px solid #fee2e2; }
    .print-btn { position:fixed; top:20px; right:20px; background:#c8302a; color:#fff; border:none; padding:12px 24px; border-radius:8px; cursor:pointer; font-size:14px; font-weight:600; box-shadow:0 4px 12px rgba(200,48,42,.4); z-index:999; }
    .print-btn:hover { background:#a82020; }
    .info-row { display:flex; gap:24px; margin-bottom:16px; flex-wrap:wrap; font-size:13px; color:#64748b; }
    .info-row strong { color:#1e293b; }
  </style>
</head>
<body>
  <button class="print-btn no-print" onclick="window.print()">Imprimir / Salvar PDF</button>

  <div class="header">
    <div class="header-left">
      <h1>ATI Services</h1>
      <p>Relatorio de Atendimento - ${grupoNome}</p>
      <span class="badge">Periodo: ${periodo}</span>
    </div>
    <div class="header-right">
      <div>Gerado em ${dataGeracao}</div>
      <div>Sistema ATI Flow v2</div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Visao Geral</div>
    <div class="metrics-grid">
      <div class="metric-card destaque">
        <div class="metric-value">${m.total || 0}</div>
        <div class="metric-label">Total de Chamados</div>
      </div>
      <div class="metric-card">
        <div class="metric-value" style="color:#d97706">${m.abertos || 0}</div>
        <div class="metric-label">Em Aberto</div>
        <div class="metric-sub">${m.total > 0 ? Math.round(((m.abertos||0)/m.total)*100) : 0}% do total</div>
      </div>
      <div class="metric-card">
        <div class="metric-value" style="color:#16a34a">${m.resolvidos || 0}</div>
        <div class="metric-label">Resolvidos</div>
        <div class="metric-sub">${m.total > 0 ? Math.round(((m.resolvidos||0)/m.total)*100) : 0}% do total</div>
      </div>
      <div class="metric-card">
        <div class="metric-value" style="color:#2563eb">${tmr}</div>
        <div class="metric-label">Tempo Medio Resolucao</div>
        <div class="metric-sub">Por chamado resolvido</div>
      </div>
    </div>
    <div style="margin-top:20px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:16px 20px">
      <div style="display:flex;justify-content:space-between;align-items:center">
        <span style="font-weight:600;font-size:14px">SLA - Cumprimento de Acordos</span>
        <span style="font-size:22px;font-weight:800;color:${slaCor}">${slaPct}%</span>
      </div>
      <div class="sla-bar-bg">
        <div class="sla-bar-fill" style="width:${slaPct}%;background:${slaCor}"></div>
      </div>
      <div style="display:flex;justify-content:space-between;margin-top:6px;font-size:11px;color:#94a3b8">
        <span>${m.sla_vencido || 0} vencidos</span>
        <span>${(m.total||0) - (m.sla_vencido||0)} dentro do prazo</span>
      </div>
    </div>
  </div>

  ${linhasGrupos ? `
  <div class="section">
    <div class="section-title">Atendimento por Cliente</div>
    <table>
      <thead><tr>
        <th>Cliente / Grupo</th><th class="center">Total</th>
        <th class="center">Resolvidos</th><th class="center">SLA Vencido</th><th class="center">SLA %</th>
      </tr></thead>
      <tbody>${linhasGrupos}</tbody>
    </table>
  </div>` : ''}

  ${linhasTecnicos ? `
  <div class="section page-break">
    <div class="section-title">Produtividade por Tecnico</div>
    <table>
      <thead><tr>
        <th>Tecnico</th><th class="center">Total</th>
        <th class="center">Resolvidos</th><th class="center">TMR</th><th class="center">Taxa Resolucao</th>
      </tr></thead>
      <tbody>${linhasTecnicos}</tbody>
    </table>
  </div>` : ''}

  ${linhasCats ? `
  <div class="section">
    <div class="section-title">Top Categorias de Chamados</div>
    <table>
      <thead><tr>
        <th>Categoria</th><th class="center">Chamados</th><th>Distribuicao</th>
      </tr></thead>
      <tbody>${linhasCats}</tbody>
    </table>
  </div>` : ''}

  ${linhasChamados ? `
  <div class="section page-break">
    <div class="section-title">Lista de Chamados${(chamados||[]).length > 100 ? ' (primeiros 100)' : ''}</div>
    <div class="info-row">
      <div><strong>${(chamados||[]).length}</strong> chamados no periodo</div>
      <div>Filtro: <strong>${grupoNome}</strong></div>
    </div>
    <table>
      <thead><tr>
        <th>#</th><th>Titulo</th><th>Empresa</th><th>Status</th>
        <th>Prioridade</th><th>Responsavel</th><th>SLA</th><th>Tempo Res.</th>
      </tr></thead>
      <tbody>${linhasChamados}</tbody>
    </table>
  </div>` : ''}

  <div class="footer">
    <div>
      <strong>ATI Services</strong> - Sistema de Gestao ATI Flow v2<br>
      Relatorio gerado automaticamente em ${dataGeracao}
    </div>
    <div style="text-align:right">
      <div>suporte.atiservices.com.br</div>
      <div style="margin-top:4px;color:#c8302a;font-weight:600">Tecnologia confiavel para empresas que nao podem parar.</div>
    </div>
  </div>
</body>
</html>`);
    win.document.close();
  }
  const STATUS_CORES = {
    aberto:'#64748B', em_analise:'#3B82F6', aguardando_aprovacao:'#F59E0B',
    em_execucao:'#8B5CF6', aguardando_cliente:'#F97316', resolvido:'#22C55E',
    rejeitado:'#EF4444', cancelado:'#6B7280', enc_prestador_externo:'#06B6D4',
    aguardando_feedback:'#F97316'
  };



  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800 }}>Relatórios</h1>
          <p style={{ margin:0, color:C.muted, fontSize:13 }}>Indicadores de atendimento e desempenho</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {aba==='chamados' && chamados.length>0 && <>
            <button onClick={exportarExcel} style={{ padding:'8px 14px', background:'rgba(34,197,94,.15)', border:'1px solid rgba(34,197,94,.3)', borderRadius:8, color:'#86EFAC', fontSize:12, fontWeight:600, cursor:'pointer' }}>📊 Excel</button>
            <button onClick={exportarPDF} style={{ padding:'8px 14px', background:'rgba(239,68,68,.15)', border:'1px solid rgba(239,68,68,.3)', borderRadius:8, color:'#FCA5A5', fontSize:12, fontWeight:600, cursor:'pointer' }}>📄 PDF</button>
          </>}
          {aba==='dashboard' && metricas && <>
            <button onClick={exportarPDF} style={{ padding:'8px 14px', background:'rgba(239,68,68,.15)', border:'1px solid rgba(239,68,68,.3)', borderRadius:8, color:'#FCA5A5', fontSize:12, fontWeight:600, cursor:'pointer' }}>📄 Exportar PDF</button>
          </>}
        </div>
      </div>

      {/* Filtros globais */}
      <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:14, marginBottom:14 }}>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
          <div><div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>De</div><input type="date" style={{ ...inp, width:140 }} value={de} onChange={e=>setDe(e.target.value)}/></div>
          <div><div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Até</div><input type="date" style={{ ...inp, width:140 }} value={ate} onChange={e=>setAte(e.target.value)}/></div>
          <div><div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Grupo</div>
            <select style={{ ...inp, width:160 }} value={grupoFiltro} onChange={e=>setGrupoFiltro(e.target.value)}>
              <option value="">Todos</option>
              {grupos.map(g=><option key={g.id} value={g.id}>{g.nome}</option>)}
            </select>
          </div>
          <div><div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Técnico</div>
            <select style={{ ...inp, width:140 }} value={tecnicoFiltro} onChange={e=>setTecnicoFiltro(e.target.value)}>
              <option value="">Todos</option>
              {tecnicos.map(t=><option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
          </div>
          <div><div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Status</div>
            <select style={{ ...inp, width:140 }} value={statusFiltro} onChange={e=>setStatusFiltro(e.target.value)}>
              <option value="">Todos</option>
              {['aberto','em_analise','em_execucao','aguardando_aprovacao','aguardando_feedback','aguardando_cliente','enc_prestador_externo','resolvido','rejeitado','cancelado'].map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
            </select>
          </div>
          <div><div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Prioridade</div>
            <select style={{ ...inp, width:120 }} value={prioFiltro} onChange={e=>setPrioFiltro(e.target.value)}>
              <option value="">Todas</option>
              {['critica','alta','media','baixa'].map(p=><option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <button onClick={()=>{ if(aba==='dashboard') carregarDashboard(); else if(aba==='chamados') carregarChamados(); }}
            style={{ padding:'9px 18px', background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:8, color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 }}>
            🔍 Filtrar
          </button>
        </div>
      </div>

      {/* Abas */}
      <div style={{ display:'flex', gap:6, marginBottom:16, flexWrap:'wrap' }}>
        {[
          {id:'dashboard', label:'📊 Dashboard'},
          {id:'chamados',  label:'📋 Lista de chamados'},
          {id:'tecnico',   label:'👤 Por técnico'},
          {id:'executivo', label:'📄 Relatório executivo'},
          {id:'ativos',    label:'🖥️ Inventário de ativos'},
        ].map(a=>(
          <button key={a.id} onClick={()=>{ setAba(a.id); if(a.id==='chamados') carregarChamados(); if(a.id==='tecnico') carregarDashboard(); if(a.id==='ativos') carregarAtivos(); }}
            style={{ padding:'8px 16px', background:aba===a.id?`linear-gradient(135deg,${C.red},${C.redDark})`:'rgba(255,255,255,.06)', border:`1px solid ${aba===a.id?'transparent':C.border}`, borderRadius:8, color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}>
            {a.label}
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign:'center', padding:40, color:C.muted }}>⏳ Carregando...</div>}

      {/* ─── DASHBOARD ─────────────────────────────────────── */}
      {!loading && aba==='dashboard' && metricas && (
        <div>
          {/* Métricas principais */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:12, marginBottom:16 }}>
            <MetricCardRel label="Total de chamados" valor={metricas.totais?.total||0} icon="🎫"/>
            <MetricCardRel label="Em aberto" valor={metricas.totais?.abertos||0} cor="#F59E0B" icon="⏳"/>
            <MetricCardRel label="Resolvidos" valor={metricas.totais?.resolvidos||0} cor="#22C55E" icon="✅"/>
            <MetricCardRel label="TMR médio" valor={`${metricas.totais?.tme_horas||'—'}h`} icon="⏱️" sub="Tempo médio resolução"/>
            <MetricCardRel label="SLA cumprido" valor={metricas.totais?.sla_ok||0} cor="#22C55E" icon="🎯"/>
            <MetricCardRel label="SLA violado" valor={metricas.totais?.sla_violado||0} cor="#EF4444" icon="⚠️"/>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            {/* Por status */}
            <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:16 }}>
              <h3 style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>Chamados por status</h3>
              {metricas.por_status?.map(s=>(
                <div key={s.status} style={{ marginBottom:8 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.muted, marginBottom:3 }}>
                    <span>{s.status.replace(/_/g,' ')}</span><span>{s.total}</span>
                  </div>
                  <MiniBar valor={parseInt(s.total)} max={Math.max(...(metricas.por_status||[]).map(x=>parseInt(x.total)))} cor={STATUS_CORES[s.status]}/>
                </div>
              ))}
            </div>

            {/* Por grupo */}
            <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:16 }}>
              <h3 style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>Chamados por cliente</h3>
              {metricas.por_grupo?.map(g=>(
                <div key={g.nome} style={{ marginBottom:8 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.muted, marginBottom:3 }}>
                    <span>{g.nome||'Sem grupo'}</span><span>{g.total} ({g.abertos} abertos)</span>
                  </div>
                  <MiniBar valor={parseInt(g.total)} max={Math.max(...(metricas.por_grupo||[]).map(x=>parseInt(x.total)))} cor={C.blue}/>
                </div>
              ))}
            </div>

            {/* Top categorias */}
            <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:16 }}>
              <h3 style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>Top categorias</h3>
              {metricas.top_categorias?.map(c=>(
                <div key={c.nome} style={{ marginBottom:8 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.muted, marginBottom:3 }}>
                    <span>{c.nome||'Sem categoria'}</span><span>{c.total}</span>
                  </div>
                  <MiniBar valor={parseInt(c.total)} max={Math.max(...(metricas.top_categorias||[]).map(x=>parseInt(x.total)))} cor="#8B5CF6"/>
                </div>
              ))}
            </div>

            {/* Por prioridade */}
            <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:16 }}>
              <h3 style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>Por prioridade</h3>
              {metricas.por_prioridade?.map(p=>{
                const cores = { critica:'#EF4444', alta:'#F97316', media:'#F59E0B', baixa:'#22C55E' };
                return (
                  <div key={p.prioridade} style={{ marginBottom:8 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.muted, marginBottom:3 }}>
                      <span style={{ color:cores[p.prioridade] }}>{p.prioridade}</span><span>{p.total}</span>
                    </div>
                    <MiniBar valor={parseInt(p.total)} max={Math.max(...(metricas.por_prioridade||[]).map(x=>parseInt(x.total)))} cor={cores[p.prioridade]}/>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ─── LISTA DE CHAMADOS ──────────────────────────────── */}
      {!loading && aba==='chamados' && (
        <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, overflow:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr>{['Protocolo','Título','Grupo','Empresa','Responsável','Prioridade','Status','SLA','TMR','Criado'].map(h=>(
                <th key={h} style={{ textAlign:'left', padding:'10px 12px', fontSize:11, color:C.muted, background:'rgba(0,0,0,.3)', whiteSpace:'nowrap' }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {chamados.map(c=>{
                const slaCor = {cumprido:'#22C55E', violado:'#EF4444', no_prazo:'#F59E0B', sem_sla:C.muted}[c.sla_status]||C.muted;
                return (
                  <tr key={c.id} onClick={()=>{ window.__pendingCard={id:c.id,titulo:c.titulo}; setAbaPrincipal('kanban'); }} style={{ borderTop:`1px solid ${C.border}`, cursor:'pointer' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,.05)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'8px 12px', color:C.red, fontWeight:700, whiteSpace:'nowrap' }}>{proto(c.id)}</td>
                    <td style={{ padding:'8px 12px', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.titulo}</td>
                    <td style={{ padding:'8px 12px', color:C.muted, whiteSpace:'nowrap' }}>{c.grupo_nome||'—'}</td>
                    <td style={{ padding:'8px 12px', color:C.muted, whiteSpace:'nowrap' }}>{c.empresa_nome||'—'}</td>
                    <td style={{ padding:'8px 12px', color:C.muted, whiteSpace:'nowrap' }}>{c.responsavel_nome||'—'}</td>
                    <td style={{ padding:'8px 12px' }}><Badge value={c.prioridade}/></td>
                    <td style={{ padding:'8px 12px' }}><Badge value={c.status} type="status"/></td>
                    <td style={{ padding:'8px 12px', color:slaCor, fontWeight:600, fontSize:11 }}>{c.sla_status?.replace(/_/g,' ')||'—'}</td>
                    <td style={{ padding:'8px 12px', color:C.muted, whiteSpace:'nowrap' }}>{c.tempo_resolucao_h ? `${c.tempo_resolucao_h}h` : '—'}</td>
                    <td style={{ padding:'8px 12px', color:C.muted, whiteSpace:'nowrap', fontSize:11 }}>{c.criado_em ? new Date(c.criado_em).toLocaleDateString('pt-BR') : '—'}</td>
                  </tr>
                );
              })}
              {!chamados.length && <tr><td colSpan={10} style={{ textAlign:'center', padding:40, color:C.muted }}>Aplique os filtros e clique em Filtrar.</td></tr>}
            </tbody>
          </table>
          {chamados.length > 0 && (
            <div style={{ padding:'10px 14px', borderTop:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', fontSize:12, color:C.muted }}>
              <span>{chamados.length} chamados encontrados</span>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={exportarExcel} style={{ padding:'5px 12px', background:'rgba(34,197,94,.15)', border:'1px solid rgba(34,197,94,.3)', borderRadius:6, color:'#86EFAC', fontSize:11, fontWeight:600, cursor:'pointer' }}>📊 Excel</button>
                <button onClick={exportarPDF} style={{ padding:'5px 12px', background:'rgba(239,68,68,.15)', border:'1px solid rgba(239,68,68,.3)', borderRadius:6, color:'#FCA5A5', fontSize:11, fontWeight:600, cursor:'pointer' }}>📄 PDF</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── POR TÉCNICO ──────────────────────────────────── */}
      {!loading && aba==='tecnico' && metricas && (
        <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:16 }}>
          <h3 style={{ fontSize:14, fontWeight:600, marginBottom:14 }}>Desempenho por técnico</h3>
          {metricas.por_tecnico?.length ? (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead><tr>{['Técnico','Perfil','Chamados','TMR médio','SLA ok','SLA violado'].map(h=>(
                <th key={h} style={{ textAlign:'left', padding:'9px 12px', fontSize:11, color:C.muted, background:'rgba(0,0,0,.3)' }}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {metricas.por_tecnico.map((t,i)=>(
                  <tr key={i} style={{ borderTop:`1px solid ${C.border}` }}>
                    <td style={{ padding:'9px 12px', fontWeight:600 }}>{t.nome||'—'}</td>
                    <td style={{ padding:'9px 12px' }}>
                      <span style={{ fontSize:10, padding:'2px 8px', borderRadius:999, fontWeight:600, background:PERFIS[t.perfil]?.bg||'rgba(255,255,255,.1)', color:PERFIS[t.perfil]?.color||C.muted }}>
                        {PERFIS[t.perfil]?.label||t.perfil}
                      </span>
                    </td>
                    <td style={{ padding:'9px 12px', color:C.blue, fontWeight:700 }}>{t.total}</td>
                    <td style={{ padding:'9px 12px', color:C.muted }}>{t.tme ? `${t.tme}h` : '—'}</td>
                    <td style={{ padding:'9px 12px', color:'#22C55E' }}>—</td>
                    <td style={{ padding:'9px 12px', color:'#EF4444' }}>—</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div style={{ textAlign:'center', padding:30, color:C.muted }}>Nenhum dado. Clique em Filtrar.</div>}
        </div>
      )}

      {/* ─── RELATÓRIO EXECUTIVO ──────────────────────────── */}
      {aba==='executivo' && (
        <div>
          <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:14, marginBottom:14 }}>
            <div style={{ display:'flex', gap:10, alignItems:'flex-end' }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Selecionar grupo de cliente</div>
                <select style={inp} value={grupoExec} onChange={e=>setGrupoExec(e.target.value)}>
                  <option value="">— Selecione um grupo —</option>
                  {grupos.map(g=><option key={g.id} value={g.id}>{g.nome}</option>)}
                </select>
              </div>
              <button onClick={carregarExecutivo} disabled={!grupoExec}
                style={{ padding:'9px 18px', background:grupoExec?`linear-gradient(135deg,${C.red},${C.redDark})`:'rgba(255,255,255,.1)', border:'none', borderRadius:8, color:'#fff', fontWeight:700, cursor:grupoExec?'pointer':'default', fontSize:12 }}>
                Gerar relatório
              </button>
              {execData && <button onClick={exportarPDF} style={{ padding:'9px 18px', background:'rgba(239,68,68,.15)', border:'1px solid rgba(239,68,68,.3)', borderRadius:8, color:'#FCA5A5', fontSize:12, fontWeight:600, cursor:'pointer' }}>📄 Exportar PDF</button>}
            </div>
          </div>

          {!loading && execData && (
            <div>
              <div style={{ background:'linear-gradient(135deg,rgba(227,27,35,.15),rgba(227,27,35,.05))', border:`1px solid rgba(227,27,35,.2)`, borderRadius:14, padding:20, marginBottom:14 }}>
                <h2 style={{ fontSize:18, fontWeight:800, marginBottom:4 }}>{execData.grupo?.nome}</h2>
                <div style={{ fontSize:12, color:C.muted }}>Relatório de {new Date(de).toLocaleDateString('pt-BR')} a {new Date(ate).toLocaleDateString('pt-BR')} · {execData.grupo?.total_empresas} empresa(s)</div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:12, marginBottom:14 }}>
                <MetricCardRel label="Total chamados" valor={execData.totais?.total||0} icon="🎫"/>
                <MetricCardRel label="Resolvidos" valor={execData.totais?.resolvidos||0} cor="#22C55E" icon="✅"/>
                <MetricCardRel label="Em aberto" valor={execData.totais?.abertos||0} cor="#F59E0B" icon="⏳"/>
                <MetricCardRel label="TMR" valor={`${execData.totais?.tme_horas||'—'}h`} icon="⏱️"/>
                <MetricCardRel label="SLA ok" valor={execData.totais?.sla_ok||0} cor="#22C55E" icon="🎯"/>
                <MetricCardRel label="SLA violado" valor={execData.totais?.sla_violado||0} cor="#EF4444" icon="⚠️"/>
              </div>

              {execData.por_empresa?.length > 0 && (
                <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:16, marginBottom:14 }}>
                  <h3 style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>Por empresa / filial</h3>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead><tr>{['Empresa','Tipo','Total','Em aberto'].map(h=><th key={h} style={{ textAlign:'left', padding:'8px 12px', fontSize:11, color:C.muted, background:'rgba(0,0,0,.3)' }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {execData.por_empresa.map((e,i)=>(
                        <tr key={i} style={{ borderTop:`1px solid ${C.border}` }}>
                          <td style={{ padding:'8px 12px', fontWeight:500 }}>{e.nome||'—'}</td>
                          <td style={{ padding:'8px 12px', color:C.muted }}>{e.tipo}</td>
                          <td style={{ padding:'8px 12px', color:C.blue, fontWeight:700 }}>{e.total}</td>
                          <td style={{ padding:'8px 12px', color:'#F59E0B' }}>{e.abertos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {execData.top_categorias?.length > 0 && (
                <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:16 }}>
                  <h3 style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>Principais categorias de chamado</h3>
                  {execData.top_categorias.map((c,i)=>(
                    <div key={i} style={{ marginBottom:8 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.muted, marginBottom:3 }}>
                        <span>{c.nome||'Sem categoria'}</span><span>{c.total}</span>
                      </div>
                      <MiniBar valor={parseInt(c.total)} max={Math.max(...(execData.top_categorias||[]).map(x=>parseInt(x.total)))} cor="#8B5CF6"/>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {!loading && !execData && <div style={{ textAlign:'center', padding:40, color:C.muted }}>Selecione um grupo e clique em "Gerar relatório".</div>}
        </div>
      )}

      {aba==='ativos' && (
        <div>
          <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:14, marginBottom:14 }}>
            <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
              <div><div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Grupo</div>
                <select style={{ ...inp, width:180 }} value={filtroAtivoGrupo} onChange={e=>setFiltroAtivoGrupo(e.target.value)}>
                  <option value=''>Todos os grupos</option>
                  {grupos.map(g=><option key={g.id} value={g.id}>{g.nome}</option>)}
                </select>
              </div>
              <div><div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Status</div>
                <select style={{ ...inp, width:140 }} value={filtroAtivoStatus} onChange={e=>setFiltroAtivoStatus(e.target.value)}>
                  <option value=''>Todos</option>
                  <option value='ativo'>Ativo</option>
                  <option value='manutencao'>Manutenção</option>
                  <option value='desativado'>Desativado</option>
                  <option value='descartado'>Descartado</option>
                </select>
              </div>
              <button onClick={carregarAtivos} style={{ padding:'9px 18px', background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:8, color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 }}>🔍 Buscar</button>
            </div>
          </div>

          {ativosPorCliente.length === 0 && !loading && (
            <div style={{ textAlign:'center', padding:40, color:C.muted }}>Clique em "Buscar" para carregar o inventário.</div>
          )}

          {ativosPorCliente.length > 0 && (
            <div>
              <div style={{ marginBottom:10, fontSize:12, color:C.muted }}>{ativos.length} ativo(s) encontrado(s) em {ativosPorCliente.length} cliente(s)</div>
              {ativosPorCliente.map((cli, ci) => (
                <div key={ci} style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, marginBottom:10, overflow:'hidden' }}>
                  <div style={{ padding:'10px 16px', background:'rgba(0,0,0,.2)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div style={{ fontWeight:700, fontSize:13 }}>🏢 {cli.cliente}{cli.grupo ? <span style={{ fontSize:11, color:C.muted, fontWeight:400 }}> · {cli.grupo}</span> : ''}</div>
                    <div style={{ display:'flex', gap:8, fontSize:11 }}>
                      <span style={{ background:'rgba(34,197,94,.15)', color:'#86EFAC', padding:'2px 8px', borderRadius:99 }}>✅ {cli.ativos} ativos</span>
                      {cli.manutencao > 0 && <span style={{ background:'rgba(245,158,11,.15)', color:'#FCD34D', padding:'2px 8px', borderRadius:99 }}>🔧 {cli.manutencao} manutenção</span>}
                      {cli.desativados > 0 && <span style={{ background:'rgba(100,116,139,.15)', color:'#94A3B8', padding:'2px 8px', borderRadius:99 }}>⬛ {cli.desativados} desativados</span>}
                      <span style={{ color:C.muted }}>Total: {cli.total}</span>
                    </div>
                  </div>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead><tr>{['Nome','Tipo','Marca / Modelo','S.O.','IP','Último contato','Status'].map(h=>(
                      <th key={h} style={{ textAlign:'left', padding:'7px 12px', fontSize:11, color:C.muted, background:'rgba(0,0,0,.15)', whiteSpace:'nowrap' }}>{h}</th>
                    ))}</tr></thead>
                    <tbody>
                      {cli.itens.map((a,ai) => (
                        <tr key={ai} style={{ borderTop:`1px solid ${C.border}` }}>
                          <td style={{ padding:'7px 12px', fontWeight:500 }}>{a.nome||'—'}</td>
                          <td style={{ padding:'7px 12px', color:C.muted }}>{a.tipo||'—'}</td>
                          <td style={{ padding:'7px 12px', color:C.muted }}>{[a.marca,a.modelo].filter(Boolean).join(' ')||'—'}</td>
                          <td style={{ padding:'7px 12px', color:C.muted, fontSize:11 }}>{a.so||'—'}</td>
                          <td style={{ padding:'7px 12px', fontFamily:'monospace', fontSize:11 }}>{a.ip||'—'}</td>
                          <td style={{ padding:'7px 12px', color:C.muted, fontSize:11 }}>{a.ultimo_contato ? new Date(a.ultimo_contato).toLocaleDateString('pt-BR') : '—'}</td>
                          <td style={{ padding:'7px 12px' }}>
                            <span style={{ padding:'2px 8px', borderRadius:99, fontSize:10, fontWeight:600,
                              background: a.status==='ativo'?'rgba(34,197,94,.15)': a.status==='manutencao'?'rgba(245,158,11,.15)':'rgba(100,116,139,.15)',
                              color: a.status==='ativo'?'#86EFAC': a.status==='manutencao'?'#FCD34D':'#94A3B8'
                            }}>{a.status||'—'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── HELPERS PERFIS ──────────────────────────────────────
const PERFIS = {
  admin:       { label:'Administrador', color:'#E31B23', bg:'rgba(227,27,35,.15)' },
  supervisor:  { label:'Supervisor',    color:'#8B5CF6', bg:'rgba(139,92,246,.15)' },
  tecnico_n2:  { label:'Técnico N2',    color:'#3B82F6', bg:'rgba(59,130,246,.15)' },
  tecnico_n1:  { label:'Técnico N1',    color:'#F59E0B', bg:'rgba(245,158,11,.15)' },
  consultor:   { label:'Consultor',     color:'#64748B', bg:'rgba(100,116,139,.15)' },
};

// ─── USUARIOS ────────────────────────────────────────────
function Usuarios({ usuario }) {
  const [users, setUsers] = useState([]);
  const [gruposAtend, setGruposAtend] = useState([]);
  const [permLista, setPermLista] = useState([]);
  const [modal, setModal] = useState(false);
  const [modalPerm, setModalPerm] = useState(null);
  const [editandoUser, setEditandoUser] = useState(null);
  const [modalGrupos, setModalGrupos] = useState(null);
  const [userPerms, setUserPerms] = useState(null);
  const [form, setForm] = useState({ nome:'', email:'', senha:'', perfil:'tecnico_n1', whatsapp:'' });
  const [busca, setBusca] = useState('');
  const [msg, setMsg] = useState('');

  const carregar = useCallback(() => {
    Promise.all([
      api('/usuarios', {}, getToken()),
      api('/grupos-atendimento', {}, getToken()),
      api('/permissoes/lista', {}, getToken()),
    ]).then(([u, g, p]) => {
      setUsers(arr(u,'usuarios'));
      setGruposAtend(arr(g,'grupos'));
      setPermLista(p.permissoes || []);
    }).catch(()=>{});
  },[]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{ carregar(); },[]);

  async function salvar(e) {
    e.preventDefault(); setMsg('');
    let r;
    if (editandoUser) {
      r = await api(`/usuarios/${editandoUser.id}`, { method:'PUT', body:JSON.stringify({ nome:form.nome, perfil:form.perfil, ativo:editandoUser.ativo, whatsapp:form.whatsapp }) }, getToken());
    } else {
      r = await api('/usuarios', { method:'POST', body:JSON.stringify(form) }, getToken());
    }
    if (r?.sucesso) { setModal(false); setEditandoUser(null); setForm({ nome:'', email:'', senha:'', perfil:'tecnico_n1', whatsapp:'' }); carregar(); }
    else setMsg(r?.mensagem || 'Erro ao salvar.');
  }

  async function abrirPerms(u) {
    const r = await api(`/usuarios/${u.id}/permissoes`, {}, getToken());
    setUserPerms(r);
    setModalPerm(u);
  }

  async function togglePerm(userId, permissao, temPerm) {
    if (temPerm) {
      await api(`/usuarios/${userId}/permissoes/${permissao}`, { method:'DELETE' }, getToken());
    } else {
      await api(`/usuarios/${userId}/permissoes`, { method:'POST', body:JSON.stringify({permissao}) }, getToken());
    }
    abrirPerms(modalPerm);
  }

  function abrirEditar(u) {
    setForm({ nome:u.nome, email:u.email, senha:'', perfil:u.perfil, whatsapp:u.whatsapp||'' });
    setEditandoUser(u);
    setModal(true);
  }

  async function toggleBloquear(u) {
    if (!window.confirm(`${u.ativo?'Bloquear':'Ativar'} o usuário "${u.nome}"? ${u.ativo?'Ele perderá acesso ao painel.':''}`)) return;
    await api(`/usuarios/${u.id}`, { method:'PUT', body:JSON.stringify({ nome:u.nome, perfil:u.perfil, ativo:!u.ativo }) }, getToken());
    carregar();
  }

  async function toggleGrupo(grupoId, userId, estaNaGrupo) {
    if (estaNaGrupo) {
      await api(`/grupos-atendimento/${grupoId}/membros/${userId}`, { method:'DELETE' }, getToken());
    } else {
      await api(`/grupos-atendimento/${grupoId}/membros`, { method:'POST', body:JSON.stringify({usuario_id:userId, papel:'membro'}) }, getToken());
    }
    carregar();
  }

  const filtrados = users.filter(u =>
    `${u.nome} ${u.email} ${u.perfil}`.toLowerCase().includes(busca.toLowerCase())
  );

  const perfilInfo = (p) => PERFIS[p] || { label: p, color: C.muted, bg: 'rgba(255,255,255,.08)' };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800 }}>Equipe ATI Services</h1>
          <p style={{ margin:0, color:C.muted, fontSize:13 }}>Gerenciamento de usuários, perfis e permissões ITIL</p>
        </div>
        <button onClick={()=>{ setEditandoUser(null); setForm({ nome:'', email:'', senha:'', perfil:'tecnico_n1', whatsapp:'' }); setMsg(''); setModal(true); }} style={{ background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:10, padding:'10px 18px', color:'#fff', fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}>
          <Icon name="plus" size={15}/> Novo Usuário
        </button>
      </div>

      {/* GRUPOS DE ATENDIMENTO */}
      <div style={{ marginBottom:20 }}>
        <h3 style={{ fontSize:13, color:C.muted, marginBottom:10, fontWeight:500 }}>GRUPOS DE ATENDIMENTO</h3>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:10 }}>
          {gruposAtend.map(g => (
            <div key={g.id} style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:10, padding:12, borderLeft:`3px solid ${g.cor}` }}>
              <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>{g.nome}</div>
              <div style={{ fontSize:11, color:C.muted, marginBottom:6 }}>{g.descricao}</div>
              <div style={{ fontSize:11, color:C.muted }}>{(g.membros||[]).length} membros</div>
            </div>
          ))}
        </div>
      </div>

      {/* BUSCA */}
      <input style={{ ...inp, maxWidth:360, marginBottom:16 }} placeholder="Buscar por nome, e-mail ou perfil..." value={busca} onChange={e=>setBusca(e.target.value)}/>

      {/* LISTA DE USUÁRIOS */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:12 }}>
        {filtrados.map(u => {
          const p = perfilInfo(u.perfil);
          const gruposUsuario = gruposAtend.filter(g => (g.membros||[]).some(m => m.id === u.id));
          return (
            <div key={u.id} style={{ background:'linear-gradient(180deg,rgba(255,255,255,.07),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:14, padding:16 }}>
              <div style={{ display:'flex', gap:12, alignItems:'flex-start', marginBottom:12 }}>
                <div style={{ width:44, height:44, borderRadius:999, background:`linear-gradient(135deg,${C.red},${C.redDark})`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:16, flexShrink:0 }}>
                  {(u.nome||'U')[0].toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>{u.nome}</div>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:6, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.email}</div>
                  <span style={{ fontSize:11, background:p.bg, color:p.color, padding:'3px 10px', borderRadius:999, fontWeight:600 }}>{p.label}</span>
                </div>
              </div>

              {/* Grupos do usuário */}
              {gruposUsuario.length > 0 && (
                <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom:10 }}>
                  {gruposUsuario.map(g => (
                    <span key={g.id} style={{ fontSize:10, padding:'2px 7px', borderRadius:999, background:'rgba(255,255,255,.07)', color:C.muted, border:`1px solid ${g.cor}30` }}>
                      {g.nome}
                    </span>
                  ))}
                </div>
              )}

              <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                <button onClick={()=>abrirPerms(u)} style={{ flex:1, padding:'6px', background:'rgba(139,92,246,.12)', border:'1px solid rgba(139,92,246,.25)', borderRadius:7, color:'#C4B5FD', fontSize:11, fontWeight:600, cursor:'pointer', minWidth:80 }}>
                  🔑 Permissões
                </button>
                <button onClick={()=>setModalGrupos(u)} style={{ flex:1, padding:'6px', background:'rgba(59,130,246,.12)', border:'1px solid rgba(59,130,246,.25)', borderRadius:7, color:'#93C5FD', fontSize:11, fontWeight:600, cursor:'pointer', minWidth:70 }}>
                  👥 Grupos
                </button>
                <button onClick={()=>abrirEditar(u)} style={{ flex:1, padding:'6px', background:'rgba(245,158,11,.1)', border:'1px solid rgba(245,158,11,.25)', borderRadius:7, color:'#FCD34D', fontSize:11, fontWeight:600, cursor:'pointer', minWidth:60 }}>
                  ✏️ Editar
                </button>
                {u.id !== usuario?.id && (
                  <button onClick={()=>toggleBloquear(u)} style={{ flex:1, padding:'6px', background:u.ativo?'rgba(239,68,68,.1)':'rgba(34,197,94,.1)', border:`1px solid ${u.ativo?'rgba(239,68,68,.25)':'rgba(34,197,94,.25)'}`, borderRadius:7, color:u.ativo?'#FCA5A5':'#86EFAC', fontSize:11, fontWeight:600, cursor:'pointer', minWidth:70 }}>
                    {u.ativo ? '🚫 Bloquear' : '✅ Ativar'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* MODAL NOVO USUÁRIO */}
      {modal && (
        <Modal title={editandoUser ? "Editar Usuário" : "Novo Usuário"} onClose={()=>{ setModal(false); setEditandoUser(null); }}>
          <form onSubmit={salvar}>
            <Field label="Nome *"><input style={inp} required value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})}/></Field>
            {editandoUser && (
              <Field label="WhatsApp">
                <input
                  style={inp}
                  placeholder="(92) 99999-9999"
                  value={form.whatsapp}
                  onChange={e => {
                    let v = e.target.value.replace(/[^0-9]/g,'').substring(0,11);
                    if (v.length > 6) v = '(' + v.substring(0,2) + ') ' + v.substring(2,7) + '-' + v.substring(7);
                    else if (v.length > 2) v = '(' + v.substring(0,2) + ') ' + v.substring(2);
                    else if (v.length > 0) v = '(' + v;
                    setForm({...form, whatsapp: v});
                  }}
                />
              </Field>
            )}
            {!editandoUser && <Field label="E-mail *"><input style={inp} type="email" required={!editandoUser} value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></Field>}
            {editandoUser && <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 12px', marginBottom:12, fontSize:12, color:C.muted }}>📧 {editandoUser.email} <span style={{fontSize:10}}>(e-mail não pode ser alterado)</span></div>}
            {!editandoUser && <SenhaInput label="Senha *" required value={form.senha} onChange={e=>setForm({...form,senha:e.target.value})} showForca/>}
            <Field label="Perfil ITIL">
              <select style={inp} value={form.perfil} onChange={e=>setForm({...form,perfil:e.target.value})}>
                {Object.entries(PERFIS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:8, padding:10, marginTop:4 }}>
              <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Permissões do perfil selecionado:</div>
              <div style={{ fontSize:11, color:'#94A3B8', lineHeight:1.7 }}>
                {{ admin:'Acesso total ao sistema', supervisor:'Gestão de equipe e qualidade', tecnico_n2:'Suporte especializado e escalações', tecnico_n1:'Primeiro atendimento', consultor:'Somente visualização' }[form.perfil]}
              </div>
            </div>
            {msg && <p style={{ color:'#FCA5A5', fontSize:12, marginTop:8 }}>{msg}</p>}
            <button type="submit" style={{ width:'100%', padding:12, background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:10, color:'#fff', fontWeight:700, cursor:'pointer', marginTop:12 }}>{editandoUser ? 'Salvar Usuário' : 'Criar Usuário'}</button>
          </form>
        </Modal>
      )}

      {/* MODAL PERMISSÕES */}
      {modalPerm && userPerms && (
        <Modal title={`Permissões — ${modalPerm.nome}`} onClose={()=>{ setModalPerm(null); setUserPerms(null); }} width={600}>
          <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:8, padding:10, marginBottom:14 }}>
            <div style={{ fontSize:11, color:C.muted, marginBottom:3 }}>Perfil base</div>
            <span style={{ ...PERFIS[modalPerm.perfil] && { color: PERFIS[modalPerm.perfil].color, background: PERFIS[modalPerm.perfil].bg }, fontSize:12, padding:'3px 10px', borderRadius:999, fontWeight:600 }}>
              {PERFIS[modalPerm.perfil]?.label || modalPerm.perfil}
            </span>
            <span style={{ fontSize:11, color:C.muted, marginLeft:8 }}>Permissões extras podem ser adicionadas abaixo</span>
          </div>

          {/* Agrupar permissões por grupo */}
          {['Chamados','Aprovações','Comunicação','Relatórios','Administração','Inventário'].map(grupo => {
            const permsGrupo = permLista.filter(p => p.grupo === grupo);
            if (!permsGrupo.length) return null;
            return (
              <div key={grupo} style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:600, color:C.muted, marginBottom:8, textTransform:'uppercase', letterSpacing:'.5px' }}>{grupo}</div>
                {permsGrupo.map(p => {
                  const isBase = (userPerms.permissoes_base||[]).includes(p.id);
                  const isExtra = (userPerms.permissoes_extras||[]).some(e => e.permissao === p.id);
                  const temPerm = isBase || isExtra;
                  return (
                    <div key={p.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 10px', background:'rgba(255,255,255,.03)', borderRadius:7, marginBottom:4, border:`1px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontSize:12, color: temPerm ? '#F1F5F9' : C.muted }}>{p.label}</div>
                        {isBase && <div style={{ fontSize:10, color:'#64748B' }}>Incluído no perfil base</div>}
                        {isExtra && !isBase && <div style={{ fontSize:10, color:'#8B5CF6' }}>Permissão extra concedida</div>}
                      </div>
                      <button
                        onClick={()=>!isBase && togglePerm(modalPerm.id, p.id, isExtra)}
                        disabled={isBase}
                        style={{
                          width:36, height:20, borderRadius:999, border:'none', cursor: isBase ? 'default' : 'pointer',
                          background: temPerm ? (isBase ? '#334155' : '#22C55E') : 'rgba(255,255,255,.1)',
                          position:'relative', transition:'all .2s', flexShrink:0
                        }}
                      >
                        <span style={{ position:'absolute', top:2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'all .2s', left: temPerm ? 18 : 2 }}/>
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Modal>
      )}

      {/* MODAL GRUPOS DE ATENDIMENTO */}
      {modalGrupos && (
        <Modal title={`Grupos — ${modalGrupos.nome}`} onClose={()=>setModalGrupos(null)}>
          <p style={{ color:C.muted, fontSize:13, marginBottom:14 }}>Selecione em quais grupos de atendimento este técnico participa:</p>
          {gruposAtend.map(g => {
            const estaNaGrupo = (g.membros||[]).some(m => m.id === modalGrupos.id);
            return (
              <div key={g.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', background:'rgba(255,255,255,.04)', borderRadius:8, marginBottom:7, border:`1px solid ${C.border}`, borderLeft:`3px solid ${g.cor}` }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{g.nome}</div>
                  <div style={{ fontSize:11, color:C.muted }}>{g.descricao}</div>
                </div>
                <button
                  onClick={()=>toggleGrupo(g.id, modalGrupos.id, estaNaGrupo)}
                  style={{
                    width:40, height:22, borderRadius:999, border:'none', cursor:'pointer',
                    background: estaNaGrupo ? '#22C55E' : 'rgba(255,255,255,.1)',
                    position:'relative', transition:'all .2s', flexShrink:0
                  }}
                >
                  <span style={{ position:'absolute', top:3, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'all .2s', left: estaNaGrupo ? 21 : 3 }}/>
                </button>
              </div>
            );
          })}
        </Modal>
      )}
    </div>
  );
}

// ─── CONFIGURACOES ────────────────────────────────────────
function Configuracoes({ usuario }) {
  const [configs, setConfigs] = useState({});
  const [editando, setEditando] = useState(null);
  const [valorEdit, setValorEdit] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState('');
  const isAdmin = usuario?.perfil === 'admin';

  useEffect(() => {
    api('/configuracoes').then(d => {
      if (d.configuracoes) {
        const m = {};
        d.configuracoes.forEach(c => { m[c.chave] = c; });
        setConfigs(m);
      }
    }).catch(() => {});
  }, []);

  const get = (chave) => configs[chave]?.valor || '';

  const iniciarEdit = (chave) => {
    setEditando(chave);
    setValorEdit(get(chave));
    setMsg('');
  };

  const salvar = async (chave) => {
    setSalvando(true);
    try {
      const r = await api(`/configuracoes/${chave}`, { method: 'PUT', body: JSON.stringify({ valor: valorEdit }) });
      if (r.sucesso) {
        setConfigs(prev => ({ ...prev, [chave]: { ...prev[chave], valor: valorEdit } }));
        setMsg(`✓ "${chave}" atualizado`);
        setEditando(null);
      } else {
        setMsg(`✗ ${r.erro || 'Erro ao salvar'}`);
      }
    } catch(e) { setMsg(`✗ ${e.message}`); }
    setSalvando(false);
  };

  const copiar = (val) => { navigator.clipboard?.writeText(val); setMsg('✓ Copiado!'); setTimeout(() => setMsg(''), 2000); };

  const CampoConfig = ({ chave, label, senha }) => {
    const val = get(chave);
    const editandoEste = editando === chave;
    return (
      <div style={{ marginBottom:14, padding:14, background:'rgba(0,0,0,.25)', borderRadius:10, border:`1px solid ${editandoEste ? C.red : C.border}` }}>
        <div style={{ fontSize:11, color:C.muted, marginBottom:6, fontWeight:600, textTransform:'uppercase', letterSpacing:1 }}>{label}</div>
        {editandoEste ? (
          <div style={{ display:'flex', gap:8 }}>
            <input
              value={valorEdit}
              onChange={e => setValorEdit(e.target.value)}
              autoFocus
              style={{ flex:1, background:'rgba(255,255,255,.08)', border:`1px solid ${C.red}`, borderRadius:7, padding:'7px 10px', color:'#fff', fontSize:13 }}
              onKeyDown={e => { if (e.key === 'Enter') salvar(chave); if (e.key === 'Escape') setEditando(null); }}
            />
            <button onClick={() => salvar(chave)} disabled={salvando} style={{ background:C.red, border:'none', borderRadius:7, padding:'7px 14px', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 }}>
              {salvando ? '...' : 'Salvar'}
            </button>
            <button onClick={() => setEditando(null)} style={{ background:'rgba(255,255,255,.08)', border:`1px solid ${C.border}`, borderRadius:7, padding:'7px 10px', color:'#fff', cursor:'pointer', fontSize:12 }}>
              ✕
            </button>
          </div>
        ) : (
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ flex:1, fontSize:13, fontFamily:'monospace', wordBreak:'break-all', opacity: val ? 1 : 0.4 }}>
              {val ? (senha ? '••••••••' : val) : '(não definido)'}
            </span>
            <button onClick={() => copiar(val)} title="Copiar" style={{ background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`, borderRadius:6, padding:'4px 8px', color:C.muted, cursor:'pointer', fontSize:11 }}>📋</button>
            {isAdmin && (
              <button onClick={() => iniciarEdit(chave)} title="Editar" style={{ background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`, borderRadius:6, padding:'4px 8px', color:C.muted, cursor:'pointer', fontSize:11 }}>✏️</button>
            )}
          </div>
        )}
      </div>
    );
  };

  const scriptPS = `# Script de registro ATI Flow Agent
$SERVER = "https://suporte.atiservices.com.br"
$TOKEN  = "${get('agente_token')}"
$RDID   = (Get-ItemProperty "HKLM:\\SOFTWARE\\RustDesk\\RustDesk" -Name "id" -EA 0)?.id

$body = @{
  token      = $TOKEN
  hostname   = $env:COMPUTERNAME
  ip         = (Get-NetIPAddress -AddressFamily IPv4 -InterfaceAlias "Ethernet*","Wi-Fi*" -EA 0 | Select -First 1).IPAddress
  so         = (Get-WmiObject Win32_OperatingSystem).Caption
  rustdesk_id = $RDID
} | ConvertTo-Json

Invoke-RestMethod -Uri "$SERVER/api/agente/registrar" -Method POST -Body $body -ContentType "application/json"
Write-Host "Registrado com sucesso!"`;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800 }}>Configurações</h1>
          <p style={{ margin:0, color:C.muted, fontSize:13 }}>Preferências e integrações do sistema</p>
        </div>
      </div>

      {msg && <div style={{ marginBottom:16, padding:'10px 16px', background: msg.startsWith('✓') ? 'rgba(34,197,94,.15)' : 'rgba(239,68,68,.15)', border:`1px solid ${msg.startsWith('✓') ? '#22c55e' : '#ef4444'}`, borderRadius:8, fontSize:13, color: msg.startsWith('✓') ? '#22c55e' : '#ef4444' }}>{msg}</div>}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(min(420px, 100%), 1fr))', gap:20 }}>

        {/* Sessão */}
        <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:16, padding:20 }}>
          <h3 style={{ margin:'0 0 16px', fontSize:14, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:1 }}>👤 Sessão Atual</h3>
          <div style={{ display:'flex', alignItems:'center', gap:14, padding:14, background:'rgba(0,0,0,.3)', borderRadius:12 }}>
            <div style={{ width:46, height:46, borderRadius:999, background:`linear-gradient(135deg,${C.red},${C.redDark})`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:19 }}>{(usuario?.nome||'A').slice(0,1)}</div>
            <div>
              <div style={{ fontWeight:700 }}>{usuario?.nome||'Administrador'}</div>
              <div style={{ fontSize:12, color:C.muted }}>{usuario?.email}</div>
              <div style={{ fontSize:11, marginTop:3, color:C.red, fontWeight:600 }}>{usuario?.perfil}</div>
            </div>
          </div>
          <div style={{ marginTop:14, padding:14, background:'rgba(227,27,35,.08)', border:'1px solid rgba(227,27,35,.2)', borderRadius:10 }}>
            <div style={{ fontSize:12, color:C.muted, marginBottom:2 }}>Sistema</div>
            <div style={{ fontWeight:600, fontSize:14 }}>ATI Flow v2.0 Enterprise</div>
            <div style={{ fontSize:11, color:C.muted, marginTop:3 }}>Substituto do GLPI · ATI Services</div>
          </div>
        </div>

        {/* RustDesk */}
        <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:16, padding:20 }}>
          <h3 style={{ margin:'0 0 16px', fontSize:14, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:1 }}>🖥️ RustDesk</h3>
          <CampoConfig chave="rustdesk_servidor" label="Servidor (ID Server)" />
          <CampoConfig chave="rustdesk_chave"    label="Chave Pública (Key)" />
          <CampoConfig chave="rustdesk_senha_padrao" label="Senha Padrão" senha />
        </div>

        {/* OCS */}
        <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:16, padding:20 }}>
          <h3 style={{ margin:'0 0 16px', fontSize:14, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:1 }}>📦 OCS Inventory</h3>
          <CampoConfig chave="ocs_url" label="URL do Servidor OCS" />
          <div style={{ marginTop:8, padding:12, background:'rgba(0,0,0,.2)', borderRadius:8, fontSize:12, color:C.muted }}>
            <div style={{ marginBottom:4 }}>🔗 Console web:</div>
            <a href="https://suporte.atiservices.com.br/ocsreports/" target="_blank" rel="noreferrer" style={{ color:C.red, textDecoration:'none', fontFamily:'monospace' }}>
              suporte.atiservices.com.br/ocsreports/
            </a>
          </div>
        </div>

        {/* Agente */}
        <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:16, padding:20 }}>
          <h3 style={{ margin:'0 0 16px', fontSize:14, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:1 }}>🤖 Agente de Instalação</h3>
          <CampoConfig chave="agente_token" label="Token de Autenticação" />
          <div style={{ marginTop:10 }}>
            <div style={{ fontSize:11, color:C.muted, marginBottom:6, fontWeight:600, textTransform:'uppercase', letterSpacing:1 }}>Script PowerShell</div>
            <div style={{ position:'relative' }}>
              <pre style={{ margin:0, padding:'12px 40px 12px 12px', background:'rgba(0,0,0,.5)', borderRadius:8, fontSize:10, color:'#a3e635', overflowX:'auto', border:`1px solid ${C.border}`, lineHeight:1.5, whiteSpace:'pre-wrap', wordBreak:'break-all' }}>{scriptPS}</pre>
              <button onClick={() => copiar(scriptPS)} title="Copiar script" style={{ position:'absolute', top:8, right:8, background:'rgba(255,255,255,.08)', border:`1px solid ${C.border}`, borderRadius:5, padding:'4px 7px', color:'#fff', cursor:'pointer', fontSize:11 }}>📋</button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}


// ─── PENDENTES (APROVAÇÃO PORTAL) ────────────────────────
function Pendentes() {
  const [pendentes, setPendentes] = useState([]);
  const [portalAtivos, setPortalAtivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [abaPortal, setAbaPortal] = useState('pendentes');

  const carregar = () => {
    api('/portal/pendentes', {}, getToken()).then(d => {
      setPendentes(arr(d,'pendentes'));
    }).catch(()=>{}).finally(()=>setLoading(false));
  };

  const carregarPortalAtivos = () => {
    api('/portal/contatos', {}, getToken()).then(d => {
      setPortalAtivos(arr(d,'contatos'));
    }).catch(()=>{});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{ carregar(); },[]);

  async function aprovar(id, nome) {
    const r = await api(`/portal/contatos/${id}/aprovar`, { method:'PATCH' }, getToken());
    if (r.sucesso) { setMsg(`✅ ${nome} aprovado com sucesso.`); carregar(); }
  }

  async function rejeitar(id, nome) {
    if (!window.confirm(`Rejeitar cadastro de ${nome}?`)) return;
    const r = await api(`/portal/contatos/${id}/rejeitar`, { method:'PATCH' }, getToken());
    if (r.sucesso) { setMsg(`❌ ${nome} rejeitado.`); carregar(); }
  }

  async function bloquearPortal(c) {
    if (!window.confirm(`${c.ativo?'Bloquear':'Reativar'} acesso de "${c.nome_completo}" ao portal?`)) return;
    await api(`/portal/contatos/${c.id}/${c.ativo?'bloquear':'aprovar'}`, { method:'PATCH' }, getToken());
    setMsg(`${c.ativo?'🚫 Bloqueado':'✅ Reativado'}: ${c.nome_completo}`);
    carregarPortalAtivos();
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800 }}>Aprovações de Cadastro</h1>
          <p style={{ margin:0, color:C.muted, fontSize:13 }}>Usuários aguardando liberação de acesso ao portal</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={()=>setAbaPortal('pendentes')} style={{ padding:'8px 14px', background:abaPortal==='pendentes'?`linear-gradient(135deg,${C.red},${C.redDark})`:'rgba(255,255,255,.08)', border:`1px solid ${abaPortal==='pendentes'?'transparent':C.border}`, borderRadius:8, color:'#fff', cursor:'pointer', fontSize:12, fontWeight:600 }}>
            Pendentes {pendentes.length>0&&`(${pendentes.length})`}
          </button>
          <button onClick={()=>{ setAbaPortal('ativos'); carregarPortalAtivos(); }} style={{ padding:'8px 14px', background:abaPortal==='ativos'?`linear-gradient(135deg,${C.red},${C.redDark})`:'rgba(255,255,255,.08)', border:`1px solid ${abaPortal==='ativos'?'transparent':C.border}`, borderRadius:8, color:'#fff', cursor:'pointer', fontSize:12, fontWeight:600 }}>
            Usuários do Portal
          </button>
          <button onClick={abaPortal==='pendentes'?carregar:carregarPortalAtivos} style={{ background:'rgba(255,255,255,.08)', border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 14px', color:C.muted, cursor:'pointer', fontSize:12 }}>
            ↻
          </button>
        </div>
      </div>

      {msg && <div style={{ background:'rgba(34,197,94,.1)', border:'1px solid rgba(34,197,94,.25)', borderRadius:8, padding:'10px 14px', color:'#86EFAC', fontSize:13, marginBottom:16 }}>{msg}</div>}

      {loading && <div style={{ textAlign:'center', padding:40, color:C.muted }}>Carregando...</div>}

      {!loading && pendentes.length === 0 && (
        <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:40, textAlign:'center', color:C.muted }}>
          <div style={{ fontSize:36, marginBottom:12 }}>✅</div>
          <div style={{ fontSize:14, fontWeight:600, color:'#F1F5F9', marginBottom:6 }}>Nenhum cadastro pendente</div>
          <div style={{ fontSize:13 }}>Todos os usuários do portal foram aprovados.</div>
        </div>
      )}

      {!loading && pendentes.map(p => (
        <div key={p.id} style={{ background:'linear-gradient(180deg,rgba(255,255,255,.07),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:12, padding:20, marginBottom:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
            <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
              <div style={{ width:44, height:44, borderRadius:999, background:`linear-gradient(135deg,${C.red},${C.redDark})`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:16, flexShrink:0 }}>
                {(p.nome_completo||'?')[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:15, color:'#F1F5F9', marginBottom:3 }}>{p.nome_completo}</div>
                <div style={{ fontSize:12, color:C.muted, marginBottom:2 }}>📧 {p.email}</div>
                {p.whatsapp && <div style={{ fontSize:12, color:C.muted, marginBottom:2 }}>📱 {p.whatsapp}</div>}
                {p.cargo && <div style={{ fontSize:12, color:C.muted, marginBottom:2 }}>💼 {p.cargo}</div>}
                <div style={{ display:'flex', gap:8, marginTop:6, flexWrap:'wrap' }}>
                  <span style={{ background:'rgba(99,102,241,.15)', color:'#A5B4FC', padding:'2px 8px', borderRadius:999, fontSize:11, fontWeight:600 }}>{p.grupo_nome}</span>
                  <span style={{ background:'rgba(255,255,255,.08)', color:C.muted, padding:'2px 8px', borderRadius:999, fontSize:11 }}>{p.empresa_nome}</span>
                  <span style={{ color:'#4B5563', fontSize:11 }}>Solicitado em {new Date(p.created_at).toLocaleDateString('pt-BR')}</span>
                </div>
              </div>
            </div>
            <div style={{ display:'flex', gap:8, flexShrink:0 }}>
              <button onClick={()=>rejeitar(p.id, p.nome_completo)} style={{ padding:'8px 16px', background:'rgba(239,68,68,.15)', border:'1px solid rgba(239,68,68,.3)', borderRadius:8, color:'#FCA5A5', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                ✕ Rejeitar
              </button>
              <button onClick={()=>aprovar(p.id, p.nome_completo)} style={{ padding:'8px 16px', background:`linear-gradient(135deg,#22C55E,#16A34A)`, border:'none', borderRadius:8, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                ✓ Aprovar acesso
              </button>
            </div>
          </div>
        </div>
      ))}
      {/* ABA USUARIOS DO PORTAL ATIVOS */}
      {abaPortal === 'ativos' && (
        <div>
          {portalAtivos.length === 0 && (
            <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:40, textAlign:'center', color:C.muted }}>
              <div style={{ fontSize:36, marginBottom:12 }}>👤</div>
              <div style={{ fontSize:14 }}>Nenhum usuário do portal cadastrado ainda.</div>
            </div>
          )}
          {portalAtivos.map(c => (
            <div key={c.id} style={{ background:'linear-gradient(180deg,rgba(255,255,255,.07),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:12, padding:16, marginBottom:10, opacity:c.ativo?1:.6 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  <div style={{ width:38, height:38, borderRadius:999, background:`linear-gradient(135deg,${C.red},${C.redDark})`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:14, flexShrink:0 }}>
                    {(c.nome_completo||'?')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13 }}>{c.nome_completo}</div>
                    <div style={{ fontSize:11, color:C.muted }}>📧 {c.email} {c.whatsapp && `· 📱 ${c.whatsapp}`}</div>
                    <div style={{ display:'flex', gap:6, marginTop:4, flexWrap:'wrap' }}>
                      <span style={{ fontSize:10, background:'rgba(99,102,241,.15)', color:'#A5B4FC', padding:'2px 7px', borderRadius:999, fontWeight:600 }}>{c.grupo_nome}</span>
                      <span style={{ fontSize:10, background:'rgba(255,255,255,.08)', color:C.muted, padding:'2px 7px', borderRadius:999 }}>{c.empresa_nome}</span>
                      {!c.ativo && <span style={{ fontSize:10, background:'rgba(239,68,68,.15)', color:'#FCA5A5', padding:'2px 7px', borderRadius:999 }}>Bloqueado</span>}
                    </div>
                  </div>
                </div>
                <button onClick={()=>bloquearPortal(c)} style={{ padding:'7px 14px', background:c.ativo?'rgba(239,68,68,.12)':'rgba(34,197,94,.12)', border:`1px solid ${c.ativo?'rgba(239,68,68,.3)':'rgba(34,197,94,.3)'}`, borderRadius:8, color:c.ativo?'#FCA5A5':'#86EFAC', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  {c.ativo ? '🚫 Bloquear' : '✅ Reativar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// ─── CAMPO DE SENHA COM MOSTRAR/OCULTAR ──────────────────
function SenhaInput({ label, value, onChange, required = false, placeholder = '••••••••', showForca = false }) {
  const [mostrar, setMostrar] = useState(false);
  return (
    <Field label={label}>
      <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
        <input
          style={{ ...inp, paddingRight:44, flex:1 }}
          type={mostrar ? 'text' : 'password'}
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
        <button
          type="button"
          onClick={()=>setMostrar(!mostrar)}
          style={{
            position:'absolute', right:0,
            width:40, height:'100%',
            background:'transparent',
            border:'none',
            borderLeft:'1px solid rgba(255,255,255,.08)',
            cursor:'pointer',
            color:'#94A3B8',
            fontSize:15,
            display:'flex', alignItems:'center', justifyContent:'center',
            borderRadius:'0 8px 8px 0',
            flexShrink:0
          }}
          title={mostrar ? 'Ocultar senha' : 'Mostrar senha'}
        >
          {mostrar ? '🙈' : '👁'}
        </button>
      </div>
      {showForca && <ForcaSenha senha={value}/>}
    </Field>
  );
}

// ─── TROCAR SENHA ─────────────────────────────────────────
function TrocarSenha({ obrigatorio = false, onConcluido }) {
  const [form, setForm] = useState({ senha_atual:'', nova_senha:'', confirmar:'' });
  const [msg, setMsg] = useState('');
  const [erro, setErro] = useState('');

  async function salvar(e) {
    e.preventDefault(); setErro(''); setMsg('');
    if (form.nova_senha !== form.confirmar) { setErro('Senhas não conferem.'); return; }
    const erros = validarForcaSenha(form.nova_senha);
    if (erros.length) { setErro(erros.join('. ')); return; }
    const token = localStorage.getItem('atiflow_token') || getToken();
    if (!token) { setErro('Sessão expirada. Faça login novamente.'); return; }
    const r = await api('/trocar-senha', { method:'POST', body:JSON.stringify({ senha_atual:form.senha_atual, nova_senha:form.nova_senha }) }, token);
    if (!r.sucesso) { setErro(r.mensagem); return; }
    setMsg('Senha alterada com sucesso!');
    localStorage.removeItem('atiflow_trocar_senha');
    setTimeout(() => onConcluido && onConcluido(), 1500);
  }

  return (
    <div style={{ maxWidth:460, margin:'60px auto' }}>
      <div style={{ background:'linear-gradient(180deg,rgba(255,255,255,.07),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:16, padding:28 }}>
        {obrigatorio && (
          <div style={{ background:'rgba(245,158,11,.1)', border:'1px solid rgba(245,158,11,.25)', borderRadius:8, padding:'10px 14px', marginBottom:16 }}>
            <div style={{ color:'#FCD34D', fontSize:13, fontWeight:600 }}>⚠️ Troca de senha obrigatória</div>
            <div style={{ color:'#FCD34D', fontSize:12, marginTop:4 }}>Por segurança, você precisa criar uma nova senha antes de continuar.</div>
          </div>
        )}
        <h2 style={{ margin:'0 0 20px', fontSize:20, fontWeight:700 }}>
          {obrigatorio ? 'Criar nova senha' : 'Alterar senha'}
        </h2>
        <form onSubmit={salvar}>
          <SenhaInput label="Senha atual *" required value={form.senha_atual} onChange={e=>setForm({...form,senha_atual:e.target.value})}/>
          <SenhaInput label="Nova senha *" required value={form.nova_senha} onChange={e=>setForm({...form,nova_senha:e.target.value})} showForca/>
          <SenhaInput label="Confirmar nova senha *" required value={form.confirmar} onChange={e=>setForm({...form,confirmar:e.target.value})}/>
          {erro && <div style={{ color:'#FCA5A5', fontSize:12, marginTop:8, background:'rgba(239,68,68,.1)', padding:'8px 12px', borderRadius:8 }}>{erro}</div>}
          {msg  && <div style={{ color:'#86EFAC', fontSize:12, marginTop:8, background:'rgba(34,197,94,.1)', padding:'8px 12px', borderRadius:8 }}>{msg}</div>}
          <button type="submit" style={{ width:'100%', padding:12, background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:10, color:'#fff', fontWeight:700, cursor:'pointer', marginTop:14 }}>
            Salvar nova senha
          </button>
        </form>
      </div>
    </div>
  );
}


// ─── FERRAMENTAS (RustDesk + OCS) ─────────────────────────
// ─── HOOK GLOBAL ─────────────────────────────────────────────────────────────
const MobileContext = React.createContext(false);
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    let timer;
    const handler = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth < 768), 150);
    };
    window.addEventListener('resize', handler);
    return () => { window.removeEventListener('resize', handler); clearTimeout(timer); };
  }, []);
  return isMobile;
}
function Ferramentas() {
  const isMobile = React.useContext(MobileContext);
  const [aba, setAba] = useState('rustdesk');
  const [rdMetricas, setRdMetricas] = useState(null);
  const [rdStatus, setRdStatus] = useState(null);
  const [ocsStatus, setOcsStatus] = useState(null);
  const [dispositivos, setDispositivos] = useState([]);
  const [sessoes, setSessoes] = useState([]);
  const [sessaoAtiva, setSessaoAtiva] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [grupoFiltro, setGrupoFiltro] = useState('');
  const [modalSessao, setModalSessao] = useState(null);
  const [notasSessao, setNotasSessao] = useState('');
  const [cards, setCards] = useState([]);
  const [cardSessao, setCardSessao] = useState('');
  const [ocsDisp, setOcsDisp] = useState([]);
  const [importando, setImportando] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [grupoImport, setGrupoImport] = useState('');
  const [empresasImport, setEmpresasImport] = useState([]);
  const [empresaImport, setEmpresaImport] = useState('');

  useEffect(() => {
    api('/rustdesk/status', {}, getToken()).then(r => setRdStatus(r.online));
    api('/ocs/status', {}, getToken()).then(r => setOcsStatus(r.online));
    api('/rustdesk/metricas', {}, getToken()).then(r => r.sucesso && setRdMetricas(r));
    api('/grupos', {}, getToken()).then(d => setGrupos(arr(d,'grupos')));
    carregarDispositivos();
    carregarSessoes();
  }, []);

  async function carregarDispositivos() {
    const r = await api(`/rustdesk/dispositivos${grupoFiltro?`?grupo_id=${grupoFiltro}`:'' }`, {}, getToken());
    setDispositivos(arr(r,'dispositivos'));
  }

  async function carregarSessoes() {
    const r = await api('/rustdesk/sessoes?limite=30', {}, getToken());
    setSessoes(arr(r,'sessoes'));
  }

  async function abrirOCS() {
    const r = await api('/ocs/dispositivos', {}, getToken());
    setOcsDisp(arr(r,'dispositivos'));
  }

  async function importarOCS() {
    setImportando(true); setImportResult(null);
    const r = await api('/ocs/importar', {
      method:'POST',
      body:JSON.stringify({ grupo_id:grupoImport||null, empresa_id:empresaImport||null })
    }, getToken());
    setImportResult(r);
    setImportando(false);
    if (r.sucesso) carregarDispositivos();
  }

  async function iniciarSessao(dispositivo) {
    // Buscar chamados abertos para vincular
    const ch = await api('/relatorios/atendimentos?status=em_execucao', {}, getToken());
    setCards(arr(ch,'registros'));
    setModalSessao(dispositivo);
    setNotasSessao('');
    setCardSessao('');
  }

  async function confirmarSessao() {
    const r = await api('/rustdesk/sessao/iniciar', {
      method:'POST',
      body:JSON.stringify({ ativo_id: modalSessao.id, card_id: cardSessao||null, notas: notasSessao||null })
    }, getToken());
    if (!r.sucesso) {
      alert(`Erro: ${r.mensagem}`);
      return;
    }
    setSessaoAtiva({ ...r.sessao, ativo_nome: modalSessao.nome, rustdesk_url: r.rustdesk_url });
    setModalSessao(null);
    // Tentar abrir protocolo rustdesk://
    try {
      const a = document.createElement('a');
      a.href = r.rustdesk_url;
      a.click();
      // Avisar se RustDesk não estiver instalado
      setTimeout(() => {
        if (document.hasFocus()) {
          const msg = 'O RustDesk não abriu automaticamente.\n\nPossíveis causas:\n• RustDesk não está instalado neste computador\n• Protocolo rustdesk:// não está registrado\n\nInstale o RustDesk em: https://rustdesk.com/pt/';
          if (window.confirm(msg + '\n\nAbrir página de download?')) {
            window.open('https://rustdesk.com/pt/', '_blank');
          }
        }
      }, 2500);
    } catch(err) {
      alert('Não foi possível abrir o RustDesk. Verifique se está instalado.');
    }
    carregarDispositivos();
    carregarSessoes();
  }

  async function encerrarSessao() {
    if (!sessaoAtiva) return;
    await api(`/rustdesk/sessao/${sessaoAtiva.id}/encerrar`, {
      method:'PATCH', body:JSON.stringify({ notas: notasSessao||null })
    }, getToken());
    setSessaoAtiva(null);
    carregarDispositivos();
    carregarSessoes();
    api('/rustdesk/metricas', {}, getToken()).then(r => r.sucesso && setRdMetricas(r));
  }

  const TIPO_ICON = { computador:'💻', servidor:'🖥️', impressora:'🖨️', switch:'🔀', roteador:'📡', nobreak:'🔋' };

  return (
    <div>
      <h1 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800 }}>Ferramentas</h1>
      <p style={{ margin:'0 0 16px', color:C.muted, fontSize:13 }}>RustDesk · OCS Inventory · Acesso remoto e inventário automático</p>

      {/* Sessão ativa banner */}
      {sessaoAtiva && (
        <div style={{ background:'rgba(34,197,94,.12)', border:'1px solid rgba(34,197,94,.3)', borderRadius:12, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:22 }}>🟢</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#86EFAC' }}>Sessão remota ativa — {sessaoAtiva.ativo_nome}</div>
            <div style={{ fontSize:11, color:C.muted }}>Iniciada agora · RustDesk ID: {sessaoAtiva.rustdesk_id}</div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <input style={{ ...inp, width:200, fontSize:12 }} placeholder="Nota sobre o atendimento..." value={notasSessao} onChange={e=>setNotasSessao(e.target.value)}/>
            <button onClick={encerrarSessao} style={{ padding:'7px 14px', background:'rgba(239,68,68,.15)', border:'1px solid rgba(239,68,68,.3)', borderRadius:8, color:'#FCA5A5', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              ⏹ Encerrar sessão
            </button>
          </div>
        </div>
      )}

      {/* Abas */}
      <div style={{ display:'flex', gap:6, marginBottom:16, flexWrap:'wrap' }}>
        {[
          { id:'rustdesk', label:'🖥️ RustDesk' },
          { id:'sessoes',  label:'📋 Histórico de sessões' },
          { id:'ocs',      label:'📦 OCS Inventory' },
        ].map(a=>(
          <button key={a.id} onClick={()=>setAba(a.id)}
            style={{ padding:'8px 16px', background:aba===a.id?`linear-gradient(135deg,${C.red},${C.redDark})`:'rgba(255,255,255,.06)', border:`1px solid ${aba===a.id?'transparent':C.border}`, borderRadius:8, color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}>
            {a.label}
          </button>
        ))}
      </div>

      {/* ─── ABA RUSTDESK ─────────────────────────────────── */}
      {aba==='rustdesk' && (
        <div>
          {/* Métricas */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10, marginBottom:16 }}>
            {[
              { label:'Servidor', valor: rdStatus===null?'...' : rdStatus?'Online':'Offline', cor: rdStatus?'#22C55E':'#EF4444', icon:'🌐' },
              { label:'Dispositivos', valor: rdMetricas?.total_dispositivos??'—', icon:'💻' },
              { label:'Sessões ativas', valor: rdMetricas?.sessoes_ativas??'—', cor:'#22C55E', icon:'🟢' },
              { label:'Total sessões', valor: rdMetricas?.total_sessoes??'—', icon:'📋' },
              { label:'Duração média', valor: rdMetricas?.media_duracao_min ? `${rdMetricas.media_duracao_min}min` : '—', icon:'⏱️' },
            ].map((m,i)=>(
              <div key={i} style={{ background:'linear-gradient(180deg,rgba(255,255,255,.07),rgba(255,255,255,.03))', border:`1px solid ${C.border}`, borderRadius:12, padding:14 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                  <span style={{ fontSize:11, color:C.muted }}>{m.label}</span>
                  <span style={{ fontSize:18 }}>{m.icon}</span>
                </div>
                <div style={{ fontSize:24, fontWeight:800, color:m.cor||'#F1F5F9' }}>{m.valor}</div>
              </div>
            ))}
          </div>

          {/* Configuração */}
          <div style={{ background:'rgba(6,182,212,.06)', border:'1px solid rgba(6,182,212,.2)', borderRadius:12, padding:14, marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#A5F3FC', marginBottom:8 }}>⚙️ Configurar RustDesk nos clientes</div>
            <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap:10 }}>
              {[
                { label:'Servidor ID', valor:'178.104.94.225' },
                { label:'Relay Server', valor:'178.104.94.225' },
                { label:'Porta', valor:'21116' },
              ].map(c=>(
                <div key={c.label} style={{ background:'rgba(0,0,0,.3)', borderRadius:8, padding:'8px 12px' }}>
                  <div style={{ fontSize:10, color:C.muted, marginBottom:3 }}>{c.label}</div>
                  <code style={{ fontSize:12, color:'#A5F3FC', fontWeight:600 }}>{c.valor}</code>
                </div>
              ))}
            </div>
            <div style={{ marginTop:10, background:'rgba(0,0,0,.3)', borderRadius:8, padding:'8px 12px' }}>
              <div style={{ fontSize:10, color:C.muted, marginBottom:3 }}>Chave pública</div>
              <code style={{ fontSize:11, color:'#A5F3FC', wordBreak:'break-all' }}>Nn4o8B2xQIEexlJ3l4TOgqbx0wXLZ1u4jiMI6HsPlN4=</code>
            </div>
          </div>

          {/* Filtro + lista de dispositivos */}
          <div style={{ display:'flex', gap:10, marginBottom:12, alignItems:'flex-end' }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Filtrar por grupo</div>
              <select style={inp} value={grupoFiltro} onChange={e=>{ setGrupoFiltro(e.target.value); }}>
                <option value="">Todos os clientes</option>
                {grupos.map(g=><option key={g.id} value={g.id}>{g.nome}</option>)}
              </select>
            </div>
            <button onClick={carregarDispositivos} style={{ padding:'9px 16px', background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:8, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
              🔍 Filtrar
            </button>
          </div>

          {dispositivos.length === 0 ? (
            <div style={{ background:'rgba(255,255,255,.04)', border:`1px dashed ${C.border}`, borderRadius:12, padding:40, textAlign:'center', color:C.muted }}>
              <div style={{ fontSize:32, marginBottom:12 }}>🖥️</div>
              <div style={{ fontSize:14, fontWeight:600, color:'#F1F5F9', marginBottom:6 }}>Nenhum dispositivo com RustDesk ID</div>
              <div style={{ fontSize:12 }}>Cadastre o RustDesk ID nos ativos em Ativos → editar → campo RustDesk ID</div>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:12 }}>
              {dispositivos.map(d => (
                <div key={d.id} style={{ background:'linear-gradient(180deg,rgba(255,255,255,.07),rgba(255,255,255,.03))', border:`1px solid ${parseInt(d.sessao_ativa)>0?'rgba(34,197,94,.4)':C.border}`, borderRadius:14, padding:16 }}>
                  <div style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom:12 }}>
                    <span style={{ fontSize:26 }}>{TIPO_ICON[d.tipo]||'💻'}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:14, marginBottom:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.nome}</div>
                      <div style={{ fontSize:11, color:C.muted }}>{d.grupo_nome||'—'} · {d.empresa_nome||'—'}</div>
                      <div style={{ fontSize:11, color:C.muted, fontFamily:'monospace' }}>{d.ip||'—'}</div>
                    </div>
                    {parseInt(d.sessao_ativa)>0 && (
                      <span style={{ fontSize:10, padding:'2px 8px', background:'rgba(34,197,94,.2)', color:'#86EFAC', borderRadius:999, fontWeight:600, flexShrink:0 }}>Em uso</span>
                    )}
                  </div>
                  <div style={{ background:'rgba(0,0,0,.25)', borderRadius:8, padding:'6px 10px', marginBottom:10, display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize:10, color:C.muted }}>RustDesk ID</span>
                    <code style={{ fontSize:11, color:'#A5F3FC', fontWeight:600 }}>{d.rustdesk_id}</code>
                  </div>
                  {d.ultima_sessao && (
                    <div style={{ fontSize:10, color:C.muted, marginBottom:10 }}>
                      Último acesso: {new Date(d.ultima_sessao).toLocaleString('pt-BR')} · {d.ultimo_tecnico||'—'}
                    </div>
                  )}
                  <button onClick={()=>iniciarSessao(d)} disabled={parseInt(d.sessao_ativa)>0}
                    title={parseInt(d.sessao_ativa)>0 ? 'Dispositivo em uso por outro técnico' : `Conectar via RustDesk — ID: ${d.rustdesk_id}`}
                    style={{ width:'100%', padding:'8px', background:parseInt(d.sessao_ativa)>0?'rgba(255,255,255,.06)':`linear-gradient(135deg,#06B6D4,#0891B2)`, border:'none', borderRadius:8, color:'#fff', fontSize:12, fontWeight:700, cursor:parseInt(d.sessao_ativa)>0?'default':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, opacity:parseInt(d.sessao_ativa)>0?0.5:1 }}>
                    {parseInt(d.sessao_ativa)>0 ? '🔒 Em uso por outro técnico' : '🖥️ Conectar via RustDesk'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── ABA HISTÓRICO DE SESSÕES ──────────────────────── */}
      {aba==='sessoes' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <h3 style={{ fontSize:15, fontWeight:600 }}>Histórico de sessões remotas</h3>
            <button onClick={carregarSessoes} style={{ background:'rgba(255,255,255,.08)', border:`1px solid ${C.border}`, borderRadius:8, padding:'7px 12px', color:C.muted, cursor:'pointer', fontSize:12 }}>↻ Atualizar</button>
          </div>
          {sessoes.length === 0 ? (
            <div style={{ textAlign:'center', padding:40, color:C.muted }}>Nenhuma sessão registrada ainda.</div>
          ) : (
            <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr>{['Dispositivo','Cliente','Técnico','Chamado','Início','Duração','Status'].map(h=>(
                    <th key={h} style={{ textAlign:'left', padding:'9px 12px', fontSize:11, color:C.muted, background:'rgba(0,0,0,.3)', whiteSpace:'nowrap' }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {sessoes.map(s=>(
                    <tr key={s.id} style={{ borderTop:`1px solid ${C.border}` }}>
                      <td style={{ padding:'9px 12px', fontWeight:500 }}>{s.ativo_nome||s.dispositivo||'—'}</td>
                      <td style={{ padding:'9px 12px', color:C.muted }}>{s.grupo_nome||'—'}</td>
                      <td style={{ padding:'9px 12px', color:C.muted }}>{s.tecnico_nome||'—'}</td>
                      <td style={{ padding:'9px 12px', color:C.muted }}>
                        {s.chamado_titulo ? <span style={{ fontSize:10, color:C.red }}>{s.chamado_titulo.slice(0,25)}...</span> : '—'}
                      </td>
                      <td style={{ padding:'9px 12px', color:C.muted, whiteSpace:'nowrap', fontSize:11 }}>
                        {s.iniciado_em ? new Date(s.iniciado_em).toLocaleString('pt-BR') : '—'}
                      </td>
                      <td style={{ padding:'9px 12px', color:C.muted }}>
                        {s.duracao_min ? `${s.duracao_min}min` : s.encerrado_em ? '<1min' : '—'}
                      </td>
                      <td style={{ padding:'9px 12px' }}>
                        {s.encerrado_em
                          ? <span style={{ fontSize:10, padding:'2px 8px', background:'rgba(100,116,139,.2)', color:'#94A3B8', borderRadius:999 }}>Encerrada</span>
                          : <span style={{ fontSize:10, padding:'2px 8px', background:'rgba(34,197,94,.2)', color:'#86EFAC', borderRadius:999, fontWeight:600 }}>🟢 Ativa</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── ABA OCS INVENTORY ────────────────────────────── */}
      {aba==='ocs' && (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px', background:ocsStatus?'rgba(34,197,94,.08)':'rgba(239,68,68,.08)', border:`1px solid ${ocsStatus?'rgba(34,197,94,.25)':'rgba(239,68,68,.25)'}`, borderRadius:10 }}>
              <div style={{ width:10, height:10, borderRadius:'50%', background:ocsStatus?'#22C55E':'#EF4444' }}/>
              <span style={{ fontSize:13, fontWeight:500 }}>OCS Inventory</span>
              <span style={{ fontSize:12, color:C.muted, marginLeft:'auto' }}>{ocsStatus===null?'Verificando...':ocsStatus?'Online':'Offline'}</span>
            </div>
            <button onClick={()=>window.open('https://suporte.atiservices.com.br/ocsreports/','_blank')}
              style={{ padding:'12px', background:'rgba(139,92,246,.12)', border:'1px solid rgba(139,92,246,.3)', borderRadius:10, color:'#C4B5FD', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              🗂️ Abrir Painel OCS
            </button>
          </div>

          <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, padding:16, marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>Importar dispositivos para Ativos</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Grupo / cliente</div>
                <select style={inp} value={grupoImport} onChange={async e=>{
                  setGrupoImport(e.target.value);
                  if(e.target.value) {
                    const r = await api(`/grupos/${e.target.value}/empresas`,{},getToken());
                    setEmpresasImport(arr(r,'empresas'));
                  }
                }}>
                  <option value="">Todos os grupos</option>
                  {grupos.map(g=><option key={g.id} value={g.id}>{g.nome}</option>)}
                </select>
              </div>
              {empresasImport.length>0 && (
                <div>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Empresa</div>
                  <select style={inp} value={empresaImport} onChange={e=>setEmpresaImport(e.target.value)}>
                    <option value="">Todas</option>
                    {empresasImport.map(e=><option key={e.id} value={e.id}>{e.nome}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={abrirOCS} style={{ flex:1, padding:'9px', background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`, borderRadius:8, color:C.muted, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                👁 Ver dispositivos no OCS
              </button>
              <button onClick={importarOCS} disabled={importando}
                style={{ flex:2, padding:'9px', background:importando?'rgba(255,255,255,.08)':`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none', borderRadius:8, color:'#fff', fontSize:12, fontWeight:700, cursor:importando?'default':'pointer' }}>
                {importando ? '⏳ Importando...' : '⬇️ Importar OCS → Ativos ATI Flow'}
              </button>
            </div>
            {importResult && (
              <div style={{ marginTop:10, padding:'9px 12px', background:importResult.sucesso?'rgba(34,197,94,.1)':'rgba(239,68,68,.1)', border:`1px solid ${importResult.sucesso?'rgba(34,197,94,.25)':'rgba(239,68,68,.25)'}`, borderRadius:8, fontSize:12, color:importResult.sucesso?'#86EFAC':'#FCA5A5' }}>
                {importResult.sucesso
                  ? `✅ ${importResult.importados} importados · ${importResult.atualizados} atualizados · ${importResult.total} total`
                  : `❌ ${importResult.mensagem}`}
              </div>
            )}
          </div>

          {ocsDisp.length > 0 && (
            <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
              <div style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:13, fontWeight:600 }}>Dispositivos no OCS ({ocsDisp.length})</span>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead><tr>{['Nome','IP','MAC','S.O.','Último contato'].map(h=>(
                    <th key={h} style={{ textAlign:'left', padding:'8px 12px', fontSize:11, color:C.muted, background:'rgba(0,0,0,.3)', whiteSpace:'nowrap' }}>{h}</th>
                  ))}</tr></thead>
                  <tbody>
                    {ocsDisp.slice(0,30).map((d,i)=>(
                      <tr key={i} style={{ borderTop:`1px solid ${C.border}` }}>
                        <td style={{ padding:'8px 12px', fontWeight:500 }}>{d.NAME||d.name||'—'}</td>
                        <td style={{ padding:'8px 12px', color:'#93C5FD', fontFamily:'monospace', fontSize:11 }}>{d.IPADDR||d.ip||'—'}</td>
                        <td style={{ padding:'8px 12px', color:C.muted, fontFamily:'monospace', fontSize:10 }}>{d.MACADDR||d.mac||'—'}</td>
                        <td style={{ padding:'8px 12px', color:C.muted }}>{d.OSNAME||d.os||'—'}</td>
                        <td style={{ padding:'8px 12px', color:C.muted, fontSize:11 }}>{d.LASTDATE||d.lastdate||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── MODAL INICIAR SESSÃO ──────────────────────────── */}
      {modalSessao && (
        <Modal title={`Conectar via RustDesk — ${modalSessao.nome}`} onClose={()=>setModalSessao(null)}>
          <div style={{ background:'rgba(6,182,212,.08)', border:'1px solid rgba(6,182,212,.2)', borderRadius:10, padding:12, marginBottom:14 }}>
            <div style={{ fontSize:12, color:C.muted, marginBottom:4 }}>RustDesk ID</div>
            <code style={{ fontSize:18, fontWeight:800, color:'#A5F3FC' }}>{modalSessao.rustdesk_id}</code>
          </div>
          <Field label="Vincular a chamado em aberto (opcional)">
            <select style={inp} value={cardSessao} onChange={e=>setCardSessao(e.target.value)}>
              <option value="">— Sem chamado vinculado —</option>
              {cards.map(c=><option key={c.id} value={c.id}>{`ATI-${new Date().getFullYear()}-${String(c.id).padStart(6,'0')} · ${c.titulo}`}</option>)}
            </select>
          </Field>
          <Field label="Notas do atendimento (opcional)">
            <input style={inp} placeholder="Ex: Configurar impressora, instalar software..." value={notasSessao} onChange={e=>setNotasSessao(e.target.value)}/>
          </Field>
          <div style={{ background:'rgba(245,158,11,.08)', border:'1px solid rgba(245,158,11,.2)', borderRadius:8, padding:'9px 12px', marginTop:8, fontSize:12, color:'#FCD34D' }}>
            ⚡ Ao confirmar, o RustDesk será aberto automaticamente e a sessão será registrada no histórico.
          </div>
          <button onClick={confirmarSessao}
            style={{ width:'100%', padding:12, background:'linear-gradient(135deg,#06B6D4,#0891B2)', border:'none', borderRadius:10, color:'#fff', fontWeight:700, cursor:'pointer', marginTop:14, fontSize:13 }}>
            🖥️ Abrir RustDesk e iniciar sessão
          </button>
        </Modal>
      )}
    </div>
  );
}




// ─── BASE DE CONHECIMENTO ─────────────────────────────────
function BaseConhecimento() {
  const [artigos, setArtigos]   = React.useState([]);
  const [busca, setBusca]       = React.useState('');
  const [loading, setLoading]   = React.useState(true);
  const [modal, setModal]       = React.useState(null); // null | 'novo' | artigo
  const [form, setForm]         = React.useState({ titulo:'', conteudo:'', publico:false, tags:'' });
  const [saving, setSaving]     = React.useState(false);
  const [artAberto, setArtAberto] = React.useState(null);

  React.useEffect(() => { carregar(); }, []);

  async function carregar(b) {
    setLoading(true);
    const q = b !== undefined ? b : busca;
    const d = await api('/kb' + (q ? '?busca=' + encodeURIComponent(q) : ''));
    if (d.sucesso) setArtigos(d.artigos || []);
    setLoading(false);
  }

  async function abrirArtigo(id) {
    const d = await api('/kb/' + id);
    if (d.sucesso) setArtAberto(d.artigo);
  }

  async function salvar() {
    setSaving(true);
    const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
    const body = { ...form, tags };
    const d = modal === 'novo'
      ? await api('/kb', { method:'POST', body: JSON.stringify(body) })
      : await api('/kb/' + modal.id, { method:'PUT', body: JSON.stringify(body) });
    setSaving(false);
    if (d.sucesso) { setModal(null); carregar(); }
    else alert(d.erro || 'Erro ao salvar');
  }

  async function excluir(id) {
    if (!window.confirm('Excluir artigo?')) return;
    await api('/kb/' + id, { method:'DELETE' });
    carregar();
  }

  function abrirNovo() {
    setForm({ titulo:'', conteudo:'', publico:false, tags:'' });
    setModal('novo');
  }

  function abrirEditar(art) {
    setForm({ titulo:art.titulo, conteudo:art.conteudo, publico:art.publico,
      tags:(art.tags||[]).join(', ') });
    setModal(art);
  }

  if (artAberto) return (
    <div style={{ padding:24, maxWidth:800 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <button onClick={()=>setArtAberto(null)}
          style={{ background:'rgba(255,255,255,.08)', border:`1px solid ${C.border}`, borderRadius:8,
            padding:'7px 14px', color:C.muted, cursor:'pointer', fontSize:12 }}>← Voltar</button>
        <button onClick={()=>{ setArtAberto(null); abrirEditar(artAberto); }}
          style={{ background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none',
            borderRadius:8, padding:'7px 14px', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:600 }}>
          Editar
        </button>
      </div>
      <h2 style={{ margin:'0 0 6px', fontSize:20 }}>{artAberto.titulo}</h2>
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {artAberto.categoria && <span style={{ background:'rgba(59,130,246,.15)', color:'#93C5FD',
          fontSize:11, padding:'3px 8px', borderRadius:20 }}>{artAberto.categoria}</span>}
        {artAberto.publico && <span style={{ background:'rgba(34,197,94,.12)', color:'#86EFAC',
          fontSize:11, padding:'3px 8px', borderRadius:20 }}>Público</span>}
        {(artAberto.tags||[]).map(t => (
          <span key={t} style={{ background:'rgba(255,255,255,.06)', color:C.muted,
            fontSize:11, padding:'3px 8px', borderRadius:20 }}>#{t}</span>
        ))}
        <span style={{ marginLeft:'auto', fontSize:11, color:C.muted }}>
          {artAberto.visualizacoes} visualizações · {artAberto.autor}
        </span>
      </div>
      <div style={{ background:'rgba(255,255,255,.03)', border:`1px solid ${C.border}`,
        borderRadius:12, padding:20, lineHeight:1.7, fontSize:14, whiteSpace:'pre-wrap' }}>
        {artAberto.conteudo}
      </div>
    </div>
  );

  return (
    <div style={{ padding:24 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 style={{ margin:0, fontSize:18, fontWeight:700 }}>Base de Conhecimento</h2>
          <p style={{ margin:0, fontSize:12, color:C.muted, marginTop:4 }}>Artigos e documentação interna</p>
        </div>
        <button onClick={abrirNovo}
          style={{ background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none',
            borderRadius:9, padding:'9px 18px', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
          + Novo Artigo
        </button>
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <input value={busca} onChange={e=>setBusca(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&carregar()}
          placeholder="Buscar artigos..." style={{ flex:1, background:'rgba(255,255,255,.06)',
            border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13 }}/>
        <button onClick={()=>carregar()}
          style={{ background:'rgba(255,255,255,.08)', border:`1px solid ${C.border}`, borderRadius:8,
            padding:'8px 16px', color:C.muted, cursor:'pointer', fontSize:13 }}>Buscar</button>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:C.muted }}>Carregando...</div>
      ) : artigos.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, color:C.muted }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📚</div>
          <div>Nenhum artigo encontrado.</div>
        </div>
      ) : (
        <div style={{ display:'grid', gap:10 }}>
          {artigos.map(art => (
            <div key={art.id} style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`,
              borderRadius:12, padding:16, cursor:'pointer' }}
              onClick={()=>abrirArtigo(art.id)}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>{art.titulo}</div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    {art.categoria && <span style={{ fontSize:11, color:'#93C5FD' }}>{art.categoria}</span>}
                    {art.publico && <span style={{ fontSize:11, color:'#86EFAC' }}>Público</span>}
                    {(art.tags||[]).slice(0,3).map(t => (
                      <span key={t} style={{ fontSize:11, color:C.muted }}>#{t}</span>
                    ))}
                  </div>
                </div>
                <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                  <span style={{ fontSize:11, color:C.muted }}>{art.visualizacoes||0} viz.</span>
                  <button onClick={e=>{ e.stopPropagation(); abrirEditar(art); }}
                    style={{ background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                      borderRadius:6, padding:'3px 10px', color:C.muted, cursor:'pointer', fontSize:11 }}>
                    Editar
                  </button>
                  <button onClick={e=>{ e.stopPropagation(); excluir(art.id); }}
                    style={{ background:'rgba(239,68,68,.08)', border:'1px solid rgba(239,68,68,.2)',
                      borderRadius:6, padding:'3px 10px', color:'#FCA5A5', cursor:'pointer', fontSize:11 }}>
                    Excluir
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal !== null && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'#111827', border:`1px solid ${C.border}`, borderRadius:16,
            padding:28, width:'100%', maxWidth:600, maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ margin:'0 0 20px', fontSize:16 }}>
              {modal === 'novo' ? 'Novo Artigo' : 'Editar Artigo'}
            </h3>
            <label style={{ fontSize:12, color:C.muted, display:'block', marginBottom:4 }}>Título *</label>
            <input value={form.titulo} onChange={e=>setForm({...form, titulo:e.target.value})}
              style={{ width:'100%', background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13, boxSizing:'border-box' }}/>
            <label style={{ fontSize:12, color:C.muted, display:'block', margin:'12px 0 4px' }}>Conteúdo *</label>
            <textarea value={form.conteudo} onChange={e=>setForm({...form, conteudo:e.target.value})}
              style={{ width:'100%', height:200, background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13, resize:'vertical',
                fontFamily:'inherit', boxSizing:'border-box' }}/>
            <label style={{ fontSize:12, color:C.muted, display:'block', margin:'12px 0 4px' }}>
              Tags (separadas por vírgula)
            </label>
            <input value={form.tags} onChange={e=>setForm({...form, tags:e.target.value})}
              placeholder="ex: rede, impressora, windows"
              style={{ width:'100%', background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13, boxSizing:'border-box' }}/>
            <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:14, cursor:'pointer', fontSize:13 }}>
              <input type="checkbox" checked={form.publico} onChange={e=>setForm({...form, publico:e.target.checked})}/>
              Artigo público (visível no portal do cliente)
            </label>
            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={salvar} disabled={saving}
                style={{ flex:1, background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none',
                  borderRadius:9, padding:11, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
              <button onClick={()=>setModal(null)}
                style={{ flex:1, background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                  borderRadius:9, padding:11, color:C.muted, cursor:'pointer', fontSize:13 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DISPAROS WPP ─────────────────────────────────────────
function WppDisparos() {
  const [disparos, setDisparos]   = React.useState([]);
  const [loading, setLoading]     = React.useState(true);
  const [modal, setModal]         = React.useState(false);
  const [form, setForm]           = React.useState({ titulo:'', mensagem:'', numeros:'' });
  const [saving, setSaving]       = React.useState(false);
  const [enviando, setEnviando]   = React.useState(null);
  const [logs, setLogs]           = React.useState(null);

  React.useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    const d = await api('/wpp-disparos');
    if (d.sucesso) setDisparos(d.disparos || []);
    setLoading(false);
  }

  async function criar() {
    setSaving(true);
    const numeros = form.numeros.split('\n').map(l => {
      const p = l.trim().split('|');
      return { numero: p[0]?.trim(), nome: p[1]?.trim() || '' };
    }).filter(n => n.numero);
    const d = await api('/wpp-disparos', { method:'POST',
      body: JSON.stringify({ titulo:form.titulo, mensagem:form.mensagem, numeros }) });
    setSaving(false);
    if (d.sucesso) { setModal(false); carregar(); }
    else alert(d.erro || 'Erro ao criar');
  }

  async function enviar(id) {
    if (!window.confirm('Iniciar envio? As mensagens serão disparadas imediatamente.')) return;
    setEnviando(id);
    const d = await api('/wpp-disparos/' + id + '/enviar', { method:'POST' });
    setEnviando(null);
    if (d.sucesso) {
      alert('Envio concluído! Enviados: ' + d.enviados + ' | Erros: ' + d.erros);
      carregar();
    } else alert(d.erro || 'Erro ao enviar');
  }

  async function verLogs(id) {
    const d = await api('/wpp-disparos/' + id + '/logs');
    if (d.sucesso) setLogs({ id, items: d.logs });
  }

  const STATUS_COLORS = {
    rascunho: '#9CA3AF', enviando: '#FBBF24', concluido: '#86EFAC', erro: '#FCA5A5'
  };
  const STATUS_LABELS = {
    rascunho: 'Rascunho', enviando: 'Enviando...', concluido: 'Concluído', erro: 'Erro'
  };

  return (
    <div style={{ padding:24 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 style={{ margin:0, fontSize:18, fontWeight:700 }}>Disparos WPP</h2>
          <p style={{ margin:0, fontSize:12, color:C.muted, marginTop:4 }}>Envio em massa via WhatsApp</p>
        </div>
        <button onClick={()=>{ setForm({titulo:'',mensagem:'',numeros:''}); setModal(true); }}
          style={{ background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none',
            borderRadius:9, padding:'9px 18px', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
          + Novo Disparo
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:C.muted }}>Carregando...</div>
      ) : disparos.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, color:C.muted }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📣</div>
          <div>Nenhum disparo criado ainda.</div>
        </div>
      ) : (
        <div style={{ display:'grid', gap:10 }}>
          {disparos.map(d => (
            <div key={d.id} style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`,
              borderRadius:12, padding:16 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:14 }}>{d.titulo}</div>
                  <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>
                    Por {d.criado_por_nome} · {new Date(d.criado_em).toLocaleDateString('pt-BR')}
                  </div>
                </div>
                <span style={{ fontSize:12, fontWeight:600, color: STATUS_COLORS[d.status] || C.muted }}>
                  {STATUS_LABELS[d.status] || d.status}
                </span>
              </div>
              <div style={{ background:'rgba(255,255,255,.03)', borderRadius:8, padding:'8px 12px',
                fontSize:12, color:C.muted, marginBottom:10, whiteSpace:'pre-wrap', maxHeight:60, overflow:'hidden' }}>
                {d.mensagem}
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {d.total_envios > 0 && (
                  <span style={{ fontSize:12, color:C.muted }}>
                    ✅ {d.total_envios} enviados · ❌ {d.total_erros||0} erros
                  </span>
                )}
                <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
                  <button onClick={()=>verLogs(d.id)}
                    style={{ background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                      borderRadius:7, padding:'5px 12px', color:C.muted, cursor:'pointer', fontSize:12 }}>
                    Logs
                  </button>
                  {d.status === 'rascunho' && (
                    <button onClick={()=>enviar(d.id)} disabled={enviando === d.id}
                      style={{ background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none',
                        borderRadius:7, padding:'5px 14px', color:'#fff', fontWeight:600,
                        fontSize:12, cursor:'pointer', opacity: enviando===d.id ? .6 : 1 }}>
                      {enviando === d.id ? 'Enviando...' : '▶ Enviar'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'#111827', border:`1px solid ${C.border}`, borderRadius:16,
            padding:28, width:'100%', maxWidth:560, maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ margin:'0 0 20px', fontSize:16 }}>Novo Disparo WPP</h3>
            <label style={{ fontSize:12, color:C.muted, display:'block', marginBottom:4 }}>Título *</label>
            <input value={form.titulo} onChange={e=>setForm({...form, titulo:e.target.value})}
              placeholder="ex: Manutenção programada"
              style={{ width:'100%', background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13, boxSizing:'border-box' }}/>
            <label style={{ fontSize:12, color:C.muted, display:'block', margin:'12px 0 4px' }}>
              Mensagem * <span style={{ color:C.muted }}>(use {'{nome}'} para o primeiro nome)</span>
            </label>
            <textarea value={form.mensagem} onChange={e=>setForm({...form, mensagem:e.target.value})}
              placeholder={'Olá, {nome}! Informamos que...'}
              style={{ width:'100%', height:100, background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13, resize:'vertical',
                fontFamily:'inherit', boxSizing:'border-box' }}/>
            <label style={{ fontSize:12, color:C.muted, display:'block', margin:'12px 0 4px' }}>
              Números (um por linha, formato: <code>92999999999|Nome</code>)
            </label>
            <textarea value={form.numeros} onChange={e=>setForm({...form, numeros:e.target.value})}
              placeholder={'92999990001|João Silva\n92999990002|Maria Santos'}
              style={{ width:'100%', height:130, background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                borderRadius:8, padding:'8px 12px', color:C.text, fontSize:12, resize:'vertical',
                fontFamily:'monospace', boxSizing:'border-box' }}/>
            <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>
              {form.numeros.split('\n').filter(l=>l.trim()).length} números adicionados
            </div>
            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={criar} disabled={saving}
                style={{ flex:1, background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none',
                  borderRadius:9, padding:11, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                {saving ? 'Salvando...' : 'Criar Disparo'}
              </button>
              <button onClick={()=>setModal(false)}
                style={{ flex:1, background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                  borderRadius:9, padding:11, color:C.muted, cursor:'pointer', fontSize:13 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {logs && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'#111827', border:`1px solid ${C.border}`, borderRadius:16,
            padding:24, width:'100%', maxWidth:500, maxHeight:'80vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <h3 style={{ margin:0, fontSize:15 }}>Logs do Disparo #{logs.id}</h3>
              <button onClick={()=>setLogs(null)}
                style={{ background:'none', border:'none', color:C.muted, fontSize:18, cursor:'pointer' }}>✕</button>
            </div>
            {logs.items.length === 0 ? (
              <div style={{ color:C.muted, fontSize:13 }}>Nenhum log ainda.</div>
            ) : logs.items.map((l,i) => (
              <div key={i} style={{ display:'flex', gap:10, alignItems:'center', padding:'6px 0',
                borderBottom:`1px solid ${C.border}`, fontSize:12 }}>
                <span style={{ color: l.status==='enviado'?'#86EFAC':'#FCA5A5', fontWeight:600, width:60 }}>
                  {l.status==='enviado'?'✅ OK':'❌ Erro'}
                </span>
                <span style={{ flex:1 }}>{l.nome || l.numero}</span>
                <span style={{ color:C.muted }}>{l.numero}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TICKETS RECORRENTES ──────────────────────────────────
function TicketsRecorrentes() {
  const [lista, setLista]         = React.useState([]);
  const [loading, setLoading]     = React.useState(true);
  const [modal, setModal]         = React.useState(false);
  const [categorias, setCategorias] = React.useState([]);
  const [empresas, setEmpresas]   = React.useState([]);
  const [form, setForm]           = React.useState({
    titulo:'', descricao:'', categoria_id:'', empresa_id:'',
    prioridade:'media', frequencia:'semanal', dia_semana:1, dia_mes:1
  });
  const [saving, setSaving]       = React.useState(false);

  React.useEffect(() => {
    carregar();
    api('/categorias').then(d => d.sucesso && setCategorias(d.categorias||[]));
    api('/empresas').then(d => d.sucesso && setEmpresas(d.empresas||[]));
  }, []);

  async function carregar() {
    setLoading(true);
    const d = await api('/tickets-recorrentes');
    if (d.sucesso) setLista(d.recorrentes || []);
    setLoading(false);
  }

  async function salvar() {
    setSaving(true);
    const d = await api('/tickets-recorrentes', { method:'POST', body: JSON.stringify(form) });
    setSaving(false);
    if (d.sucesso) { setModal(false); carregar(); }
    else alert(d.erro || 'Erro ao salvar');
  }

  async function toggleAtivo(rec) {
    await api('/tickets-recorrentes/' + rec.id, { method:'PUT',
      body: JSON.stringify({ ...rec, ativo: !rec.ativo }) });
    carregar();
  }

  const FREQ_LABELS = { diario:'Diário', semanal:'Semanal', mensal:'Mensal' };
  const DIAS_SEMANA = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
  const PRIO_COLORS = { baixa:'#86EFAC', media:'#FBBF24', alta:'#FCA5A5', critica:'#F87171' };

  return (
    <div style={{ padding:24 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 style={{ margin:0, fontSize:18, fontWeight:700 }}>Tickets Recorrentes</h2>
          <p style={{ margin:0, fontSize:12, color:C.muted, marginTop:4 }}>
            Chamados criados automaticamente por agendamento
          </p>
        </div>
        <button onClick={()=>{ setForm({ titulo:'', descricao:'', categoria_id:'', empresa_id:'',
          prioridade:'media', frequencia:'semanal', dia_semana:1, dia_mes:1 }); setModal(true); }}
          style={{ background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none',
            borderRadius:9, padding:'9px 18px', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
          + Nova Recorrência
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:C.muted }}>Carregando...</div>
      ) : lista.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, color:C.muted }}>
          <div style={{ fontSize:36, marginBottom:12 }}>🔁</div>
          <div>Nenhum ticket recorrente configurado.</div>
        </div>
      ) : (
        <div style={{ display:'grid', gap:10 }}>
          {lista.map(rec => {
            const proximo = new Date(rec.proximo_em);
            const atrasado = proximo < new Date() && rec.ativo;
            return (
              <div key={rec.id} style={{ background:'rgba(255,255,255,.04)',
                border:`1px solid ${atrasado ? 'rgba(239,68,68,.3)' : C.border}`,
                borderRadius:12, padding:16, opacity: rec.ativo ? 1 : .55 }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                      <span style={{ fontWeight:600, fontSize:14 }}>{rec.titulo}</span>
                      <span style={{ fontSize:11, color: PRIO_COLORS[rec.prioridade], fontWeight:600 }}>
                        {rec.prioridade}
                      </span>
                      {!rec.ativo && <span style={{ fontSize:11, color:C.muted }}>• Pausado</span>}
                    </div>
                    <div style={{ display:'flex', gap:12, fontSize:12, color:C.muted }}>
                      <span>🔁 {FREQ_LABELS[rec.frequencia]}
                        {rec.frequencia==='semanal' && rec.dia_semana!=null ? ' · ' + DIAS_SEMANA[rec.dia_semana] : ''}
                        {rec.frequencia==='mensal' && rec.dia_mes ? ' · dia ' + rec.dia_mes : ''}
                      </span>
                      <span style={{ color: atrasado ? '#FCA5A5' : C.muted }}>
                        📅 Próximo: {proximo.toLocaleDateString('pt-BR')}
                        {atrasado && ' ⚠️'}
                      </span>
                      {rec.categoria_nome && <span>📂 {rec.categoria_nome}</span>}
                      {rec.empresa_nome && <span>🏢 {rec.empresa_nome}</span>}
                    </div>
                    {rec.ultimo_criado && (
                      <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>
                        Último criado: {new Date(rec.ultimo_criado).toLocaleDateString('pt-BR')}
                      </div>
                    )}
                  </div>
                  <button onClick={()=>toggleAtivo(rec)}
                    style={{ background: rec.ativo ? 'rgba(239,68,68,.1)' : 'rgba(34,197,94,.1)',
                      border: `1px solid ${rec.ativo ? 'rgba(239,68,68,.3)' : 'rgba(34,197,94,.3)'}`,
                      borderRadius:7, padding:'5px 12px', fontSize:12, fontWeight:600, cursor:'pointer',
                      color: rec.ativo ? '#FCA5A5' : '#86EFAC', flexShrink:0 }}>
                    {rec.ativo ? 'Pausar' : 'Ativar'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'#111827', border:`1px solid ${C.border}`, borderRadius:16,
            padding:28, width:'100%', maxWidth:520, maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ margin:'0 0 20px', fontSize:16 }}>Nova Recorrência</h3>

            <label style={{ fontSize:12, color:C.muted, display:'block', marginBottom:4 }}>Título *</label>
            <input value={form.titulo} onChange={e=>setForm({...form, titulo:e.target.value})}
              placeholder="ex: Backup semanal dos servidores"
              style={{ width:'100%', background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13, boxSizing:'border-box' }}/>

            <label style={{ fontSize:12, color:C.muted, display:'block', margin:'12px 0 4px' }}>Descrição</label>
            <textarea value={form.descricao} onChange={e=>setForm({...form, descricao:e.target.value})}
              style={{ width:'100%', height:70, background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13, resize:'vertical',
                fontFamily:'inherit', boxSizing:'border-box' }}/>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:12 }}>
              <div>
                <label style={{ fontSize:12, color:C.muted, display:'block', marginBottom:4 }}>Categoria</label>
                <select value={form.categoria_id} onChange={e=>setForm({...form, categoria_id:e.target.value})}
                  style={{ width:'100%', background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                    borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13 }}>
                  <option value="">Nenhuma</option>
                  {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:12, color:C.muted, display:'block', marginBottom:4 }}>Prioridade</label>
                <select value={form.prioridade} onChange={e=>setForm({...form, prioridade:e.target.value})}
                  style={{ width:'100%', background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                    borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13 }}>
                  <option value="baixa">Baixa</option>
                  <option value="media">Média</option>
                  <option value="alta">Alta</option>
                  <option value="critica">Crítica</option>
                </select>
              </div>
            </div>

            <label style={{ fontSize:12, color:C.muted, display:'block', margin:'12px 0 4px' }}>Empresa</label>
            <select value={form.empresa_id} onChange={e=>setForm({...form, empresa_id:e.target.value})}
              style={{ width:'100%', background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13 }}>
              <option value="">Nenhuma</option>
              {empresas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>

            <label style={{ fontSize:12, color:C.muted, display:'block', margin:'12px 0 4px' }}>Frequência *</label>
            <select value={form.frequencia} onChange={e=>setForm({...form, frequencia:e.target.value})}
              style={{ width:'100%', background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13 }}>
              <option value="diario">Diário</option>
              <option value="semanal">Semanal</option>
              <option value="mensal">Mensal</option>
            </select>

            {form.frequencia === 'semanal' && (
              <>
                <label style={{ fontSize:12, color:C.muted, display:'block', margin:'12px 0 4px' }}>Dia da semana</label>
                <select value={form.dia_semana} onChange={e=>setForm({...form, dia_semana:parseInt(e.target.value)})}
                  style={{ width:'100%', background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                    borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13 }}>
                  {DIAS_SEMANA.map((d,i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </>
            )}
            {form.frequencia === 'mensal' && (
              <>
                <label style={{ fontSize:12, color:C.muted, display:'block', margin:'12px 0 4px' }}>Dia do mês</label>
                <input type="number" min={1} max={28} value={form.dia_mes}
                  onChange={e=>setForm({...form, dia_mes:parseInt(e.target.value)})}
                  style={{ width:'100%', background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                    borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13, boxSizing:'border-box' }}/>
              </>
            )}

            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={salvar} disabled={saving}
                style={{ flex:1, background:`linear-gradient(135deg,${C.red},${C.redDark})`, border:'none',
                  borderRadius:9, padding:11, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                {saving ? 'Salvando...' : 'Criar Recorrência'}
              </button>
              <button onClick={()=>setModal(false)}
                style={{ flex:1, background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`,
                  borderRadius:9, padding:11, color:C.muted, cursor:'pointer', fontSize:13 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CSAT ─────────────────────────────────────────────────

function CSATGrafico({historico}) {
  
  if (!historico || !historico.length) return React.createElement('div',{style:{color:C.muted,textAlign:'center',padding:20,fontSize:13}},'Aguardando dados historicos...');
  return React.createElement('div',{style:{background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.08)',borderRadius:12,padding:20,marginTop:20}},
    React.createElement('h4',{style:{color:C.text,fontSize:14,fontWeight:600,margin:'0 0 16px'}},'Evolucao NPS — Ultimos 12 meses'),
    React.createElement('div',{style:{display:'flex',alignItems:'flex-end',gap:6,height:100,padding:'0 4px'}},
      ...historico.map((m,i)=>{
        const nps=Math.max(-100,Math.min(100,parseInt(m.nps)||0));
        const h=Math.max(4,Math.abs(nps)/100*80);
        const cor=nps>=50?'#22c55e':nps>=0?'#eab308':'#ef4444';
        return React.createElement('div',{key:i,style:{display:'flex',flexDirection:'column',alignItems:'center',gap:3,flex:1}},
          React.createElement('span',{style:{color:cor,fontSize:9,fontWeight:700}},nps>0?'+'+nps:String(nps)),
          React.createElement('div',{style:{width:'100%',maxWidth:36,height:h,background:cor,borderRadius:'3px 3px 0 0'}}),
          React.createElement('span',{style:{color:C.muted,fontSize:8,textAlign:'center',lineHeight:1.2}},m.mes_label)
        );
      })
    ),
    React.createElement('div',{style:{display:'flex',gap:16,marginTop:10,justifyContent:'center'}},
      React.createElement('span',{style:{fontSize:11,color:'#22c55e'}},'NPS 50+ Excelente'),
      React.createElement('span',{style:{fontSize:11,color:'#eab308'}},'NPS 0-49 Bom'),
      React.createElement('span',{style:{fontSize:11,color:'#ef4444'}},'NPS negativo Critico')
    )
  );
}

function ConvitesPortal() {
  
  const [clientes, setClientes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [enviando, setEnviando] = React.useState(null);
  const [busca, setBusca] = React.useState('');
  const [resultados, setResultados] = React.useState({});
  const carregar = async () => {
    setLoading(true);
    try { const d = await api('/clientes/com-portal'); setClientes(d.clientes||[]); } catch(e){}
    setLoading(false);
  };
  React.useEffect(()=>{carregar();},[]);
  const enviar = async (id, nome) => {
    if (!window.confirm('Enviar link do portal para '+nome+' via WhatsApp?')) return;
    setEnviando(id);
    try {
      const d = await api('/clientes/'+id+'/enviar-convite-portal',{method:'POST'});
      setResultados(r=>({...r,[id]:{ok:true,msg:d.mensagem}}));
    } catch(e){ setResultados(r=>({...r,[id]:{ok:false,msg:e.message}})); }
    setEnviando(null);
  };
  const filtrados = clientes.filter(c=>!busca||c.nome.toLowerCase().includes(busca.toLowerCase()));
  return React.createElement('div',{style:{padding:24}},
    React.createElement('div',{style:{marginBottom:24}},
      React.createElement('h2',{style:{margin:0,fontSize:22,fontWeight:700,color:C.text}},'Convites do Portal'),
      React.createElement('p',{style:{margin:'4px 0 0',color:C.muted,fontSize:13}},'Envie o link de acesso ao portal para empresas via WhatsApp')
    ),
    React.createElement('div',{style:{background:'rgba(99,102,241,.08)',border:'1px solid rgba(99,102,241,.2)',borderRadius:10,padding:'12px 16px',marginBottom:20,fontSize:13,color:'#818cf8'}},
      'Link unico por empresa. O cliente acessa o portal sem precisar criar conta.'
    ),
    React.createElement('input',{value:busca,onChange:e=>setBusca(e.target.value),placeholder:'Filtrar empresas...',style:{width:'100%',padding:'10px 14px',background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.12)',borderRadius:8,color:C.text,fontSize:14,boxSizing:'border-box',marginBottom:16}}),
    loading
      ? React.createElement('div',{style:{color:C.muted,textAlign:'center',padding:40}},'Carregando...')
      : React.createElement('div',{style:{display:'flex',flexDirection:'column',gap:8}},
          ...filtrados.map(c=>{
            const res=resultados[c.id];
            const temFone=c.telefone&&c.telefone.trim();
            return React.createElement('div',{key:c.id,style:{background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.08)',borderRadius:10,padding:'14px 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}},
              React.createElement('div',null,
                React.createElement('div',{style:{fontWeight:600,color:C.text,fontSize:14,marginBottom:2}},c.nome),
                React.createElement('div',{style:{fontSize:12,color:C.muted}},
                  temFone?'Tel: '+c.telefone:'Sem telefone cadastrado',
                  parseInt(c.total_usuarios_portal)>0&&React.createElement('span',{style:{marginLeft:12,color:'#22c55e'}},' | '+c.total_usuarios_portal+' usuario(s) no portal')
                ),
                res&&React.createElement('div',{style:{fontSize:12,marginTop:4,color:res.ok?'#22c55e':'#ef4444'}},(res.ok?'Enviado: ':'Erro: ')+res.msg)
              ),
              React.createElement('button',{
                onClick:()=>enviar(c.id,c.nome),
                disabled:!temFone||enviando===c.id,
                style:{background:temFone?'rgba(34,197,94,.15)':'rgba(255,255,255,.04)',color:temFone?'#22c55e':C.muted,border:'1px solid '+(temFone?'rgba(34,197,94,.3)':'rgba(255,255,255,.08)'),borderRadius:8,padding:'8px 16px',cursor:temFone?'pointer':'not-allowed',fontWeight:600,fontSize:13,whiteSpace:'nowrap'}
              },enviando===c.id?'Enviando...':'Enviar Convite')
            );
          })
        )
  );
}

function PortalKB() {
  const [artigos, setArtigos] = React.useState([]);
  const [busca, setBusca] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [aberto, setAberto] = React.useState(null);
  const carregar = async (b) => {
    setLoading(true);
    try {
      const url='/api/portal/kb'+(b?'?busca='+encodeURIComponent(b):'');
      const d=await fetch(url).then(r=>r.json());
      setArtigos(d.artigos||[]);
    } catch(e){}
    setLoading(false);
  };
  React.useEffect(()=>{carregar('');},[]);
  const abrirArtigo = async (a) => {
    await fetch('/api/portal/kb/'+a.id+'/visualizar',{method:'POST'}).catch(()=>{});
    setAberto(a);
  };
  if (aberto) return React.createElement('div',{style:{padding:'0 4px'}},
    React.createElement('button',{onClick:()=>setAberto(null),style:{background:'none',border:'none',color:'#6366f1',cursor:'pointer',fontSize:14,marginBottom:16,padding:0}},'<- Voltar'),
    React.createElement('h2',{style:{fontSize:18,fontWeight:700,margin:'0 0 8px',color:'#1e293b'}},aberto.titulo),
    React.createElement('div',{style:{fontSize:12,color:'#64748b',marginBottom:16}},aberto.categoria_nome||'Geral'),
    React.createElement('div',{style:{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:10,padding:20,fontSize:14,lineHeight:1.7,color:'#334155',whiteSpace:'pre-wrap'}},aberto.conteudo)
  );
  return React.createElement('div',null,
    React.createElement('div',{style:{display:'flex',gap:8,marginBottom:14}},
      React.createElement('input',{value:busca,onChange:e=>setBusca(e.target.value),onKeyDown:e=>e.key==='Enter'&&carregar(busca),placeholder:'Buscar artigos...',style:{flex:1,padding:'9px 12px',border:'1px solid #e2e8f0',borderRadius:8,fontSize:13,color:'#1e293b'}}),
      React.createElement('button',{onClick:()=>carregar(busca),style:{background:'#6366f1',color:'#fff',border:'none',borderRadius:8,padding:'9px 14px',cursor:'pointer',fontSize:13}},'Buscar')
    ),
    loading?React.createElement('div',{style:{textAlign:'center',padding:32,color:'#94a3b8'}},'Carregando...')
    :artigos.length===0
      ?React.createElement('div',{style:{textAlign:'center',padding:40,color:'#94a3b8'}},
          React.createElement('div',{style:{fontSize:32,marginBottom:8}},'Sem artigos'),
          React.createElement('div',null,busca?'Sem resultados para "'+busca+'"':'Nenhum artigo publicado ainda')
        )
      :React.createElement('div',{style:{display:'flex',flexDirection:'column',gap:8}},
          ...artigos.map(a=>React.createElement('div',{key:a.id,onClick:()=>abrirArtigo(a),style:{background:'#fff',border:'1px solid #e2e8f0',borderRadius:8,padding:14,cursor:'pointer'}},
            React.createElement('div',{style:{fontWeight:600,fontSize:14,color:'#1e293b',marginBottom:4}},a.titulo),
            React.createElement('div',{style:{display:'flex',gap:8,alignItems:'center'}},
              React.createElement('span',{style:{background:'#f1f5f9',color:'#64748b',borderRadius:4,padding:'1px 7px',fontSize:11}},a.categoria_nome||'Geral'),
              React.createElement('span',{style:{color:'#94a3b8',fontSize:11,marginLeft:'auto'}},'Views: '+(a.visualizacoes||0))
            )
          ))
        )
  );
}
function CSAT() {
  const [metricas, setMetricas] = React.useState(null);
  const [historico, setHistorico] = React.useState([]);
  const [ultimas, setUltimas]   = React.useState([]);
  const [periodo, setPeriodo]   = React.useState('30d');
  const [loading, setLoading]   = React.useState(true);

  React.useEffect(() => { carregar();
    api('/csat/historico-mensal').then(d=>setHistorico(d.historico||[])).catch(()=>{}); }, [periodo]);

  async function carregar() {
    setLoading(true);
    const d = await api('/csat/metricas?periodo=' + periodo);
    if (d.sucesso) { setMetricas(d.metricas); setUltimas(d.ultimas||[]); }
    setLoading(false);
  }

  const NOTA_LABELS = ['','Muito ruim','Ruim','Neutro','Bom','Excelente'];
  const NOTA_COLORS = ['','#EF4444','#F97316','#EAB308','#22C55E','#10B981'];

  function Estrelas({ nota }) {
    return (
      <span style={{ color:'#FBBF24', fontSize:14 }}>
        {'★'.repeat(nota) + '☆'.repeat(5 - nota)}
      </span>
    );
  }

  function MetricCard({ label, value, color, sub }) {
    return (
      <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid rgba(255,255,255,.08)`,
        borderRadius:12, padding:'18px 20px', textAlign:'center' }}>
        <div style={{ fontSize:28, fontWeight:700, color: color || C.text }}>{value}</div>
        <div style={{ fontSize:11, color:C.muted, marginTop:4, textTransform:'uppercase', letterSpacing:.5 }}>{label}</div>
        {sub && <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{sub}</div>}
      </div>
    );
  }

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:200, color:C.muted }}>
      Carregando métricas CSAT...
    </div>
  );

  const m = metricas;
  const media = m ? parseFloat(m.media||0) : 0;
  const total = m ? parseInt(m.total||0) : 0;
  const nps   = m ? parseInt(m.nps||0) : 0;

  const distrib = m ? [
    { nota:5, count: parseInt(m.nota5||0), label:'Excelente' },
    { nota:4, count: parseInt(m.nota4||0), label:'Bom' },
    { nota:3, count: parseInt(m.nota3||0), label:'Neutro' },
    { nota:2, count: parseInt(m.nota2||0), label:'Ruim' },
    { nota:1, count: parseInt(m.nota1||0), label:'Muito ruim' },
  ] : [];

  return (
    <div style={{ padding:24 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 style={{ margin:0, fontSize:18, fontWeight:700 }}>CSAT — Satisfação do Cliente</h2>
          <p style={{ margin:0, fontSize:12, color:C.muted, marginTop:4 }}>Pesquisas enviadas automaticamente ao resolver chamados</p>
        </div>
        <select value={periodo} onChange={e=>setPeriodo(e.target.value)}
          style={{ background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`, borderRadius:8,
            padding:'7px 12px', color:C.text, fontSize:12, cursor:'pointer' }}>
          <option value="7d">Últimos 7 dias</option>
          <option value="30d">Últimos 30 dias</option>
          <option value="90d">Últimos 90 dias</option>
          <option value="">Todos</option>
        </select>
      </div>

      {total === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:C.muted }}>
          <div style={{ fontSize:48, marginBottom:16 }}>⭐</div>
          <div style={{ fontSize:14, marginBottom:8 }}>Nenhuma avaliação recebida ainda.</div>
          <div style={{ fontSize:12 }}>As pesquisas são enviadas via WhatsApp ao fechar chamados.</div>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
            <MetricCard label="Avaliações" value={total} />
            <MetricCard label="Média"
              value={media.toFixed(1) + ' ★'}
              color={media >= 4 ? '#22C55E' : media >= 3 ? '#EAB308' : '#EF4444'} />
            <MetricCard label="NPS"
              value={(nps > 0 ? '+' : '') + nps}
              color={nps >= 50 ? '#22C55E' : nps >= 0 ? '#EAB308' : '#EF4444'}
              sub={nps >= 75 ? 'Excelente' : nps >= 50 ? 'Muito bom' : nps >= 0 ? 'Regular' : 'Crítico'} />
            <MetricCard label="Promotores"
              value={m ? parseInt(m.promotores||0) : 0}
              color="#22C55E"
              sub={total > 0 ? Math.round((parseInt(m.promotores||0)/total)*100) + '%' : ''} />
          </div>

          {/* Distribuição */}
          <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`,
            borderRadius:12, padding:20, marginBottom:20 }}>
            <h3 style={{ margin:'0 0 14px', fontSize:14, fontWeight:600 }}>Distribuição de Notas</h3>
            {distrib.map(d => {
              const pct = total > 0 ? Math.round((d.count/total)*100) : 0;
              return (
                <div key={d.nota} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                  <span style={{ width:70, fontSize:12, color:C.muted, flexShrink:0 }}>
                    {'★'.repeat(d.nota)} {d.nota}
                  </span>
                  <div style={{ flex:1, background:'rgba(255,255,255,.06)', borderRadius:4, height:8, overflow:'hidden' }}>
                    <div style={{ width:pct+'%', height:'100%', background:NOTA_COLORS[d.nota],
                      borderRadius:4, transition:'width .4s ease' }} />
                  </div>
                  <span style={{ width:40, fontSize:12, color:C.muted, textAlign:'right' }}>{d.count}</span>
                  <span style={{ width:34, fontSize:11, color:C.muted }}>{pct}%</span>
                </div>
              );
            })}
          </div>

          {/* Últimas respostas */}
          {ultimas.length > 0 && (
            <div style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${C.border}`,
              borderRadius:12, padding:20 }}>
              <h3 style={{ margin:'0 0 14px', fontSize:14, fontWeight:600 }}>Últimas Avaliações</h3>
              {ultimas.map((u,i) => (
                <div key={i} style={{ borderBottom:i<ultimas.length-1?`1px solid ${C.border}`:'none',
                  paddingBottom:i<ultimas.length-1?12:0, marginBottom:i<ultimas.length-1?12:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                    <Estrelas nota={u.nota} />
                    <span style={{ fontSize:11, color:NOTA_COLORS[u.nota], fontWeight:600 }}>
                      {NOTA_LABELS[u.nota]}
                    </span>
                    <span style={{ marginLeft:'auto', fontSize:11, color:C.muted }}>
                      {new Date(u.respondido_em).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:C.text, marginBottom:2 }}>{u.titulo}</div>
                  {u.comentario && (
                    <div style={{ fontSize:12, color:C.muted, fontStyle:'italic', marginTop:4,
                      background:'rgba(255,255,255,.03)', padding:'6px 10px', borderRadius:6 }}>
                      "{u.comentario}"
                    </div>
                  )}
                  <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>
                    {u.solicitante}{u.empresa ? ' · ' + u.empresa : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <CSATGrafico historico={historico} />
    </div>
  );
}

// ─── MENU ITEMS ──────────────────────────────────────────
const MENU = [
  { id:'dashboard',    label:'Dashboard',     sub:'Visão geral',    icon:'dashboard',  comp:Dashboard },
  { id:'kanban',       label:'Kanban',        sub:'Chamados',       icon:'kanban',     comp:Kanban },
  { id:'chamados',     label:'Chamados',      sub:'Tickets',        icon:'reports',    comp:Chamados },
  { id:'clientes',     label:'Clientes',      sub:'Empresas',       icon:'clients',    comp:Clientes },
  { id:'ativos',       label:'Ativos',        sub:'Inventário',     icon:'assets',     comp:Ativos },
  { id:'categorias',   label:'Categorias',    sub:'Serviços',       icon:'category',   comp:Categorias },
  { id:'sla',          label:'SLA',           sub:'Acordos',        icon:'sla',        comp:SLA },
  { id:'relatorios',   label:'Relatórios',    sub:'Indicadores',    icon:'reports',    comp:Relatorios },
  { id:'usuarios',     label:'Usuários',      sub:'Equipe',         icon:'users',      comp:Usuarios },
  { id:'pendentes',     label:'Pendentes',     sub:'Aprovações',     icon:'users',      comp:Pendentes },
  { id:'configuracoes',label:'Configurações', sub:'Sistema',        icon:'settings',   comp:Configuracoes },
  { id:'csat',         label:'CSAT',          sub:'Satisfação',      icon:'sla',        comp:CSAT },
      { id:'convites', label:'Convites Portal', sub:'Links de acesso', icon:'users', comp:ConvitesPortal },
  { id:'ferramentas',  label:'Ferramentas',  sub:'RustDesk · OCS', icon:'tool',       comp:Ferramentas },
  { id:'kb',           label:'Base de Conhecimento', sub:'Artigos',         icon:'reports',    comp:BaseConhecimento },
  { id:'disparos',     label:'Disparos WPP',         sub:'Envio em massa',  icon:'tool',       comp:WppDisparos },
  { id:'recorrentes',  label:'Recorrentes',           sub:'Auto-chamados',   icon:'settings',   comp:TicketsRecorrentes },
  { id:'trocar_senha', label:'Trocar Senha',  sub:'Segurança',      icon:'settings',   comp:null },
];

// ─── APP SHELL ───────────────────────────────────────────
function AppShell({ usuario, onLogout }) {
  const [aba, setAba] = useState('dashboard');

  // Logout por inatividade — 30 minutos
  useEffect(() => {
    const INATIVIDADE_MS = 30 * 60 * 1000;
    let timer;

    const resetTimer = () => {
      clearTimeout(timer);
      localStorage.setItem('atiflow_ultimo_ativo', Date.now().toString());
      timer = setTimeout(() => {
        alert('Sessão encerrada por inatividade (30 minutos). Faça login novamente.');
        onLogout();
      }, INATIVIDADE_MS);
    };

    const checkExpiry = () => {
      const ultimo = parseInt(localStorage.getItem('atiflow_ultimo_ativo') || '0');
      if (Date.now() - ultimo > INATIVIDADE_MS) { onLogout(); return; }
      resetTimer();
    };

    // Verificar senha expirada
    if (localStorage.getItem('atiflow_trocar_senha') === '1') {
      setAba('trocar_senha');
    }

    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('click', resetTimer);
    const interval = setInterval(checkExpiry, 60000);
    resetTimer();

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('click', resetTimer);
    };
  }, [onLogout]);
  const [notifs, setNotifs] = useState(8);
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);

  const item = MENU.find(m=>m.id===aba)||MENU[0];
  if (aba === 'trocar_senha') {
    return (
      <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ width:'100%', maxWidth:500, padding:'0 16px' }}>
          <div style={{ textAlign:'center', marginBottom:20 }}>
            <img src="/logo-ati.png" alt="ATI Services" style={{ height:60, filter:'drop-shadow(0 0 12px rgba(227,27,35,.4))' }}/>
          </div>
          <TrocarSenha obrigatorio={true} onConcluido={()=>{ localStorage.removeItem('atiflow_trocar_senha'); setAba('dashboard'); }}/>
        </div>
      </div>
    );
  }
  const Comp = item.comp;

  return (
    <MobileContext.Provider value={isMobile}>
    <div style={{ display:'flex', minHeight:'100vh', background:C.bg, fontFamily:"Inter,'Segoe UI',sans-serif", color:C.text }}>
      {isMobile && sidebarOpen && (
        <div onClick={()=>setSidebarOpen(false)} style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.55)',zIndex:998}} />
      )}
      {/* SIDEBAR */}
      <aside style={{ width: sidebarOpen?240:0, minWidth:0, overflow:'hidden', flexShrink:0, background:`linear-gradient(180deg,#050505,#0B111D)`, borderRight: sidebarOpen ? `1px solid ${C.border}` : 'none', display:'flex', flexDirection:'column', transition:'width .25s ease', position: isMobile ? 'fixed' : 'relative', top: isMobile ? 0 : 'auto', left: isMobile ? 0 : 'auto', height: isMobile ? '100vh' : '100%', zIndex: isMobile ? 999 : 'auto' }}>
        <div style={{ width:240, display:'flex', flexDirection:'column', height:'100%' }}>
          {/* LOGO */}
          <div style={{ padding:'16px 16px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
            <img src="/logo-ati.png" alt="ATI Services" style={{ width:120, height:'auto', filter:'drop-shadow(0 0 12px rgba(227,27,35,.4))' }}/>
            <div style={{ fontSize:10, color:C.red, fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginTop:2 }}>ATI Flow v2 · Enterprise</div>
          </div>

          {/* NAV */}
          <nav style={{ flex:1, padding:'12px 10px', overflowY:'auto' }}>
            {MENU.map(m=>(
              <button key={m.id} onClick={()=>setAba(m.id)} style={{
                display:'flex', alignItems:'center', gap:12, width:'100%', padding:'10px 12px', borderRadius:10, border:'none', cursor:'pointer', textAlign:'left', marginBottom:2, transition:'all .15s',
                background: aba===m.id ? `linear-gradient(135deg,${C.red},${C.redDark})` : 'transparent',
                color: aba===m.id ? '#fff' : C.muted,
                boxShadow: aba===m.id ? `0 8px 20px rgba(227,27,35,.25)` : 'none',
              }}>
                <Icon name={m.icon} size={17} color={aba===m.id?'#fff':C.muted}/>
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{m.label}</div>
                  <div style={{ fontSize:10, opacity:.7 }}>{m.sub}</div>
                </div>
              </button>
            ))}
          </nav>

          {/* SIDEBAR FOOTER */}
          <div style={{ padding:16, borderTop:`1px solid ${C.border}` }}>
            <div style={{ background:`radial-gradient(circle at top,rgba(227,27,35,.2),rgba(255,255,255,.03))`, border:`1px solid ${C.border}`, borderRadius:12, padding:14, textAlign:'center', marginBottom:12 }}>
              <img src="/logo-ati.png" alt="ATI Services" style={{ width:100, height:'auto', marginBottom:8 }}/>
              <p style={{ margin:0, fontSize:11, color:'#D1D5DB', lineHeight:1.5 }}>Tecnologia confiável para empresas que <b style={{ color:C.red }}>não podem parar.</b></p>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.muted, alignItems:'center' }}>
              <span>ATI Flow v2.0</span>
              <span style={{ background:C.red, color:'#fff', padding:'3px 8px', borderRadius:6, fontWeight:700 }}>Enterprise</span>
            </div>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>
        {/* TOPBAR */}
        <header style={{ height:64, display:'flex', alignItems:'center', gap:16, padding:'0 24px', borderBottom:`1px solid ${C.border}`, background:'rgba(5,5,5,.8)', backdropFilter:'blur(14px)', position:'sticky', top:0, zIndex:50 }}>
          <button onClick={()=>setSidebarOpen(o=>!o)} style={{ background:'transparent', border:'none', color:C.muted, cursor:'pointer', padding:4, display:'flex' }}>
            <Icon name="menu" size={20}/>
          </button>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:18, fontWeight:800 }}><span style={{ color:C.red }}>ATI</span> Flow v2</span>
            <span style={{ color:C.border }}>|</span>
            {!isMobile && <span style={{ fontSize:13, color:C.muted }}>Gestão de Chamados e Inventário</span>}
          </div>
          <div style={{ flex:1, maxWidth:420, marginLeft:'auto', display: isMobile ? 'none' : 'block' }}>
            <div style={{ position:'relative' }}>
              <Icon name="search" size={15} color={C.muted}/>
              <input style={{ ...inp, paddingLeft:32, position:'relative' }} placeholder="Buscar (Ctrl + K)" value={search} onChange={e=>setSearch(e.target.value)}/>
              <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <button onClick={()=>setNotifs(0)} style={{ position:'relative', background:'transparent', border:`1px solid ${C.border}`, borderRadius:10, padding:8, cursor:'pointer', color:C.muted, display:'flex' }}>
              <Icon name="bell" size={18}/>
              {notifs>0 && <span style={{ position:'absolute', top:-6, right:-6, background:C.red, color:'#fff', borderRadius:999, fontSize:10, fontWeight:700, width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center' }}>{notifs}</span>}
            </button>
            <button style={{ background:'transparent', border:`1px solid ${C.border}`, borderRadius:10, padding:8, cursor:'pointer', color:C.muted, display:'flex' }}>
              <Icon name="moon" size={18}/>
            </button>
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 12px', background:'rgba(255,255,255,.05)', border:`1px solid ${C.border}`, borderRadius:12, cursor:'pointer' }}>
              <div style={{ width:32, height:32, borderRadius:999, background:`linear-gradient(135deg,${C.red},${C.redDark})`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13 }}>{(usuario?.nome||'A').slice(0,1)}</div>
              <div>
                <div style={{ fontSize:12, fontWeight:600 }}>{usuario?.nome||'Administrador'}</div>
                <div style={{ fontSize:10, color:C.muted, display: isMobile ? 'none' : 'block' }}>{usuario?.email}</div>
              </div>
              <button onClick={onLogout} style={{ background:'transparent', border:`1px solid ${C.border}`, color:C.muted, borderRadius:8, padding:'4px 8px', cursor:'pointer', fontSize:11, marginLeft:4, display: isMobile ? 'none' : 'block' }}>Sair</button>
            </div>
          </div>
        </header>

        {/* CONTENT */}
        <main style={{ flex:1, padding: isMobile ? '16px 12px' : 28, overflowY:'auto', overflowX:'hidden', maxWidth:'100%', boxSizing:'border-box' }}>
          <Comp setAba={setAba} usuario={usuario}/>
        </main>

        {/* FOOTER */}
        <footer style={{ padding: isMobile ? '10px 12px' : '14px 28px', borderTop:`1px solid ${C.border}`, display:'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent:'space-between', fontSize:12, color:'#4B5563', gap: isMobile ? 4 : 0 }}>
          <span>© {new Date().getFullYear()} ATI Services. Todos os direitos reservados.</span>
          <span>Desenvolvido com <span style={{ color:C.red }}>♥</span> pela ATI Services</span>
        </footer>
      </div>

      <style>{`
        *{box-sizing:border-box}
        body{margin:0;background:${C.bg};color:${C.text};font-family:Inter,'Segoe UI',Arial,sans-serif}
        ::-webkit-scrollbar{width:6px;height:6px}
        ::-webkit-scrollbar-track{background:rgba(255,255,255,.03)}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,.12);border-radius:3px}
        input,select,textarea,button{font-family:inherit} select option{background:#1e293b;color:#F8FAFC}
        input::placeholder{color:#4B5563}
        a{color:${C.red};text-decoration:none}
      `}</style>
    </div>
    </MobileContext.Provider>
  );
}

// ─── ROOT ────────────────────────────────────────────────
export default function App() {
  const [usuario, setUsuario] = useState(getUser);
  function logout() { clearSession(); setUsuario(null); }
  if (!usuario) return <Login onLogin={u=>{ setUsuario(u); }}/>;
  return <AppShell usuario={usuario} onLogout={logout}/>;
}
